import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import { registerAllModules } from 'handsontable/registry';
import template from '../assets/dailyReportTemplate.json';
import * as XLSX from 'xlsx';
import { socket } from '../socket';
import { debounce } from 'lodash';
import StatementTemplatePage from './StatementTemplatePage';
import html2pdf from 'html2pdf.js';
import { createRoot } from 'react-dom/client';
import SettlementSidebar from './SettlementSidebar';
import { auth } from '../firebaseConfig';
import FullScreenButton from './FullScreenButton';
import RouteHistorySidebar from './RouteHistorySidebar';
import VehicleHistorySidebar from './VehicleHistorySidebar';
import { formatAmountDisplay, safeAmountToNumber, processAmountInput } from '../utils/numberUtils';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import NoteIcon from '@mui/icons-material/Note';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import EditIcon from '@mui/icons-material/Edit';
import emailToName from '../utils/emailToName';
import {
  filterDriverStatementRows,
  getStatementRowCompany,
} from '../utils/driverStatementFilter';
import { RemittanceEditTable, RemittanceDraftRow } from './RemittanceEditTable';
import { resolveBankCodeInput, getBankPrimaryNameFromCode } from '../utils/bankCodeAliases';
import { mergeRemittanceDraftByVehicle } from '../utils/remittanceMerge';
import { computeSanjeAndRemittance } from '../utils/remittanceAmounts';
import { withdrawalAccountMemoFrom } from '../utils/remittanceWithdrawalMemo';
import {
  applyThousandsFormatToNumericCellsInColumn,
  xlsxSheetColWidthsFromAoa,
  xlsxSheetRowsFromRowCount,
} from '../utils/xlsxAutoWidth';

registerAllModules();

// 타입 정의
interface SettlementRow {
  id: string;
  date: string;
  company?: string;
  vehicleNumber?: string;
  route?: string;
  itemName?: string;
  cargo?: string;
  billingAmount?: number;
  department?: string;
  contact?: string;
  paymentAmount?: number;
  profit?: number;
  businessNumber?: string;
  driverName?: string;
  settlementStatus?: string;
  settlementDate?: string | null;
  Receipt?: string;
  remarks?: string;
  distance?: number | null;
  weight?: number | null;
  order?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any; // 인덱스 시그니처 추가
}

const COLUMN_HEADERS = [
  '날짜', '거래처상호', '차량번호', '운송구간', '품명', '차종', '청구운송비', '소속', '연락처', '지불운송비', '손익', '사업자번호', '이름', '정산상태', '정산일', '인수증', '비고', '거리', '중량'
];
const COLUMN_FIELDS = [
  'date', 'company', 'vehicleNumber', 'route', 'itemName', 'cargo', 'billingAmount', 'department', 'contact', 'paymentAmount', 'profit', 'businessNumber', 'driverName', 'settlementStatus', 'settlementDate', 'Receipt', 'remarks', 'distance', 'weight'
];
const colWidths = [110, 160, 120, 190, 90, 60, 90, 100, 120, 90, 110, 100, 100, 110, 120, 100, 150, 72, 72];

const SETTLEMENT_STATUS_COL = COLUMN_FIELDS.indexOf('settlementStatus');
const RECEIPT_COL = COLUMN_FIELDS.indexOf('Receipt');

/** Handsontable 하단 여백(px). 표가 뷰 하단까지 꽉 차지 않게 해 가독성 유지 */
const SETTLEMENT_TABLE_BOTTOM_INSET = 36;

// formatNumberComma 함수를 공통 함수로 대체

/** PDF 캡처 전 DOM·웹폰트 렌더 완료 대기 */
async function waitForStatementPdfRender(): Promise<void> {
  await new Promise<void>(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {
    /* ignore */
  }
  await new Promise<void>(resolve => setTimeout(resolve, 80));
}

/** 사업자등록번호 10자리 → XXX-XX-XXXXX */
function formatBusinessNumber(raw: unknown): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length !== 10) return String(raw ?? '').trim();
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

const STATEMENT_SUPPLIERS = {
  seongwoo: {
    registrationNumber: '606-81-18248',
    name: '성우로지스(주)',
    ceo: '장우성',
    address: '부산 사상구 광장로 10 화인빌딩 12층',
    phone: '051-316-3500',
    fax: '051-316-3502',
  },
  korea: {
    registrationNumber: '606-27-34019',
    name: '한국운수',
    ceo: '장정호',
    address: '부산 사상구 광장로 10 화인빌딩 12층',
    phone: '051-316-3500',
    fax: '051-316-3502',
  },
};

function parseNumber(val: any) {
  if (val === undefined || val === null || val === '') return 0;
  return Number(String(val).replace(/[^\d.-]/g, '')) || 0;
}

/** 손익 표시·스타일·INSERT 병합: 항상 청구−지불 (DB profit 필드와 어긋날 수 있음) */
function settlementProfitFromRow(row: { billingAmount?: unknown; paymentAmount?: unknown }): number {
  return safeAmountToNumber(row.billingAmount) - safeAmountToNumber(row.paymentAmount);
}

/** Handsontable 날짜 셀 → YYYY-MM-DD (서버 UPDATE·newDate 공통) */
function normalizeSettlementDateCell(v: any): string {
  if (v == null || v === '') return '';
  if (v instanceof Date) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, '0');
    const d = String(v.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const t = Date.parse(s);
  if (!Number.isNaN(t)) {
    const d = new Date(t);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return s;
}

function getDateList(start: string, end: string): string[] {
  const result: string[] = [];
  let cur = new Date(start);
  const endDate = new Date(end);
  while (cur <= endDate) {
    result.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return result;
}

// 운송구간 운임 이력용 routeKey (통합일보·route-history-cache API와 동일)
function safeFirestoreId(str: string) {
  return (str || '').replace(/[\/.#$\[\]]/g, '_');
}
function makeRouteKey(company: string, route: string, cargo: string) {
  return safeFirestoreId(`${company}|${route}|${cargo}`);
}

/** 백업 목록/복구 API — 사내 사용자 인증용 Firebase ID 토큰 */
async function settlementBackupAuthHeaders(jsonBody?: boolean): Promise<HeadersInit> {
  const u = auth.currentUser;
  if (!u) throw new Error('로그인이 필요합니다.');
  const token = await u.getIdToken();
  const h: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (jsonBody) h['Content-Type'] = 'application/json';
  return h;
}

const SettlementReportPage: React.FC<{ 
  isFullScreen: boolean; 
  setIsFullScreen: (v: boolean) => void;
  statementSidebarCollapsed?: boolean;
  setStatementSidebarCollapsed?: (v: boolean) => void;
}> = ({ isFullScreen, setIsFullScreen, statementSidebarCollapsed, setStatementSidebarCollapsed }) => {
  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState<any[]>([]);
  // 오늘 날짜 YYYY-MM-DD (로컬 기준 — toISOString은 UTC라 새벽/오전에 어제로 나올 수 있음)
  const getTodayLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(() => {
    const today = getTodayLocal();
    return { start: today, end: today };
  });
  const [pendingDateRange, setPendingDateRange] = useState<{ start: string; end: string }>(() => {
    const today = getTodayLocal();
    return { start: today, end: today };
  });
  const [filters, setFilters] = useState({ 
    company: [] as string[], 
    date: [] as string[], 
    vehicleNumber: '', 
    route: '', 
    itemName: [] as string[], 
    cargo: [] as string[],
    department: [] as string[],
    settlementStatus: [] as string[],
    settlementDate: [] as string[], // 정산일 필터 추가
    remarks: ''
  });

  // 필터 팝업 상태 관리
  const [filterPopups, setFilterPopups] = useState({
    company: false,
    date: false,
    itemName: false,
    cargo: false,
    department: false,
    settlementStatus: false,
    settlementDate: false
  });
  const [filterSectionVisible, setFilterSectionVisible] = useState(false);
  const [collectedFilters, setCollectedFilters] = useState<Record<string, { value: string; mode: 'exact' | 'partial' }>>({});
  // 감추기: 특정 값과 완전 일치하는 행을 숨기는 필터 (항상 완전 일치)
  const [hiddenFilters, setHiddenFilters] = useState<Record<string, { value: string }>>({});
  /** 풀기 모달 표시 여부 */
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  /** 풀기 모달에서 체크된 항목 (컬럼 필드명 집합) — 완료 시 이 항목만 유지 */
  const [releaseModalChecked, setReleaseModalChecked] = useState<Record<string, boolean>>({});
  /** 우클릭 검색: 컬럼별 부분 일치 검색 (거래처상호·운송구간 등) */
  const [searchFilter, setSearchFilter] = useState<{ field: string; query: string } | null>(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchModalField, setSearchModalField] = useState<string | null>(null);
  const [searchModalQuery, setSearchModalQuery] = useState('');
  /** 우클릭 "행 추가" 시 몇 행 추가할지 입력받는 모달 */
  const [showAddRowsModal, setShowAddRowsModal] = useState(false);
  const [addRowsCountInput, setAddRowsCountInput] = useState('');
  const [addRowsContextRowIndex, setAddRowsContextRowIndex] = useState<number>(0);
  /** 우클릭 "행 삭제" 시 선택된 여러 행 삭제를 위한 모달 */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalTargets, setDeleteModalTargets] = useState<any[]>([]);
  const [deleteModalStep, setDeleteModalStep] = useState<1 | 2>(1);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  /** 운송구간 운임 이력 모드: ON이면 운송구간 클릭 시 이력 사이드바, 편집 불가 / OFF면 기존처럼 편집 */
  const [routeFreightHistoryMode, setRouteFreightHistoryMode] = useState(false);
  const [routeHistoryOpen, setRouteHistoryOpen] = useState(false);
  const [routeHistoryData, setRouteHistoryData] = useState<{
    recentHistory: any[];
    avg: number | null;
    mode: number | null;
    min: number | null;
    max: number | null;
    count: number | null;
    lastUsed: string | null;
  }>({
    recentHistory: [],
    avg: null,
    mode: null,
    min: null,
    max: null,
    count: null,
    lastUsed: null,
  });
  const [routeHistoryLoading, setRouteHistoryLoading] = useState(false);
  const [routeHistoryHasMore, setRouteHistoryHasMore] = useState(true);
  const [routeHistoryOffset, setRouteHistoryOffset] = useState(0);
  const routeHistoryKeyRef = useRef<string | null>(null);

  /** 동시 접속자 및 마지막 편집 정보 */
  const [presenceUsers, setPresenceUsers] = useState<{ email: string; name: string }[]>([]);
  const [lastEditInfo, setLastEditInfo] = useState<{ email: string; name: string; timestamp: string } | null>(null);
  const [pendingChangesCount, setPendingChangesCount] = useState(0);

  /** 선택 고정: ON이면 클릭/드래그 시 선택만 추가, 해제 시 전체 선택 해제 */
  const [selectionLockMode, setSelectionLockMode] = useState(false);

  /** 정산일보: 기본 읽기 전용 — 입력은 비밀번호(3500) 후 기존과 동일한 편집 동작 */
  const [settlementViewMode, setSettlementViewMode] = useState<'read' | 'input'>('read');
  const [settlementPasswordModalOpen, setSettlementPasswordModalOpen] = useState(false);
  const [settlementPasswordInput, setSettlementPasswordInput] = useState('');
  const [settlementPasswordError, setSettlementPasswordError] = useState('');

  const confirmSettlementPassword = useCallback(() => {
    if (settlementPasswordInput === '3500') {
      setSettlementViewMode('input');
      setSettlementPasswordModalOpen(false);
      setSettlementPasswordInput('');
      setSettlementPasswordError('');
    } else {
      setSettlementPasswordError('비밀번호가 올바르지 않습니다.');
    }
  }, [settlementPasswordInput]);

  const cancelSettlementPassword = useCallback(() => {
    setSettlementPasswordModalOpen(false);
    setSettlementPasswordInput('');
    setSettlementPasswordError('');
  }, []);

  /** 변경 이력(감사 로그) 모달 */
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [auditLogEntries, setAuditLogEntries] = useState<Array<{ id: number; entityDate: string; entityRowId: string; columnName: string; oldValue: string | null; newValue: string | null; changedBy: string | null; changedAt: string }>>([]);
  const [auditLogTitle, setAuditLogTitle] = useState('');
  const [auditLogHasMore, setAuditLogHasMore] = useState(false);
  const [auditLogLoadingMore, setAuditLogLoadingMore] = useState(false);
  const auditLogQueryRef = useRef<
    | { type: 'row'; date: string; rowId: string }
    | { type: 'all' }
    | null
  >(null);

  /** 백업 복구 모달 */
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [backupList, setBackupList] = useState<Array<{ filename: string; date: string }>>([]);
  const [backupListLoading, setBackupListLoading] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreSecret, setRestoreSecret] = useState('');
  const [restoreError, setRestoreError] = useState<string | null>(null);

  /** 송금(대량이체 준비) 1단계: 송금 편집 모달 */
  const [remittanceModalOpen, setRemittanceModalOpen] = useState(false);
  /** 차량·행 단위 원본 (송금내역 엑셀용 — 통합 없음) */
  const [remittanceDetailRows, setRemittanceDetailRows] = useState<RemittanceDraftRow[]>([]);
  /** 차량번호별 통합 행 (모달·대량이체 엑셀) */
  const [remittanceDraftRows, setRemittanceDraftRows] = useState<RemittanceDraftRow[]>([]);

  /** 차량 운임 이력 모드: ON이면 차량번호 클릭 시 이력 사이드바, 편집 불가 / OFF면 기존처럼 편집 */
  const [vehicleFreightHistoryMode, setVehicleFreightHistoryMode] = useState(false);
  const [vehicleHistoryOpen, setVehicleHistoryOpen] = useState(false);
  const [vehicleHistoryData, setVehicleHistoryData] = useState<{
    recentHistory: any[];
    avg: number | null;
    mode: number | null;
    min: number | null;
    max: number | null;
    count: number | null;
    lastUsed: string | null;
  }>({
    recentHistory: [],
    avg: null,
    mode: null,
    min: null,
    max: null,
    count: null,
    lastUsed: null,
  });
  const [vehicleHistoryLoading, setVehicleHistoryLoading] = useState(false);
  const [vehicleHistoryHasMore, setVehicleHistoryHasMore] = useState(true);
  const [vehicleHistoryVehicleNumber, setVehicleHistoryVehicleNumber] = useState('');

  // 임시 선택 상태 (팝업에서 사용)
  const [tempSelections, setTempSelections] = useState({
    company: [] as string[],
    date: [] as string[],
    itemName: [] as string[],
    cargo: [] as string[],
    department: [] as string[],
    settlementStatus: [] as string[],
    settlementDate: [] as string[]
  });

  // 필터 팝업 검색어 상태
  const [filterSearchTerms, setFilterSearchTerms] = useState({
    company: '',
    date: '',
    itemName: '',
    cargo: '',
    department: '',
    settlementStatus: '',
    settlementDate: ''
  });
  const [dirty, setDirty] = useState(false);
  const hotRef = useRef<any>(null);
  /** Handsontable은 부모 height %가 불안정할 수 있어 영역을 측정해 픽셀 높이로 전달 */
  const settlementTableAreaRef = useRef<HTMLDivElement | null>(null);
  const [settlementHotHeight, setSettlementHotHeight] = useState(480);
  const [saveStatus, setSaveStatus] = useState<{ [rowId: string]: { status: 'success' | 'error' | 'saving' | null, message?: string } }>({});
  const [hasRestored, setHasRestored] = useState(false);
  const lastEventIdRef = useRef<string | null>(null);
  const [showStatementModal, setShowStatementModal] = useState(false);
  // 사이드바 열기/닫기와 숨기기/펼치기 분리
  const isStatementSidebarOpen = showStatementModal;
  const setIsStatementSidebarOpen = setShowStatementModal;
  const isStatementSidebarCollapsed = statementSidebarCollapsed !== undefined ? statementSidebarCollapsed : false;
  const [statementDateRange, setStatementDateRange] = useState<{ start: string; end: string }>(() => {
    const today = getTodayLocal();
    return { start: today, end: today };
  });
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<string[]>([]);
  const [excelGenerating, setExcelGenerating] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [selectedTemplateType, setSelectedTemplateType] = useState<'A' | 'B' | 'C' | 'D' | 'ramen' | ''>('');
  const [taxGenerating, setTaxGenerating] = useState(false);
  const [supplierType, setSupplierType] = useState<'seongwoo' | 'korea'>('seongwoo');
  const [selectedItemNames, setSelectedItemNames] = useState<string[]>([]);
  const [itemNameOptions, setItemNameOptions] = useState<string[]>([]); // 추가
  const [driverVehicleInput, setDriverVehicleInput] = useState('');
  const [driverVehicleList, setDriverVehicleList] = useState<string[]>([]);
  const prevRowsRef = useRef<any[]>([]);
  const collectedRowsRef = useRef<any[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [statsBarVisible, setStatsBarVisible] = useState(false);
  const lastFetchId = useRef(0);
  /** INSERT 직후 교체된 행 id — 필터에서 vehicle/route 없어도 표시 유지 (다음 조회 시 초기화) */
  const recentlyInsertedRowIds = useRef<Set<string>>(new Set());
  /** 마지막 행에서 아래 방향키로 새 행 추가 후 포커스할 행 인덱스 (엑셀 스타일) */
  const pendingSelectRowIndexRef = useRef<number | null>(null);
  /**
   * Handsontable: 휠로만 세로 스크롤하고 커서는 거의 안 움직이면 mousedown 행과 mouseup 행이 달라져
   * 드래그로 오인되어 여러 행이 잡힘 → 화면 좌표 이동이 작을 때만 한 셀로 보정 (afterOnCellMouseUp).
   */
  const cellMouseDownRef = useRef<{
    row: number;
    col: number;
    x: number;
    y: number;
    shift: boolean;
    ctrl: boolean;
  } | null>(null);
  
  // 선택된 행: ref는 즉시 반영(셀 스타일용), state는 디바운스(합계 표시용) — 클릭 시 리렌더 최소화
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const selectedRowsRef = useRef<Set<number>>(new Set());
  const debouncedSetSelectedRowsRef = useRef(
    debounce((rowsSet: Set<number>) => setSelectedRows(rowsSet), 150)
  ).current;

  // 이벤트 ID ref 분리
  const lastSettlementEventIdRef = useRef<string | null>(null);
  const lastRowsEventIdRef = useRef<string | null>(null);

  // 디바운스된 fetch 함수 추가
  const debouncedFetch = useCallback(
    debounce(async () => {
      const currentId = ++lastFetchId.current;
      try {
        const res = await fetch(`/api/settlement-reports/pg?start=${dateRange.start}&end=${dateRange.end}`);
        const data = await res.json();
        if (currentId !== lastFetchId.current) return; // 최신 요청만 반영
        // 503 등 실패 시 JSON이 { error } 객체라 배열이 아님 → 빈 표로 두고 크래시 방지
        const rows = res.ok && Array.isArray(data) ? data : [];
        if (!res.ok && data && typeof data === 'object' && 'error' in data) {
          console.warn('정산일보(PG) 조회 실패:', (data as { error?: string }).error);
        }
        setAllRows(rows);
      } catch (error) {
        console.error('데이터 fetch 실패:', error);
      } finally {
        if (currentId === lastFetchId.current) {
          setLoading(false);
        }
      }
    }, 300),
    [dateRange]
  );

  // 운송구간(route) 자동완성(타이핑 중 prefix)용: 간단 캐시 + 디바운스
  const routePrefixSuggestCacheRef = useRef<Map<string, { t: number; items: string[] }>>(new Map());
  const routePrefixSuggestTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const routePrefixSuggestLastQueryRef = useRef<string>('');
  const routeAutocompleteSource = useCallback((q: any, process: (items: string[]) => void) => {
    const query = String(q ?? '').trim();
    const minLen = 2;
    const ttlMs = 5 * 60 * 1000;

    if (routePrefixSuggestTimerRef.current) clearTimeout(routePrefixSuggestTimerRef.current);

    if (!query || query.length < minLen) {
      process([]);
      return;
    }

    const cached = routePrefixSuggestCacheRef.current.get(query);
    if (cached && Date.now() - cached.t < ttlMs) {
      process(cached.items);
      return;
    }

    routePrefixSuggestLastQueryRef.current = query;
    routePrefixSuggestTimerRef.current = setTimeout(async () => {
      try {
        // 최신 쿼리만 반영
        if (routePrefixSuggestLastQueryRef.current !== query) return;
        const params = new URLSearchParams({ q: query, limit: '15' });
        const res = await fetch(`/api/settlement-reports/pg/route-suggestions-prefix?${params.toString()}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data.filter(Boolean).map((v: any) => String(v)) : [];
        routePrefixSuggestCacheRef.current.set(query, { t: Date.now(), items: list });
        process(list);
      } catch {
        process([]);
      }
    }, 250);
  }, []);

  // 거래처상호(company) 자동완성(타이핑 중 prefix)용: 간단 캐시 + 디바운스
  const companyPrefixSuggestCacheRef = useRef<Map<string, { t: number; items: string[] }>>(new Map());
  const companyPrefixSuggestTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const companyPrefixSuggestLastQueryRef = useRef<string>('');
  const companyAutocompleteSource = useCallback((q: any, process: (items: string[]) => void) => {
    const query = String(q ?? '').trim();
    const minLen = 2;
    const ttlMs = 5 * 60 * 1000;

    if (companyPrefixSuggestTimerRef.current) clearTimeout(companyPrefixSuggestTimerRef.current);

    if (!query || query.length < minLen) {
      process([]);
      return;
    }

    const cached = companyPrefixSuggestCacheRef.current.get(query);
    if (cached && Date.now() - cached.t < ttlMs) {
      process(cached.items);
      return;
    }

    companyPrefixSuggestLastQueryRef.current = query;
    companyPrefixSuggestTimerRef.current = setTimeout(async () => {
      try {
        if (companyPrefixSuggestLastQueryRef.current !== query) return;
        const params = new URLSearchParams({ q: query, limit: '15' });
        const res = await fetch(`/api/settlement-reports/pg/company-suggestions-prefix?${params.toString()}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data.filter(Boolean).map((v: any) => String(v)) : [];
        companyPrefixSuggestCacheRef.current.set(query, { t: Date.now(), items: list });
        process(list);
      } catch {
        process([]);
      }
    }, 250);
  }, []);

  useLayoutEffect(() => {
    const el = settlementTableAreaRef.current;
    if (!el) return;
    const apply = () => {
      const raw = Math.floor(el.getBoundingClientRect().height);
      const h = Math.max(120, raw - SETTLEMENT_TABLE_BOTTOM_INSET);
      setSettlementHotHeight(prev => (prev === h ? prev : h));
    };
    apply();
    const ro = new ResizeObserver(() => {
      apply();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const hot = hotRef.current?.hotInstance;
    if (hot) hot.refreshDimensions?.();
  }, [settlementHotHeight]);

  // 날짜 범위가 변경될 때마다 데이터를 fetch로 불러오기 (항상 서버 fetch)
  useEffect(() => {
    if (!dateRange.start || !dateRange.end) return;
    setLoading(true);
    debouncedFetch();
  }, [dateRange, debouncedFetch]);

  const fetchRows = useCallback(async (start: string, end: string) => {
    recentlyInsertedRowIds.current.clear();
    const currentId = ++lastFetchId.current;
    try {
      const res = await fetch(`/api/settlement-reports/pg?start=${start}&end=${end}`);
      if (!res.ok) throw new Error('데이터 fetch 실패');
      const rows = await res.json();
      if (currentId !== lastFetchId.current) return;

      const toAmount = (v: any) => (v === null || v === undefined || v === '') ? null : parseNumber(v);
      const processedRows = Array.isArray(rows) ? rows.map((row: any) => ({
        ...row,
        billingAmount: toAmount(row.billingAmount),
        paymentAmount: toAmount(row.paymentAmount),
        profit: toAmount(row.profit),
      })) : [];

      setAllRows(processedRows);
    } catch (error) {
      setAllRows([]);
    } finally {
      if (currentId === lastFetchId.current) {
        setLoading(false);
      }
    }
  }, []);



  // 필터 적용 (API 오류 등으로 allRows가 배열이 아닐 때 방어)
  const filteredRows = (Array.isArray(allRows) ? allRows : []).filter(row => {
    const isNewRow = String(row.id || '').startsWith('new-');
    const justInserted = row.id ? recentlyInsertedRowIds.current.has(String(row.id)) : false;

    // 새 행·방금 저장한 행은 나머지 필터 없이 통과 (Delete로 비워도 행은 항상 보이도록 유지)
    if (isNewRow || justInserted) return true;

    // 거래처 필터 (다중 선택)
    if (filters.company.length > 0 && !filters.company.some(company => row.company?.includes(company))) return false;
    
    if (filters.vehicleNumber && !String(row.vehicleNumber || '').includes(filters.vehicleNumber)) return false;
    if (filters.route && !row.route?.includes(filters.route)) return false;
    
    // 품명 필터 (다중 선택)
    if (filters.itemName.length > 0) {
      const includeEmpty = filters.itemName.includes('품명 없음');
      const selectedNonEmpty = filters.itemName.filter(n => n !== '품명 없음');
      const itemNameVal = row.itemName || '';
      const isEmptyItemName = itemNameVal === null || itemNameVal === undefined || String(itemNameVal).trim() === '';
      const matchesNonEmpty = selectedNonEmpty.length > 0 && selectedNonEmpty.some(n => (row.itemName || '').includes(n));
      if (!((includeEmpty && isEmptyItemName) || matchesNonEmpty)) return false;
    }
    
    // 차종 필터 (다중 선택)
    if (filters.cargo.length > 0) {
      const includeEmpty = filters.cargo.includes('차종 없음');
      const selectedNonEmpty = filters.cargo.filter(c => c !== '차종 없음');
      const cargoVal = row.cargo || '';
      const isEmptyCargo = cargoVal === null || cargoVal === undefined || String(cargoVal).trim() === '';
      const matchesNonEmpty = selectedNonEmpty.length > 0 && selectedNonEmpty.some(c => row.cargo === c);
      if (!((includeEmpty && isEmptyCargo) || matchesNonEmpty)) return false;
    }
    
    // 소속 필터 (다중 선택)
    if (filters.department.length > 0) {
      const includeEmpty = filters.department.includes('소속 없음');
      const selectedNonEmpty = filters.department.filter(d => d !== '소속 없음');
      const departmentVal = row.department || '';
      const isEmptyDepartment = departmentVal === null || departmentVal === undefined || String(departmentVal).trim() === '';
      const matchesNonEmpty = selectedNonEmpty.length > 0 && selectedNonEmpty.some(d => row.department?.includes(d));
      if (!((includeEmpty && isEmptyDepartment) || matchesNonEmpty)) return false;
    }
    
    // 날짜 필터 (다중 선택)
    if (filters.date.length > 0 && !filters.date.includes(row.date)) return false;
    
    // 정산상태 필터 (다중 선택)
    if (filters.settlementStatus.length > 0 && !filters.settlementStatus.includes(row.settlementStatus)) return false;
    
    // 정산일 필터 (다중 선택)
    if (filters.settlementDate.length > 0 && !filters.settlementDate.includes(row.settlementDate)) return false;
    
    if (filters.remarks?.trim()) {
      const q = filters.remarks.trim().toLowerCase();
      if (!String(row.remarks ?? '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // 날짜+order+회사 순으로 정렬된 행 (order 우선: 회사명 수정 시에도 행 위치 유지)
  const sortedRows = [...filteredRows].sort((a, b) => {
    if (a.date < b.date) return -1;
    if (a.date > b.date) return 1;
    const ao = a.order ?? 0;
    const bo = b.order ?? 0;
    if (ao < bo) return -1;
    if (ao > bo) return 1;
    if ((a.company || '') < (b.company || '')) return -1;
    if ((a.company || '') > (b.company || '')) return 1;
    return 0;
  });

  // 모으기/감추기 적용:
  // - 모으기(collectedFilters): 조건을 만족하는 행만 남김
  // - 감추기(hiddenFilters): 조건과 완전 일치하는 행은 숨김
  // 새 행·방금 저장한 행은 항상 포함
  const collectedRows = useMemo(() => {
    const keys = Object.keys(collectedFilters);
    const hiddenKeys = Object.keys(hiddenFilters);
    if (keys.length === 0 && hiddenKeys.length === 0) return sortedRows;
    return sortedRows.filter(row => {
      if (String(row.id || '').startsWith('new-')) return true;
      if (row.id && recentlyInsertedRowIds.current.has(String(row.id))) return true;
      // 모으기 조건: 모두 만족해야 통과
      const collectOk = keys.every(field => {
        const f = collectedFilters[field];
        const cellVal = String(row[field] ?? '').trim();
        if (f.mode === 'partial') return cellVal.toLowerCase().includes(f.value.toLowerCase());
        return cellVal === f.value.trim();
      });
      if (!collectOk) return false;
      // 감추기 조건: 하나라도 일치하면 숨김
      if (hiddenKeys.length > 0) {
        for (const field of hiddenKeys) {
          const hv = hiddenFilters[field].value.trim();
          const cellVal = String(row[field] ?? '').trim();
          if (hv && cellVal === hv) return false;
        }
      }
      return true;
    });
  }, [sortedRows, collectedFilters, hiddenFilters]);

  // 검색 적용: searchFilter가 있으면 해당 컬럼 값에 검색어가 포함된 행만 표시. 새 행·방금 저장한 행은 항상 포함.
  const displayedRows = useMemo(() => {
    if (!searchFilter?.query?.trim()) return collectedRows;
    const q = searchFilter.query.trim().toLowerCase();
    const field = searchFilter.field;
    return collectedRows.filter(row => {
      if (String(row.id || '').startsWith('new-')) return true;
      if (row.id && recentlyInsertedRowIds.current.has(String(row.id))) return true;
      return String(row[field] ?? '').toLowerCase().includes(q);
    });
  }, [collectedRows, searchFilter]);

  /** 송금: 현재 표시 행이 모두 정산이고 정산일이 하나로 같을 때 활성(필터·모으기·검색 반영, 우클릭 모으기 전용 조건 없음) */
  const remittanceButtonEnabled = useMemo(() => {
    if (displayedRows.length === 0) return false;
    const allSettled = displayedRows.every(
      r =>
        String(r.settlementStatus || '') === '정산' &&
        String(r.settlementDate || '').trim() !== ''
    );
    if (!allSettled) return false;
    const dates = new Set(
      displayedRows.map(r => String(r.settlementDate || '').trim()).filter(Boolean)
    );
    if (dates.size !== 1) return false;
    return true;
  }, [displayedRows]);

  const remittanceSummaryDate = useMemo(() => {
    if (!remittanceButtonEnabled || displayedRows.length === 0) return '';
    return String(displayedRows[0].settlementDate || '').trim();
  }, [remittanceButtonEnabled, displayedRows]);

  /** 송금 모달 표시 행 기준 송금액(부가세포함) 총합 */
  const remittanceAmountTotalSum = useMemo(
    () => remittanceDraftRows.reduce((sum, r) => sum + safeAmountToNumber(r.colC), 0),
    [remittanceDraftRows]
  );

  /** 송금 모달: 차량번호별 저장된 은행코드·계좌 upsert */
  const persistDriverBankAccount = useCallback(async (row: RemittanceDraftRow) => {
    const vn = String(row.vehicleNumber || '').trim();
    if (!vn) return;
    const bankCode = String(resolveBankCodeInput(row.colA)).trim();
    const accountNumber = String(row.colB).replace(/\s/g, '').trim();
    if (!bankCode || !accountNumber) return;
    const businessName = String(row.businessName ?? '').trim();
    try {
      const res = await fetch(
        `/api/settlement-reports/pg/driver-bank-accounts/${encodeURIComponent(vn)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bankCode,
            accountNumber,
            businessName,
            userEmail: auth.currentUser?.email ?? undefined,
          }),
        }
      );
      if (!res.ok) {
        const errText = await res.text();
        let msg = errText;
        try {
          const j = JSON.parse(errText) as { error?: string };
          if (j?.error) msg = j.error;
        } catch {
          /* raw text */
        }
        console.warn('[driver_bank_accounts] 저장 실패', errText);
        alert(`계좌/상호명 저장 실패: ${msg || res.status}`);
      }
    } catch (e) {
      console.warn('[driver_bank_accounts] 저장 오류', e);
      alert('계좌/상호명 저장 중 오류가 발생했습니다. 네트워크를 확인해 주세요.');
    }
  }, []);

  /** 송금 모달 예금주 → 정산일보 이름(driverName) PG 반영 (차량 통합 행이면 동일 차량 행 전부) */
  const persistRemittanceDriverName = useCallback(
    async (draftRow: RemittanceDraftRow, newName: string) => {
      const name = newName.trim();
      const vn = String(draftRow.vehicleNumber ?? '').trim();
      const isMerged = String(draftRow.sourceId || '').startsWith('merged-');

      let targets: { id: string; date: string }[] = [];
      if (isMerged && vn) {
        targets = remittanceDetailRows
          .filter(r => String(r.vehicleNumber ?? '').trim() === vn)
          .map(r => ({ id: String(r.sourceId), date: String(r.date || '').trim() }))
          .filter(t => t.id && t.date);
      } else {
        const id = String(draftRow.sourceId);
        const date = String(draftRow.date || '').trim();
        if (id && !id.startsWith('merged-') && date) targets = [{ id, date }];
      }

      if (targets.length === 0) return;

      for (const t of targets) {
        try {
          const res = await fetch('/api/settlement-reports/update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: t.id,
              date: t.date,
              driverName: name,
              userId: auth.currentUser?.uid,
              userEmail: auth.currentUser?.email,
            }),
          });
          if (!res.ok) continue;
          setAllRows(prev => prev.map(row => (String(row.id) === t.id ? { ...row, driverName: name } : row)));
        } catch (e) {
          console.warn('[송금] 이름 동기화 실패', e);
        }
      }

      setRemittanceDetailRows(prev =>
        prev.map(r => {
          if (isMerged && vn && String(r.vehicleNumber ?? '').trim() === vn) return { ...r, colD: name };
          if (String(r.sourceId) === String(draftRow.sourceId)) return { ...r, colD: name };
          return r;
        })
      );
    },
    [remittanceDetailRows]
  );

  const handleOpenRemittanceModal = useCallback(() => {
    if (!remittanceButtonEnabled) return;
    const next: RemittanceDraftRow[] = displayedRows.map(r => {
      const pay = safeAmountToNumber(r.paymentAmount);
      const businessNumberStr = String(r.businessNumber ?? '').trim();
      const { sanje, remittance } = computeSanjeAndRemittance(pay, businessNumberStr);
      const name = String(r.driverName ?? '').trim();
      const vn = String(r.vehicleNumber ?? '').trim();
      const route = String(r.route ?? '').trim();
      return {
        sourceId: String(r.id),
        date: String(r.date ?? '').trim(),
        vehicleNumber: vn,
        route,
        colA: '',
        colB: '',
        businessName: '',
        paymentAmount: pay,
        colC: remittance,
        colSanje: sanje,
        colD: name,
        colE: businessNumberStr,
        colF: withdrawalAccountMemoFrom('', vn),
      };
    });
    const merged = mergeRemittanceDraftByVehicle(next);
    setRemittanceDetailRows(next);
    setRemittanceDraftRows(merged);
    setRemittanceModalOpen(true);

    const vehicles = [...new Set(merged.map(r => r.vehicleNumber).filter(Boolean))];
    if (vehicles.length === 0) return;
    void (async () => {
      try {
        const res = await fetch('/api/settlement-reports/pg/driver-bank-accounts/lookup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vehicles }),
        });
        if (!res.ok) return;
        const data = (await res.json()) as {
          accounts?: Record<string, { bank_code?: string; account_number?: string; business_name?: string }>;
        };
        const accounts = data.accounts || {};
        const applyLookup = (r: RemittanceDraftRow): RemittanceDraftRow => {
          const acc = accounts[r.vehicleNumber];
          if (!acc?.bank_code || !acc?.account_number) return r;
          const newBiz = acc.business_name != null ? String(acc.business_name) : r.businessName;
          return {
            ...r,
            colA: String(acc.bank_code),
            colB: String(acc.account_number),
            businessName: newBiz,
            colF: withdrawalAccountMemoFrom(newBiz, r.vehicleNumber),
          };
        };
        setRemittanceDraftRows(prev => prev.map(applyLookup));
        setRemittanceDetailRows(prev => prev.map(applyLookup));
      } catch {
        /* PG 미설정 등은 조용히 무시 */
      }
    })();
  }, [displayedRows, remittanceButtonEnabled]);

  const closeRemittanceModal = useCallback(() => {
    setRemittanceModalOpen(false);
    setRemittanceDetailRows([]);
  }, []);

  /** 송금 모달: 대량이체(실무 양식·헤더 없음 10열, J1=송금액 합계) + 회사 보관용(송금내역) 엑셀 2종 저장 */
  const handleExportRemittanceExcels = useCallback(() => {
    if (remittanceDraftRows.length === 0) {
      alert('내보낼 데이터가 없습니다.');
      return;
    }
    const safeBase = (remittanceSummaryDate || 'export').replace(/[/\\?*[\]:]/g, '-').trim() || 'export';

    /** 대량이체 엑셀: A~H 동일, I=운행일, J1=C열 송금액 합계(나머지 행 J는 빈칸) */
    const BULK_TRANSFER_COMPANY = '성우로지스(주)';
    const totalBulkRemittance = remittanceDraftRows.reduce(
      (sum, r) => sum + (Math.round(Number(r.colC)) || 0),
      0
    );
    const aoaBulk: (string | number)[][] = remittanceDraftRows.map((r, idx) => [
      String(r.colA ?? '').trim(),
      String(r.colB ?? '').replace(/\s/g, ''),
      Math.round(Number(r.colC)) || 0,
      BULK_TRANSFER_COMPANY,
      String(r.colF ?? '').trim(),
      String(r.vehicleNumber ?? '').trim(),
      String(r.colE ?? '').trim(),
      remittanceSummaryDate || '',
      String(r.date ?? '').trim(),
      idx === 0 ? totalBulkRemittance : '',
    ]);
    const wsBulk = XLSX.utils.aoa_to_sheet(aoaBulk);
    applyThousandsFormatToNumericCellsInColumn(wsBulk, 2);
    applyThousandsFormatToNumericCellsInColumn(wsBulk, 9);
    wsBulk['!cols'] = xlsxSheetColWidthsFromAoa(aoaBulk);
    wsBulk['!rows'] = xlsxSheetRowsFromRowCount(aoaBulk.length);
    const wbBulk = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wbBulk, wsBulk, '대량이체');
    XLSX.writeFile(wbBulk, `${safeBase}_대량이체.xlsx`);

    const headerInternal = [
      '날짜',
      '차량번호',
      '운송구간',
      '이름',
      '사업자번호',
      '송금액',
      '산재보험',
      '합계',
      '은행명',
      '계좌번호',
      '상호명',
    ];
    /** 통합 행(remittanceDraftRows)에만 채워진 A·B·상호명을 차량번호로 송금내역 각 행에 전달 */
    const bankByVehicle = new Map<string, { colA: string; colB: string; businessName: string }>();
    for (const r of remittanceDraftRows) {
      const vn = String(r.vehicleNumber ?? '').trim();
      if (!vn) continue;
      bankByVehicle.set(vn, {
        colA: String(r.colA ?? ''),
        colB: String(r.colB ?? ''),
        businessName: String(r.businessName ?? ''),
      });
    }
    const internalRowsForExport =
      remittanceDetailRows.length > 0 ? remittanceDetailRows : remittanceDraftRows;
    const aoaInternal: (string | number)[][] = [
      headerInternal,
      ...internalRowsForExport.map(r => {
        const vn = String(r.vehicleNumber ?? '').trim();
        const merged = vn ? bankByVehicle.get(vn) : undefined;
        const rawBank = (merged?.colA ?? r.colA ?? '').trim();
        const account = (merged?.colB ?? r.colB ?? '').trim();
        const biz = merged?.businessName ?? r.businessName ?? '';
        const bankName = getBankPrimaryNameFromCode(rawBank);
        const pay = Math.round(Number(r.colC)) || 0;
        const sanje = Math.round(Number(r.colSanje)) || 0;
        return [
          String(r.date ?? '').trim(),
          r.vehicleNumber ?? '',
          r.route ?? '',
          r.colD,
          r.colE ?? '',
          pay,
          sanje,
          pay + sanje,
          bankName,
          account,
          biz
        ];
      })
    ];
    const wsInt = XLSX.utils.aoa_to_sheet(aoaInternal);
    applyThousandsFormatToNumericCellsInColumn(wsInt, 5);
    applyThousandsFormatToNumericCellsInColumn(wsInt, 6);
    applyThousandsFormatToNumericCellsInColumn(wsInt, 7);
    wsInt['!cols'] = xlsxSheetColWidthsFromAoa(aoaInternal);
    wsInt['!rows'] = xlsxSheetRowsFromRowCount(aoaInternal.length);
    const wbInt = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wbInt, wsInt, '송금내역');
    XLSX.writeFile(wbInt, `${safeBase}_송금내역.xlsx`);
  }, [remittanceDraftRows, remittanceDetailRows, remittanceSummaryDate]);

  // 표에 넘길 데이터 (모은 결과 + 검색 결과 기준)
  const tableData = displayedRows.map(row => COLUMN_FIELDS.map(f => {
    if (f === 'profit') {
      return formatAmountDisplay(settlementProfitFromRow(row));
    }
    if (f === 'billingAmount' || f === 'paymentAmount' || f === 'distance' || f === 'weight') {
      return formatAmountDisplay(row[f]);
    }
    return row[f] ?? '';
  }));

  useEffect(() => {
    collectedRowsRef.current = displayedRows;
  }, [displayedRows]);

  // 마지막 행에서 아래 방향키로 새 행 추가 후, 다음 렌더에서 새 행 첫 셀 선택 (엑셀 스타일)
  const prevTableDataLengthRef = useRef(0);
  useEffect(() => {
    const pending = pendingSelectRowIndexRef.current;
    if (pending !== null && tableData.length > prevTableDataLengthRef.current && pending < tableData.length) {
      const hot = hotRef.current?.hotInstance;
      if (hot) {
        hot.selectCell(pending, 0);
        hot.scrollViewportTo(pending, 0);
      }
      pendingSelectRowIndexRef.current = null;
    }
    prevTableDataLengthRef.current = tableData.length;
  }, [tableData.length]);

  useEffect(() => {
    if (showReleaseModal) {
      const allKeys = Array.from(new Set([
        ...Object.keys(collectedFilters),
        ...Object.keys(hiddenFilters),
      ]));
      setReleaseModalChecked(allKeys.reduce((acc, k) => ({ ...acc, [k]: true }), {}));
    }
  }, [showReleaseModal, collectedFilters, hiddenFilters]);

  // 엑셀 다운로드
  const handleDownloadExcel = () => {
    // 엑셀용 데이터만 따로 가공 (현재 표시된 모은+검색 결과 기준)
    const excelData = displayedRows.map(row => {
      // 각 행의 데이터를 새로 만듦
      return COLUMN_FIELDS.map(field => {
        // 금액 필드만 특별 처리
        if (field === 'billingAmount' || field === 'paymentAmount' || field === 'profit' || field === 'distance' || field === 'weight') {
          const rawValue = field === 'profit' ? settlementProfitFromRow(row) : row[field];
          // 숫자만 추출 (콤마, 공백 등 제거)
          const numericValue = String(rawValue).replace(/[^\d.-]/g, '');
          // 순수 숫자로 변환
          return Number(numericValue) || 0;
        }
        // 나머지 필드는 그대로
        return row[field] ?? '';
      });
    });

    const ws = XLSX.utils.aoa_to_sheet([COLUMN_HEADERS, ...excelData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '정산일보');
    XLSX.writeFile(wb, `정산일보_${dateRange.start}_${dateRange.end}.xlsx`);
  };

  /** 정산 행 추가: 현재 기간 시작일 기준 빈 행을 allRows에 추가 (저장 시 INSERT API 호출). 정렬 시 맨 아래로 가도록 order 부여 */
  const handleAddRow = () => {
    const newId = 'new-' + Date.now();
    const newRow: any = {
      id: newId,
      date: dateRange.start,
      company: '',
      vehicleNumber: '',
      route: '',
      itemName: '',
      cargo: '',
      billingAmount: undefined,
      department: '',
      contact: '',
      paymentAmount: undefined,
      profit: undefined,
      businessNumber: '',
      driverName: '',
      distance: undefined,
      weight: undefined,
      settlementStatus: '미정산',
      settlementDate: '',
      Receipt: '미수취',
      remarks: '',
      order: 999999
    };
    setAllRows(prev => [...prev, newRow]);
  };

  /** 우클릭 "행 추가" 모달에서 확인 시: 지정한 개수만큼 클릭한 행 바로 아래에 빈 행 삽입 (order는 항상 정수로 사용) */
  const handleAddRowsConfirm = () => {
    const n = Math.max(1, Math.min(100, parseInt(addRowsCountInput, 10) || 1));
    addRowsBelowDisplayedIndex(addRowsContextRowIndex, n);
    setShowAddRowsModal(false);
    setAddRowsCountInput('1');
  };

  /** 지정한 표시 행 인덱스 바로 아래에 N개 행 삽입 (우클릭 행추가·아래방향키 공용) */
  const addRowsBelowDisplayedIndex = useCallback((rowIndex: number, count: number) => {
    const contextRow = displayedRows[rowIndex];
    const insertDate = contextRow?.date ?? dateRange.start;
    const baseOrder = contextRow && (contextRow.order !== undefined && contextRow.order !== null)
      ? Math.trunc(Number(contextRow.order))
      : 0;
    const n = Math.max(1, Math.min(100, count));

    const makeNewRows = (): any[] =>
      Array.from({ length: n }, (_, i) => ({
        id: 'new-' + Date.now() + '-' + i + '-' + Math.random().toString(36).slice(2, 9),
        date: insertDate,
        company: '',
        vehicleNumber: '',
        route: '',
        itemName: '',
        cargo: '',
        billingAmount: undefined,
        department: '',
        contact: '',
        paymentAmount: undefined,
        profit: undefined,
        businessNumber: '',
        driverName: '',
        distance: undefined,
        weight: undefined,
        settlementStatus: '미정산',
        settlementDate: '',
        Receipt: '미수취',
        remarks: '',
        order: baseOrder + i + 1
      }));

    /** 이미 PG에 있는 행만: order 밀린 값을 DB에 반영해야 새로고침 후에도 순서가 유지됨 */
    let orderUpdatesForPg: { id: string; date: string; order: number }[] = [];

    setAllRows(prev => {
      if (!contextRow?.id) return [...prev, ...makeNewRows()];

      // 같은 날짜에서 order > 기준행 인 기존 행 order를 먼저 N만큼 밀어야 함.
      // 그렇지 않으면 새 행 order(base+1~base+N)가 기존 행과 겹쳐, 정렬(날짜→order→회사) 시
      // 정산/미정산과 무관하게 한 줄이 끼어든 것처럼 보임.
      orderUpdatesForPg = [];
      const shifted = prev.map(r => {
        if (r.date !== insertDate) return r;
        const ro = r.order !== undefined && r.order !== null ? Math.trunc(Number(r.order)) : 0;
        if (ro > baseOrder) {
          const newOrder = ro + n;
          if (!String(r.id || '').startsWith('new-')) {
            orderUpdatesForPg.push({ id: String(r.id), date: String(r.date), order: newOrder });
          }
          return { ...r, order: newOrder };
        }
        return r;
      });

      const idx = shifted.findIndex(r => r.id === contextRow.id);
      const newRows = makeNewRows();
      if (idx === -1) return [...shifted, ...newRows];
      const next = [...shifted];
      next.splice(idx + 1, 0, ...newRows);
      return next;
    });

    if (orderUpdatesForPg.length > 0) {
      queueMicrotask(() => {
        const batch = orderUpdatesForPg;
        void Promise.all(
          batch.map(u =>
            fetch('/api/settlement-reports/update', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: u.id,
                date: u.date,
                order: u.order,
                userId: auth.currentUser?.uid,
                userEmail: auth.currentUser?.email ?? undefined,
              }),
            }).then(res => {
              if (!res.ok) {
                console.warn('[행추가] order DB 반영 실패', u.id, res.status);
              }
            })
          )
        ).catch(e => console.warn('[행추가] order DB 반영 오류', e));
      });
    }
  }, [displayedRows, dateRange.start]);

  /** 우클릭 "행 삭제" 모달에서 최종 확인 시: deleteModalTargets에 담긴 행들을 일괄 삭제 */
  const handleDeleteConfirm = async () => {
    const targets = deleteModalTargets;
    if (!targets || targets.length === 0) {
      setShowDeleteModal(false);
      setDeleteModalStep(1);
      return;
    }
    setDeleteConfirmLoading(true);

    const existingTargets = targets.filter((t: any) =>
      t.id && t.date && !String(t.id).startsWith('new-')
    );
    const newTargets = targets.filter((t: any) =>
      t.id && String(t.id).startsWith('new-')
    );

    try {
      let deletedCount = 0;
      if (existingTargets.length > 0) {
        const resultIds = await Promise.all(
          existingTargets.map((t: any) =>
            fetch('/api/settlement-reports/row', {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ date: t.date, id: t.id, userId: auth.currentUser?.uid, userEmail: auth.currentUser?.email })
            }).then(res => (res.ok ? t.id : null))
              .catch(() => null)
          )
        );
        const successIds = resultIds.filter((id): id is string => !!id);
        const idsToRemove = new Set<string>([
          ...successIds,
          ...newTargets.map((t: any) => String(t.id))
        ]);
        setAllRows(prev => prev.filter(r => !idsToRemove.has(String(r.id))));
        deletedCount = successIds.length + newTargets.length;
        if (successIds.length < existingTargets.length) {
          alert('일부 행은 삭제에 실패했습니다.');
        }
      } else {
        const idsToRemove = new Set<string>(newTargets.map((t: any) => String(t.id)));
        setAllRows(prev => prev.filter(r => !idsToRemove.has(String(r.id))));
        deletedCount = newTargets.length;
      }
      if (deletedCount > 0) {
        alert(`총 ${deletedCount}개의 행이 삭제되었습니다.`);
      }
    } catch {
      alert('삭제 요청 중 오류가 발생했습니다.');
    } finally {
      setDeleteConfirmLoading(false);
      setShowDeleteModal(false);
      setDeleteModalTargets([]);
      setDeleteModalStep(1);
      selectedRowsRef.current = new Set();
      setSelectedRows(new Set());
    }
  };

  // 날짜 범위 선택 핸들러 (입력창 값 변경 시)
  const handlePendingDateChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'start' | 'end') => {
    if (!e.target.value) return;
    setPendingDateRange(prev => ({ ...prev, [type]: e.target.value }));
  };

  // 날짜 확정(조회) 버튼 클릭 시
  const handleDateConfirm = () => {
    setDateRange(pendingDateRange);
    fetchRows(pendingDateRange.start, pendingDateRange.end);
  };

  // 첫 진입 시만 자동 조회
  useEffect(() => {
    fetchRows(dateRange.start, dateRange.end);
  }, [fetchRows, dateRange]);

  // 필터 팝업 열기
  const openFilterPopup = (filterName: keyof typeof filterPopups) => {
    setFilterPopups(prev => ({ ...prev, [filterName]: true }));
    // 현재 필터 값을 임시 선택 상태로 복사
    setTempSelections(prev => ({ ...prev, [filterName]: [...filters[filterName]] }));
    // 검색어 초기화
    setFilterSearchTerms(prev => ({ ...prev, [filterName]: '' }));
  };

  // 필터 팝업 닫기
  const closeFilterPopup = (filterName: keyof typeof filterPopups) => {
    setFilterPopups(prev => ({ ...prev, [filterName]: false }));
    // 검색어 초기화
    setFilterSearchTerms(prev => ({ ...prev, [filterName]: '' }));
  };

  // 필터 적용 (확인 버튼 클릭 시)
  const applyFilter = (filterName: keyof typeof filterPopups) => {
    setFilters(prev => ({ ...prev, [filterName]: tempSelections[filterName] }));
    closeFilterPopup(filterName);
    
    // 연관 필터 초기화
    if (filterName === 'company') {
      setFilters(prev => ({ ...prev, itemName: [], cargo: [], department: [] }));
    } else if (filterName === 'itemName') {
      setFilters(prev => ({ ...prev, cargo: [], department: [] }));
    } else if (filterName === 'cargo') {
      setFilters(prev => ({ ...prev, department: [] }));
    }
  };

  // 필터 취소 (취소 버튼 클릭 시)
  const cancelFilter = (filterName: keyof typeof filterPopups) => {
    closeFilterPopup(filterName);
  };

  // 임시 선택 상태 변경
  const handleTempSelectionChange = (filterName: keyof typeof filterPopups, value: string) => {
    setTempSelections(prev => {
      const current = prev[filterName];
      if (current.includes(value)) {
        return { ...prev, [filterName]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [filterName]: [...current, value] };
      }
    });
  };

  // 전체 선택/해제
  const handleSelectAll = (filterName: keyof typeof filterPopups, allOptions: string[]) => {
    const current = tempSelections[filterName];
    if (current.length === allOptions.length) {
      setTempSelections(prev => ({ ...prev, [filterName]: [] }));
    } else {
      setTempSelections(prev => ({ ...prev, [filterName]: [...allOptions] }));
    }
  };

  // 필터 입력 핸들러 (텍스트 필터만 처리)
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // 동적 필터를 위한 연관 필터 초기화
    let newFilters = { ...filters, [name]: value };
    
    // 거래처가 변경되면 품명, 차종, 소속 초기화
    if (name === 'company') {
      newFilters = {
        ...newFilters,
        itemName: [],
        cargo: [],
        department: []
      };
    }
    
    // 품명이 변경되면 차종, 소속 초기화
    if (name === 'itemName') {
      newFilters = {
        ...newFilters,
        cargo: [],
        department: []
      };
    }
    
    setFilters(newFilters);
  };

  // 필터 초기화 핸들러 추가
  const handleResetFilters = () => {
    setFilters({
      company: [],
      date: [],
      vehicleNumber: '',
      route: '',
      itemName: [],
      cargo: [],
      department: [],
      settlementStatus: [],
      settlementDate: [], // 정산일 필터 추가
      remarks: ''
    });
    // 옵션이 비어 있으면 자동으로 불러오기
    if (itemNameOptions.length === 0 && filters.company.length > 0) loadItemNames(filters.company[0]);
  };

  // 정산상태·인수증 토글 (스페이스바 — beforeKeyDown에서만 호출)
  const handleCellClick = async (row: number, col: number) => {
    if (col === SETTLEMENT_STATUS_COL) {
      // 실제 표시되는 행의 데이터를 가져옴
      const displayedRow = displayedRows[row];
      if (!displayedRow) {
        console.error('클릭한 행의 데이터를 찾을 수 없습니다:', row);
        return;
      }

      // console.log('클릭된 행:', row);
      // console.log('sortedRows:', sortedRows[row]);
      // console.log('filteredRows:', filteredRows[row]);
      // console.log('allRows에서 해당 ID:', allRows.find(r => r.id === sortedRows[row]?.id));

      const rowId = displayedRow.id;
      const newValue = displayedRow.settlementStatus === '정산' ? '미정산' : '정산';
      const todayStr = getTodayLocal();
      const settlementDateColIdx = COLUMN_FIELDS.indexOf('settlementDate');
      
      // 상태 및 정산일 동시 업데이트 (정산 -> 오늘, 미정산 -> 공백)
      setAllRows(prev => prev.map(r => r.id === rowId ? { ...r, settlementStatus: newValue, settlementDate: newValue === '정산' ? todayStr : '' } : r));
      
      // UI 즉시 업데이트
      setTimeout(() => {
        if (hotRef.current) hotRef.current.hotInstance.render();
        // 정산으로 변경 시 정산일 셀에도 즉시 값 반영
        if (hotRef.current && settlementDateColIdx >= 0) {
          hotRef.current.hotInstance.setDataAtCell(row, settlementDateColIdx, newValue === '정산' ? todayStr : '');
          hotRef.current.hotInstance.render();
        }
      }, 10);

      // 저장 상태 표시
      setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'saving' } }));

      try {
        // API 호출
        await fetch('/api/settlement-reports/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: rowId,
            date: displayedRow.date,
            settlementStatus: newValue,
            settlementDate: newValue === '정산' ? todayStr : '',
            userId: auth.currentUser?.uid,
            userEmail: auth.currentUser?.email
          })
        });

        setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'success' } }));
        
        // 성공 메시지 제거
        setTimeout(() => {
          setSaveStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[rowId];
            return newStatus;
          });
        }, 2000);
      } catch (error) {
        console.error('정산상태 업데이트 실패:', error);
        setSaveStatus(prev => ({ 
          ...prev, 
          [rowId]: { status: 'error', message: '저장 실패' } 
        }));
      }
      return;
    }
    if (col === RECEIPT_COL) {
      // 실제 표시되는 행의 데이터를 가져옴
      const displayedRow = displayedRows[row];
      if (!displayedRow) {
        console.error('클릭한 행의 데이터를 찾을 수 없습니다:', row);
        return;
      }

      // console.log('클릭된 행:', row);
      // console.log('sortedRows:', sortedRows[row]);
      // console.log('filteredRows:', filteredRows[row]);
      // console.log('allRows에서 해당 ID:', allRows.find(r => r.id === sortedRows[row]?.id));

      const rowId = displayedRow.id;
      const newValue = displayedRow.Receipt === '수취' ? '미수취' : '수취';
      
      // 상태 업데이트
      setAllRows(prev => prev.map(r => r.id === rowId ? { ...r, Receipt: newValue } : r));
      
      // UI 즉시 업데이트
      setTimeout(() => {
        if (hotRef.current) hotRef.current.hotInstance.render();
      }, 10);

      // 저장 상태 표시
      setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'saving' } }));

      try {
        // API 호출
        await fetch('/api/settlement-reports/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: rowId,
            date: displayedRow.date,
            Receipt: newValue,
            userId: auth.currentUser?.uid,
            userEmail: auth.currentUser?.email
          })
        });

        setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'success' } }));
        
        // 성공 메시지 제거
        setTimeout(() => {
          setSaveStatus(prev => {
            const newStatus = { ...prev };
            delete newStatus[rowId];
            return newStatus;
          });
        }, 2000);
      } catch (error) {
        console.error('인수증 상태 업데이트 실패:', error);
        setSaveStatus(prev => ({ 
          ...prev, 
          [rowId]: { status: 'error', message: '저장 실패' } 
        }));
      }
      return;
    }
  };

  // 운송구간 운임 이력: 셀 클릭 시 사이드바 열기 (routeFreightHistoryMode ON일 때만 사용)
  const handleRouteCellClick = useCallback(async (row: number) => {
    const rowData = displayedRows[row];
    if (!rowData) return;
    const company = String(rowData.company ?? '').trim();
    const route = String(rowData.route ?? '').trim();
    const cargo = String(rowData.cargo ?? '').trim();
    if (!route) return;
    const routeKey = makeRouteKey(company, route, cargo);
    routeHistoryKeyRef.current = routeKey;
    setRouteHistoryOpen(true);
    setRouteHistoryHasMore(true);
    setRouteHistoryLoading(true);
    setRouteHistoryData({
      recentHistory: [],
      avg: null,
      mode: null,
      min: null,
      max: null,
      count: null,
      lastUsed: null,
    });
    try {
      const res = await fetch(`/api/settlement-reports/pg/route-freight-history/${encodeURIComponent(routeKey)}`);
      if (!res.ok) throw new Error('조회 실패');
      const data = await res.json();
      setRouteHistoryData(data);
      if (!data.recentHistory || data.recentHistory.length < 20) setRouteHistoryHasMore(false);
    } catch {
      setRouteHistoryData({
        recentHistory: [],
        avg: null,
        mode: null,
        min: null,
        max: null,
        count: null,
        lastUsed: null,
      });
    }
    setRouteHistoryLoading(false);
  }, [displayedRows]);

  const handleRouteHistoryLoadMore = useCallback(async () => {
    const key = routeHistoryKeyRef.current;
    if (!key) return;
    setRouteHistoryLoading(true);
    const currentHistory = routeHistoryData.recentHistory;
    const startAfter = currentHistory.length > 0
      ? `${currentHistory[currentHistory.length - 1].date}|${currentHistory[currentHistory.length - 1].id ?? ''}`
      : undefined;
    const url = `/api/settlement-reports/pg/route-freight-history-rows/${encodeURIComponent(key)}?limit=20${startAfter ? `&startAfter=${encodeURIComponent(startAfter)}` : ''}`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('더보기 실패');
      const rows = await res.json();
      if (!Array.isArray(rows) || rows.length < 20) setRouteHistoryHasMore(false);
      setRouteHistoryData(prev => ({
        ...prev,
        recentHistory: [...prev.recentHistory, ...rows],
      }));
    } catch {
      // ignore
    }
    setRouteHistoryLoading(false);
  }, [routeHistoryData.recentHistory]);

  // 차량 운임 이력: 차량번호 셀 클릭 시 사이드바 열기 (vehicleFreightHistoryMode ON일 때만 사용)
  const vehicleHistoryRouteRef = useRef<string>('');
  const handleVehicleCellClick2 = useCallback(async (row: number) => {
    const rowData = displayedRows[row];
    if (!rowData) return;
    const vehicleNumber = String(rowData.vehicleNumber ?? '').trim();
    if (!vehicleNumber) return;
    const route = String(rowData.route ?? '').trim();
    vehicleHistoryRouteRef.current = route;
    setVehicleHistoryVehicleNumber(vehicleNumber);
    setVehicleHistoryOpen(true);
    setVehicleHistoryHasMore(true);
    setVehicleHistoryLoading(true);
    setVehicleHistoryData({
      recentHistory: [],
      avg: null, mode: null, min: null, max: null, count: null, lastUsed: null,
    });
    try {
      const routeParam = route ? `?route=${encodeURIComponent(route)}` : '';
      const res = await fetch(`/api/settlement-reports/pg/vehicle-freight-history/${encodeURIComponent(vehicleNumber)}${routeParam}`);
      if (!res.ok) throw new Error('조회 실패');
      const data = await res.json();
      setVehicleHistoryData(data);
      if (!data.recentHistory || data.recentHistory.length < 20) setVehicleHistoryHasMore(false);
    } catch {
      setVehicleHistoryData({
        recentHistory: [],
        avg: null, mode: null, min: null, max: null, count: null, lastUsed: null,
      });
    }
    setVehicleHistoryLoading(false);
  }, [displayedRows]);

  const handleVehicleHistoryLoadMore = useCallback(async () => {
    if (!vehicleHistoryVehicleNumber) return;
    setVehicleHistoryLoading(true);
    const currentHistory = vehicleHistoryData.recentHistory;
    const startAfter = currentHistory.length > 0
      ? `${currentHistory[currentHistory.length - 1].date}|${currentHistory[currentHistory.length - 1].id ?? ''}`
      : undefined;
    const route = vehicleHistoryRouteRef.current;
    const qs = new URLSearchParams();
    qs.set('limit', '20');
    if (startAfter) qs.set('startAfter', startAfter);
    if (route) qs.set('route', route);
    const url = `/api/settlement-reports/pg/vehicle-freight-history-rows/${encodeURIComponent(vehicleHistoryVehicleNumber)}?${qs.toString()}`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error('더보기 실패');
      const rows = await res.json();
      if (!Array.isArray(rows) || rows.length < 20) setVehicleHistoryHasMore(false);
      setVehicleHistoryData(prev => ({
        ...prev,
        recentHistory: [...prev.recentHistory, ...rows],
      }));
    } catch {
      // ignore
    }
    setVehicleHistoryLoading(false);
  }, [vehicleHistoryVehicleNumber, vehicleHistoryData.recentHistory]);

  // 부분 업데이트 함수 추가 (타임스탬프 검증 포함)
  const updateSingleRow = useCallback((rowId: string, changedData: any, timestamp?: number) => {
    setAllRows(prev => {
      const newRows = [...prev];
      const rowIndex = newRows.findIndex(row => row.id === rowId);
      
      if (rowIndex !== -1) {
        const currentRow = newRows[rowIndex];
        
        // 타임스탬프 검증 (서버 데이터가 더 최신인 경우만 업데이트)
        if (timestamp && currentRow.updatedAt) {
          const serverTime = new Date(timestamp).getTime();
          const localTime = new Date(currentRow.updatedAt).getTime();
          
          if (localTime > serverTime) {
            console.log('로컬 데이터가 더 최신입니다. 업데이트 무시');
            return prev;
          }
        }
        
        // 변경된 필드만 업데이트
        newRows[rowIndex] = {
          ...currentRow,
          ...changedData,
          updatedAt: timestamp ? new Date(timestamp).toISOString() : currentRow.updatedAt
        };
      } else {
        // 행을 찾을 수 없으면 전체 재로드 및 선택 상태 초기화
        console.log('행을 찾을 수 없음. 전체 재로드 실행');
        selectedRowsRef.current = new Set();
        setSelectedRows(new Set()); // 선택 상태 초기화
        debouncedFetch();
        return prev;
      }
      
      return newRows;
    });
  }, [debouncedFetch]);

  // 안전한 부분 업데이트 여부 확인
  const isSafeForPartialUpdate = useCallback((data: any) => {
    return (
      data.rowId && 
      data.changedData &&
      Object.keys(data.changedData).length > 0 &&
      (!data.operationType || data.operationType === 'cell_edit')
    );
  }, []);

  // [비활성화] 실시간 자동 반영 중지 — 알림 방식으로 전환
  // 접속자/최근편집/변경건수 알림은 별도 useEffect에서 처리

  // allRows, filteredRows 상태 변화 추적
  useEffect(() => {
    // console.log('[상태] allRows', allRows);
  }, [allRows]);
  useEffect(() => {
    // console.log('[상태] filteredRows', filteredRows);
  }, [filteredRows]);

  // 셀 수정 핸들러
  const handleAfterChange = async (changes: any, source: string) => {
    if (!changes || source === 'loadData') return;

    const isBulk = source === 'CopyPaste.paste' || source === 'Autofill.fill';
    type NewRowBulkEntry = { baseRow: any; updates: Record<string, any> };
    const newRowBulkMap: Record<string, NewRowBulkEntry> = {};

    for (const [row, col, oldValue, newValue] of changes) {
      if (row >= tableData.length) continue;
      const sortedRow = displayedRows[row];
      if (!sortedRow || !sortedRow.id) continue;
      const field = COLUMN_FIELDS[col];
      let value = newValue;
      const rowId = sortedRow.id;
      const isNewRow = String(rowId).startsWith('new-');

      // 새 행 + 붙여넣기/자동채우기: 한 행당 1번만 INSERT 하기 위해 변경 내용을 모아 둠
      if (isNewRow && isBulk) {
        if (!sortedRow.date) continue;
        // 손익 열 붙여넣기는 무시 — INSERT 시 청구·지불로만 계산
        if (field === 'profit') continue;
        const num = (v: any) => (v === undefined || v === null || v === '') ? undefined : parseNumber(v);
        if (!newRowBulkMap[rowId]) {
          newRowBulkMap[rowId] = { baseRow: sortedRow, updates: {} };
        }
        newRowBulkMap[rowId].updates[field] =
          field === 'billingAmount' || field === 'paymentAmount' || field === 'distance' || field === 'weight'
            ? num(newValue)
            : newValue;
        continue;
      }

      // 새 행 + 단일 수정(타이핑): 기존 로직대로 바로 INSERT
      if (isNewRow) {
        if (!sortedRow.date) continue;
        // 손익은 직접 입력 불가 — 청구·지불 기준으로만 표시
        if (field === 'profit') {
          const profitIdx = COLUMN_FIELDS.indexOf('profit');
          const correct = settlementProfitFromRow(sortedRow);
          if (hotRef.current && profitIdx >= 0) {
            hotRef.current.hotInstance.setDataAtCell(row, profitIdx, correct, 'loadData');
          }
          continue;
        }
        setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'saving' } }));
        const num = (v: any) => (v === undefined || v === null || v === '') ? undefined : parseNumber(v);
        const payload: any = {
          ...sortedRow,
          [field]: field === 'billingAmount' || field === 'paymentAmount' || field === 'distance' || field === 'weight' ? num(newValue) : newValue,
          userId: auth.currentUser?.uid,
          userEmail: auth.currentUser?.email
        };
        {
          const nb =
            payload.billingAmount != null && payload.billingAmount !== ''
              ? parseNumber(payload.billingAmount)
              : 0;
          const np =
            payload.paymentAmount != null && payload.paymentAmount !== ''
              ? parseNumber(payload.paymentAmount)
              : 0;
          payload.profit = nb - np;
        }
        try {
          const res = await fetch('/api/settlement-reports/insert', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'error', message: data?.error || '저장 실패' } }));
            continue;
          }
          const savedId = data.row?.id;
          if (savedId) recentlyInsertedRowIds.current.add(savedId);
          const mergedRow = { ...payload, ...(data.row || {}), id: savedId ?? rowId };
          mergedRow.profit = settlementProfitFromRow(mergedRow);
          setAllRows(prev => prev.map(r => r.id === rowId ? mergedRow : r));
          setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'success' } }));
          setTimeout(() => setSaveStatus(prev => { const next = { ...prev }; delete next[rowId]; return next; }), 2000);
        } catch (e) {
          setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'error', message: '저장 실패' } }));
        }
        continue;
      }

      if (!sortedRow.date) continue;

      // 손익은 DB·화면 모두 청구−지불로만 유지 (직접 수정·붙여넣기 차단)
      if (field === 'profit') {
        const profitIdx = COLUMN_FIELDS.indexOf('profit');
        const correct = settlementProfitFromRow(sortedRow);
        setAllRows(prev => prev.map(r => (r.id === rowId ? { ...r, profit: correct } : r)));
        if (hotRef.current && profitIdx >= 0) {
          hotRef.current.hotInstance.setDataAtCell(row, profitIdx, correct, 'loadData');
        }
        continue;
      }

      // 정산상태가 '미정산'으로 바뀌면 정산일도 ''로
      if (field === 'settlementStatus' && newValue === '미정산') {
        setAllRows(prev => prev.map(r =>
          r.id === rowId ? { ...r, settlementStatus: newValue, settlementDate: '' } : r
        ));
        // UI도 즉시 비움
        const settlementDateColIdx = COLUMN_FIELDS.indexOf('settlementDate');
        if (hotRef.current) {
          hotRef.current.hotInstance.setDataAtCell(row, settlementDateColIdx, '');
          hotRef.current.hotInstance.render();
        }
        // 서버에도 반영
        await fetch('/api/settlement-reports/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: sortedRow.id,
            date: sortedRow.date,
            settlementStatus: newValue,
            settlementDate: '',
            userId: auth.currentUser?.uid,
            userEmail: auth.currentUser?.email
          })
        });
        continue;
      }
      setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'saving' } }));
      if (field === 'billingAmount' || field === 'paymentAmount' || field === 'distance' || field === 'weight') {
        value = (newValue === '' || newValue === null || newValue === undefined) ? null : parseNumber(newValue);
      }
      if (field === 'date') {
        value = normalizeSettlementDateCell(newValue);
      }

      // 청구운송비가 변경되면 손익 자동 계산
      if (field === 'billingAmount') {
        const paymentAmount = sortedRow.paymentAmount || 0;
        const newBillingAmount = value || 0;
        const newProfit = newBillingAmount - paymentAmount;

        setAllRows(prev =>
          prev.map(r => (r.id === rowId ? { ...r, [field]: value, profit: newProfit } : r))
        );

        const profitColIdx = COLUMN_FIELDS.indexOf('profit');
        if (hotRef.current && profitColIdx >= 0) {
          hotRef.current.hotInstance.setDataAtCell(row, profitColIdx, newProfit);
          hotRef.current.hotInstance.render();
        }

        try {
          const res = await fetch('/api/settlement-reports/update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: sortedRow.id,
              date: sortedRow.date,
              billingAmount: value,
              profit: newProfit,
              userId: auth.currentUser?.uid,
              userEmail: auth.currentUser?.email
            })
          });
          if (!res.ok) {
            throw new Error('저장 실패');
          }
          setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'success' } }));
          setTimeout(() => {
            setSaveStatus(prev => {
              const newStatus = { ...prev };
              delete newStatus[rowId];
              return newStatus;
            });
          }, 2000);
        } catch (error) {
          console.error('청구운송비 업데이트 실패:', error);
          setSaveStatus(prev => ({
            ...prev,
            [rowId]: { status: 'error', message: '저장 실패' }
          }));
          if (hotRef.current) {
            hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
            hotRef.current.hotInstance.render();
          }
        }
        continue;
      }

      // 지불운송비가 변경되면 손익 자동 계산
      if (field === 'paymentAmount') {
        const billingAmount = sortedRow.billingAmount || 0;
        const newPaymentAmount = value || 0;
        const newProfit = billingAmount - newPaymentAmount;
        
        // 손익 컬럼 업데이트
        setAllRows(prev => prev.map(r => (r.id === rowId ? { ...r, [field]: value, profit: newProfit } : r)));
        
        // UI 즉시 업데이트
        const profitColIdx = COLUMN_FIELDS.indexOf('profit');
        if (hotRef.current && profitColIdx >= 0) {
          hotRef.current.hotInstance.setDataAtCell(row, profitColIdx, newProfit);
          hotRef.current.hotInstance.render();
        }
        
        // 서버에 지불운송비와 손익 모두 업데이트
        try {
          const res = await fetch('/api/settlement-reports/update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: sortedRow.id,
              date: sortedRow.date,
              paymentAmount: value,
              profit: newProfit,
              userId: auth.currentUser?.uid,
              userEmail: auth.currentUser?.email
            })
          });
          if (!res.ok) {
            throw new Error('저장 실패');
          }
          setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'success' } }));
          setTimeout(() => {
            setSaveStatus(prev => {
              const newStatus = { ...prev };
              delete newStatus[rowId];
              return newStatus;
            });
          }, 2000);
        } catch (error) {
          console.error('지불운송비 업데이트 실패:', error);
          setSaveStatus(prev => ({ 
            ...prev, 
            [rowId]: { status: 'error', message: '저장 실패' } 
          }));
          // 실패 시 원래 값으로 복원
          if (hotRef.current) {
            hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
            hotRef.current.hotInstance.render();
          }
        }
        continue; // 기존 로직 건너뛰기
      }
      setAllRows(prev => prev.map(r => (r.id === rowId ? { ...r, [field]: value } : r)));
      setTimeout(() => {
        if (hotRef.current) hotRef.current.hotInstance.render();
      }, 10);
      try {
        const updateBody: Record<string, unknown> = {
          id: sortedRow.id,
          date: sortedRow.date,
          userId: auth.currentUser?.uid,
          userEmail: auth.currentUser?.email
        };
        if (field === 'date') {
          updateBody.newDate = value;
        } else {
          updateBody[field] = value;
        }
        const res = await fetch('/api/settlement-reports/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateBody)
        });
        if (!res.ok) {
          if (res.status === 404) {
            const err = await res.json();
            alert(err.error || '해당 행이 존재하지 않습니다.');
            setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'error', message: err.error || '해당 행이 존재하지 않습니다.' } }));
            if (hotRef.current) {
              hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
              hotRef.current.hotInstance.render();
            }
            continue;
          }
          setSaveStatus(prev => ({
            ...prev,
            [rowId]: { status: 'error', message: '저장 실패: 네트워크 또는 서버 오류' }
          }));
          if (hotRef.current) {
            hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
            hotRef.current.hotInstance.render();
          }
        } else {
          setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'success' } }));
          setTimeout(() => {
            setSaveStatus(prev => {
              const newStatus = { ...prev };
              delete newStatus[rowId];
              return newStatus;
            });
          }, 2000);
        }
      } catch (error) {
        setSaveStatus(prev => ({
          ...prev,
          [rowId]: { status: 'error', message: '저장 실패: 네트워크 오류' }
        }));
        if (hotRef.current) {
          hotRef.current.hotInstance.setDataAtCell(row, col, oldValue);
          hotRef.current.hotInstance.render();
        }
      }
    }

    // 새 행에 대한 붙여넣기/자동채우기 변경들 처리: 행마다 1번씩 INSERT
    const newRowIds = Object.keys(newRowBulkMap);
    for (const rowId of newRowIds) {
      const entry = newRowBulkMap[rowId];
      const baseRow = entry.baseRow;
      if (!baseRow?.date) continue;
      setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'saving' } }));
      const payload: any = {
        ...baseRow,
        ...entry.updates,
        userId: auth.currentUser?.uid,
        userEmail: auth.currentUser?.email
      };
      {
        const nb =
          payload.billingAmount != null && payload.billingAmount !== ''
            ? parseNumber(payload.billingAmount)
            : 0;
        const np =
          payload.paymentAmount != null && payload.paymentAmount !== ''
            ? parseNumber(payload.paymentAmount)
            : 0;
        payload.profit = nb - np;
      }
      try {
        const res = await fetch('/api/settlement-reports/insert', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'error', message: data?.error || '저장 실패' } }));
          continue;
        }
        const savedId = data.row?.id;
        if (savedId) recentlyInsertedRowIds.current.add(savedId);
        const mergedRow = { ...payload, ...(data.row || {}), id: savedId ?? rowId };
        mergedRow.profit = settlementProfitFromRow(mergedRow);
        setAllRows(prev => prev.map(r => r.id === rowId ? mergedRow : r));
        setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'success' } }));
        setTimeout(() => setSaveStatus(prev => { const next = { ...prev }; delete next[rowId]; return next; }), 2000);
      } catch (e) {
        setSaveStatus(prev => ({ ...prev, [rowId]: { status: 'error', message: '저장 실패' } }));
      }
    }
  };

  // cells 함수에서 인수증 관련 readOnly/자동 미수취 로직 제거
  const cells = (row: number, col: number) => {
    const realRow = displayedRows[row];
    if (!realRow) return {};
    const cellProps: any = {};
    if (settlementViewMode === 'read') {
      cellProps.readOnly = true;
    }
    const settlementStatusColIdx = COLUMN_FIELDS.indexOf('settlementStatus');
    const receiptColIdx = COLUMN_FIELDS.indexOf('Receipt');
    const settlementDateColIdx = COLUMN_FIELDS.indexOf('settlementDate');
    const billingAmountColIdx = COLUMN_FIELDS.indexOf('billingAmount');
    const paymentAmountColIdx = COLUMN_FIELDS.indexOf('paymentAmount');
    const profitColIdx = COLUMN_FIELDS.indexOf('profit');

    // 손익: 항상 청구−지불 자동값만 (입력 모드에서도 편집 불가)
    if (col === profitColIdx) {
      cellProps.readOnly = true;
    }

    // 정산상태·인수증: 직접 타이핑 불가(편집기 미오픈), 스페이스로만 토글
    if (col === settlementStatusColIdx || col === receiptColIdx) {
      cellProps.readOnly = true;
    }

    // 정산일: 미정산 행이면 편집 불가(값 자동 정리)
    if (realRow.settlementStatus === '미정산') {
      if (col === settlementDateColIdx) {
        cellProps.readOnly = true;
        if (realRow.settlementDate) {
          setTimeout(() => {
            if (hotRef.current) {
              hotRef.current.hotInstance.setDataAtCell(row, col, '');
              hotRef.current.hotInstance.render();
            }
          }, 0);
        }
        return cellProps;
      }
    }

    // 선택된 행 스타일 적용 (ref 사용 → 선택 시 React 리렌더 없이 hot.render()만 호출)
    if (selectedRowsRef.current.has(row)) {
      cellProps.className = (cellProps.className || '') + ' selected-row';
    }

    // 청구·지불·손익 스타일 적용
    if (col === billingAmountColIdx) {
      cellProps.className = (cellProps.className || '') + ' htCenter htMiddle ellipsis-cell billing-amount';
    } else if (col === paymentAmountColIdx) {
      cellProps.className = (cellProps.className || '') + ' htCenter htMiddle ellipsis-cell payment-amount';
    } else if (col === profitColIdx) {
      const pv = settlementProfitFromRow(realRow);
      const profitTone = pv < 0 ? 'profit-amount-negative' : 'profit-amount-positive';
      cellProps.className =
        (cellProps.className || '') + ` htCenter htMiddle ellipsis-cell ${profitTone}`;
    } else {
      cellProps.className = (cellProps.className || '') + ' htCenter htMiddle ellipsis-cell';
    }

    return cellProps;
  };

  // 저장 상태별 스타일을 <style> 태그로 추가
  useEffect(() => {
    if (!document.getElementById('save-status-styles')) {
      const style = document.createElement('style');
      style.id = 'save-status-styles';
      style.innerHTML = `
        .saving-row, td.saving-row, .handsontable .saving-row { background: #fff3e0 !important; }
        .success-row, td.success-row, .handsontable .success-row { background: #e8f5e9 !important; }
        .error-row, td.error-row, .handsontable .error-row { background: #ffebee !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // 컴포넌트 최상단 useEffect에 글로벌 CSS 추가
  useEffect(() => {
    if (!document.getElementById('ellipsis-cell-style')) {
      const style = document.createElement('style');
      style.id = 'ellipsis-cell-style';
      style.innerHTML = `
        .ellipsis-cell {
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          max-width: 120px !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (!document.getElementById('gray-bg-style')) {
      const style = document.createElement('style');
      style.id = 'gray-bg-style';
      style.innerHTML = `
        .gray-background-cell {
          background-color: #e0e0e0 !important;
          font-weight: 700 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    // 통합일보 등 다른 페이지와 style id 충돌을 피하려고 고유 id 사용
    const STYLE_ID = 'settlement-highlight-row-style';
    let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }
    style.innerHTML = `
      .highlight-row {
        background: #fffde7 !important;
        transition: background 0.7s;
        animation: highlight-fade 5s;
      }
      @keyframes highlight-fade {
        0% { background: #ffe082; }
        100% { background: #fffde7; }
      }
      .selected-row {
        background: #e3f2fd !important;
        border: 2px solid #1976d2 !important;
      }
      .selected-row td {
        background: #e3f2fd !important;
      }
    `;
  }, []);

  // order 중간값 계산 함수
  function getInsertOrder(date: string, baseOrder: number, direction: 'above' | 'below') {
    const dateRows = allRows.filter(r => r.date === date).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = dateRows.findIndex(r => r.order === baseOrder);
    if (idx === -1) return Date.now();
    if (direction === 'above') {
      const aboveOrder = idx > 0 ? dateRows[idx - 1].order : undefined;
      if (aboveOrder !== undefined) {
        return (aboveOrder + baseOrder) / 2;
      } else {
        return baseOrder - 1000;
      }
    } else {
      const belowOrder = idx < dateRows.length - 1 ? dateRows[idx + 1].order : undefined;
      if (belowOrder !== undefined) {
        return (baseOrder + belowOrder) / 2;
      } else {
        return baseOrder + 1000;
      }
    }
  }

  // 거래처명 비교를 위한 정규화 함수
  function normalizeCompanyName(name: string) {
    if (!name) return '';
    return name
      .replace(/\(.*?\)/g, '') // 괄호 및 괄호 안 내용 제거
      .replace(/(주식회사|유한회사|유한|주|유|합|재|사)/gi, '') // 법인 구분어 제거
      .replace(/\s+/g, '') // 모든 공백 제거
      .toLowerCase()
      .trim();
  }

  // 명세서 버튼 클릭 핸들러
  const handleStatementClick = () => {
    setIsStatementSidebarOpen(true);
    // 사이드바가 숨겨져 있다면 펼치기
    if (setStatementSidebarCollapsed && isStatementSidebarCollapsed) {
      setStatementSidebarCollapsed(false);
    }
    setSelectedCompanies([]);
    setSelectedTemplateType(''); // 초기화
    setStatementDateRange({ ...dateRange }); // 최신 날짜 범위로 동기화
  };

  const AUDIT_LOG_PAGE_SIZE = 100;

  // 변경 이력(감사 로그) 버튼 클릭: 최대 100개 먼저 로드, 더보기로 추가 로드
  const handleAuditLogClick = async () => {
    setAuditLogOpen(true);
    setAuditLogLoading(true);
    setAuditLogEntries([]);
    setAuditLogHasMore(false);
    auditLogQueryRef.current = null;
    try {
      const selectedIndices = Array.from(selectedRows);
      if (selectedIndices.length === 1) {
        const row = displayedRows[selectedIndices[0]];
        if (row?.date && row?.id && !String(row.id).startsWith('new-')) {
          auditLogQueryRef.current = { type: 'row', date: row.date, rowId: row.id };
          setAuditLogTitle(`변경 이력 · ${row.date} ${row.company || ''} ${row.route || row.id}`);
          const url = `/api/settlement-reports/pg/audit-log?date=${encodeURIComponent(row.date)}&rowId=${encodeURIComponent(row.id)}&limit=${AUDIT_LOG_PAGE_SIZE}&offset=0`;
          const res = await fetch(url);
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          setAuditLogEntries(list);
          setAuditLogHasMore(list.length === AUDIT_LOG_PAGE_SIZE);
          setAuditLogLoading(false);
          return;
        }
      }
      auditLogQueryRef.current = { type: 'all' };
      setAuditLogTitle('변경 이력 · 전체 (최신순)');
      const url = `/api/settlement-reports/pg/audit-log?scope=all&limit=${AUDIT_LOG_PAGE_SIZE}&offset=0`;
      const res = await fetch(url);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setAuditLogEntries(list);
      setAuditLogHasMore(list.length === AUDIT_LOG_PAGE_SIZE);
    } catch {
      setAuditLogEntries([]);
    } finally {
      setAuditLogLoading(false);
    }
  };

  const handleAuditLogLoadMore = async () => {
    const q = auditLogQueryRef.current;
    if (!q || auditLogLoadingMore || auditLogEntries.length === 0) return;
    setAuditLogLoadingMore(true);
    try {
      let url: string;
      if (q.type === 'row') {
        url = `/api/settlement-reports/pg/audit-log?date=${encodeURIComponent(q.date)}&rowId=${encodeURIComponent(q.rowId)}&limit=${AUDIT_LOG_PAGE_SIZE}&offset=${auditLogEntries.length}`;
      } else {
        url = `/api/settlement-reports/pg/audit-log?scope=all&limit=${AUDIT_LOG_PAGE_SIZE}&offset=${auditLogEntries.length}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setAuditLogEntries(prev => [...prev, ...list]);
      setAuditLogHasMore(list.length === AUDIT_LOG_PAGE_SIZE);
    } finally {
      setAuditLogLoadingMore(false);
    }
  };

  const handleRestoreModalOpen = async () => {
    setRestoreModalOpen(true);
    setBackupListLoading(true);
    setBackupList([]);
    setSelectedBackup(null);
    setRestoreError(null);
    setRestoreSecret('');
    try {
      const res = await fetch('/api/settlement-reports/pg/backup-list', {
        headers: await settlementBackupAuthHeaders(false),
      });
      const data = await res.json();
      if (!res.ok) {
        setRestoreError(data?.error || '백업 목록을 불러올 수 없습니다.');
        setBackupList([]);
        return;
      }
      setBackupList(Array.isArray(data?.backups) ? data.backups : []);
    } catch (e: any) {
      setRestoreError(e?.message || '백업 목록을 불러올 수 없습니다.');
      setBackupList([]);
    } finally {
      setBackupListLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedBackup) {
      setRestoreError('복구할 백업 날짜를 선택하세요.');
      return;
    }
    setRestoreLoading(true);
    setRestoreError(null);
    try {
      const res = await fetch('/api/settlement-reports/pg/restore', {
        method: 'POST',
        headers: await settlementBackupAuthHeaders(true),
        body: JSON.stringify({ filename: selectedBackup, restoreSecret: restoreSecret || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        setRestoreError(data?.error || '복구에 실패했습니다.');
        return;
      }
      setRestoreModalOpen(false);
      setSelectedBackup(null);
      setRestoreSecret('');
      fetchRows(dateRange.start, dateRange.end);
    } catch (e: any) {
      setRestoreError(e?.message || '복구 요청 중 오류가 발생했습니다.');
    } finally {
      setRestoreLoading(false);
    }
  };

  // 명세서 양식 선택 핸들러
  const handleTemplateTypeChange = (type: 'A' | 'B' | 'C' | 'D' | 'ramen' | '') => {
    const prevType = selectedTemplateType;
    setSelectedTemplateType(type);

    // 기사명세서 ↔ 기사계산서 전환 시 차량 목록 초기화
    if ((prevType === 'C' && type === 'D') || (prevType === 'D' && type === 'C')) {
      setDriverVehicleList([]);
      setDriverVehicleInput('');
      setDriverVehicleSuggestions([]);
      setShowDriverVehicleSuggestions(false);
    }

    // 선택된 거래처와 품명 초기화
    setSelectedCompanies([]);
    setSelectedItemNames([]);
    setItemNameOptions([]);

    if (type === 'A' || type === 'B') {
      setCompanyOptions([]);
      setCompanyLoading(true);
      setTimeout(() => {
        loadItemCompanyOptions(type as 'A' | 'B');
      }, 50);
    } else {
      setCompanyOptions([]);
      setCompanyLoading(false);
    }
  };

  // 화주품명명세서 거래처 목록 fetch (품명 존재 거래처만)
  const loadItemCompanyOptions = async (templateType?: 'A' | 'B') => {
    try {
      // 현재 선택된 타입 또는 전달받은 타입 사용
      const currentType = templateType || selectedTemplateType;
      
      const res = await fetch(`/api/settlement-reports/companies?start=${statementDateRange.start}&end=${statementDateRange.end}`);
      let companies = await res.json();
      const rowsRes = await fetch(`/api/settlement-reports/pg?start=${statementDateRange.start}&end=${statementDateRange.end}`);
      const allRowsFromPg = await rowsRes.json();
      
      if (currentType === 'B') {
        // 품명이 존재하는 거래처상호만 남김 (빈 문자열/공백 제외)
        companies = companies.filter((company: string) => {
          return allRowsFromPg.some(
            (row: any) => row.company === company && row.itemName && String(row.itemName).trim() !== ''
          );
        });
      } else if (currentType === 'A') {
        // vehicleNumber, route가 하나라도 있는 거래처만 남김 (화주명세서)
        companies = companies.filter((company: string) => {
          return allRowsFromPg.some((row: any) => row.company === company && (row.vehicleNumber || row.route));
        });
      }
      // 현재 표에 있는 거래처(방금 추가·미저장 포함)를 기간 내에서 병합 — 명세서에 바로 노출
      const sStart = statementDateRange.start;
      const sEnd = statementDateRange.end;
      const fromTable = new Set<string>();
      allRows.forEach((r: any) => {
        const d = r.date ? String(r.date).slice(0, 10) : '';
        const c = r.company ? String(r.company).trim() : '';
        if (!c || d < sStart || d > sEnd) return;
        // 화주품명명세서(B)일 때는 품명이 있는 거래처만 목록에 포함
        if (currentType === 'B') {
          const hasItemName = r.itemName != null && String(r.itemName).trim() !== '';
          if (!hasItemName) return;
        }
        fromTable.add(c);
      });
      companies = Array.from(new Set([...companies, ...fromTable])).sort();
      setCompanyOptions(companies);
    } catch (error) {
      console.error('거래처 목록 로드 중 오류:', error);
    } finally {
      setCompanyLoading(false);
    }
  };

  // 거래처 단일 선택 시 품명 목록 fetch
  const loadItemNames = async (company: string) => {
    setSelectedItemNames([]);
    setItemNameOptions([]); // 초기화
    if (!company) return;
    try {
      const res = await fetch(`/api/settlement-reports/pg/itemNames?company=${encodeURIComponent(company)}&start=${statementDateRange.start}&end=${statementDateRange.end}`);
      const itemNames = await res.json();
      setItemNameOptions(itemNames);
    } catch (error) {
      console.error('품명 목록 로드 중 오류:', error);
    }
  };

  // 거래처 단일 선택 핸들러 (B)
  const handleBCompanySelect = (company: string) => {
    setSelectedCompanies([company]);
    loadItemNames(company);
  };

  // 품명 체크박스 핸들러
  const handleItemNameCheck = (itemName: string) => {
    setSelectedItemNames(prev =>
      prev.includes(itemName)
        ? prev.filter(n => n !== itemName)
        : [...prev, itemName]
    );
  };

  // 화주품명명세서 선택 시 거래처 목록 fetch
  useEffect(() => {
    if (isStatementSidebarOpen && (selectedTemplateType === 'A' || selectedTemplateType === 'B')) {
      // 로딩 상태가 이미 true인 경우에만 실행 (날짜 변경 시)
      if (companyLoading) {
        loadItemCompanyOptions(selectedTemplateType as 'A' | 'B');
      } else {
        // 타입 변경 시에는 로딩 상태 설정 후 실행
        setCompanyLoading(true);
        setTimeout(() => {
          loadItemCompanyOptions(selectedTemplateType as 'A' | 'B');
        }, 50);
      }
    }
    // eslint-disable-next-line
  }, [isStatementSidebarOpen, selectedTemplateType, statementDateRange.start, statementDateRange.end]);



  // 기사명세서 차량번호 입력 상태 및 자동완성(부분 검색)용 상태
  const driverVehicleInputWrapperRef = useRef<HTMLDivElement | null>(null);
  const [driverVehicleSuggestions, setDriverVehicleSuggestions] = useState<string[]>([]);
  const [showDriverVehicleSuggestions, setShowDriverVehicleSuggestions] = useState(false);
  const driverVehicleSuggestionsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 기사명세서 차량번호 입력 이벤트 핸들러
  const handleDriverVehicleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDriverVehicleInput(e.target.value);
  };
  const handleDriverVehicleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      const values = driverVehicleInput
        .split(/,|\n|\r|\t/)
        .map(v => v.trim())
        .filter(v => v && !driverVehicleList.includes(v));
      if (values.length > 0) {
        setDriverVehicleList([...driverVehicleList, ...values]);
        setDriverVehicleInput('');
      }
    }
  };
  const handleRemoveDriverVehicle = (vehicle: string) => {
    setDriverVehicleList(driverVehicleList.filter(v => v !== vehicle));
  };

  // 기사명세서 차량번호 등록 로딩 상태
  const [vehicleRegistering, setVehicleRegistering] = useState(false);

  // 기사명세서 차량번호 자동완성: settlement_rows 기반 부분 검색
  useEffect(() => {
    const q = driverVehicleInput.trim();
    if (driverVehicleSuggestionsTimeoutRef.current) {
      clearTimeout(driverVehicleSuggestionsTimeoutRef.current);
    }
    if (!q) {
      setDriverVehicleSuggestions([]);
      setShowDriverVehicleSuggestions(false);
      return;
    }
    driverVehicleSuggestionsTimeoutRef.current = setTimeout(async () => {
      try {
        // 명세서 패널에서 선택한 기간 내에 실제 내역이 있는 차량만 제안되도록 start/end 전달
        const periodStart = startDateRef.current?.value || statementDateRange.start;
        const periodEnd = endDateRef.current?.value || statementDateRange.end;
        const params = new URLSearchParams({
          q,
          start: periodStart,
          end: periodEnd,
        });
        const res = await fetch(`/api/settlement-reports/pg/vehicle-suggestions?${params.toString()}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setDriverVehicleSuggestions(list);
        setShowDriverVehicleSuggestions(list.length > 0);
      } catch {
        setDriverVehicleSuggestions([]);
        setShowDriverVehicleSuggestions(false);
      }
    }, 300);
    return () => {
      if (driverVehicleSuggestionsTimeoutRef.current) {
        clearTimeout(driverVehicleSuggestionsTimeoutRef.current);
      }
    };
  }, [driverVehicleInput]);

  // 기사명세서 차량번호 자동완성: 인풋 바깥 클릭 시 제안 숨김
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (driverVehicleInputWrapperRef.current && !driverVehicleInputWrapperRef.current.contains(e.target as Node)) {
        setShowDriverVehicleSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 기사명세서: 제안 목록에서 차량번호 선택 시 입력 및 리스트 추가
  const handleSelectDriverVehicleSuggestion = (num: string) => {
    // 이미 리스트에 있으면 입력만 바꿔주고 닫기
    if (!driverVehicleList.includes(num)) {
      setDriverVehicleList(prev => [...prev, num]);
    }
    setDriverVehicleInput('');
    setDriverVehicleSuggestions([]);
    setShowDriverVehicleSuggestions(false);
  };

  // 기사명세서 차량번호 등록 버튼 핸들러 리팩토링
  const handleAddDriverVehicle = async () => {
    if (vehicleRegistering) return;
    setVehicleRegistering(true);
    const start = startDateRef.current?.value || statementDateRange.start;
    const end = endDateRef.current?.value || statementDateRange.end;
    const vehicles = driverVehicleInput
      .split(/,|\n|\r|\t/)
      .map(v => v.trim())
      .filter(v => v && !driverVehicleList.includes(v));
    if (vehicles.length === 0) {
      setVehicleRegistering(false);
      return;
    }
    // DB에서 실제 내역 조회
    const params = new URLSearchParams({ start, end, vehicles: vehicles.join(',') });
    try {
      const res = await fetch(`/api/settlement-reports/pg/driver?${params.toString()}`);
      const data = await res.json(); // { [차량번호]: [row, ...] }
      let added = false;
      vehicles.forEach(vehicle => {
        if (data[vehicle] && data[vehicle].length > 0) {
          setDriverVehicleList(prev => [...prev, vehicle]);
          added = true;
        } else {
          alert(`${vehicle}의 내역이 존재하지 않습니다.`);
        }
      });
      if (added) setDriverVehicleInput('');
    } catch (error) {
      alert('차량번호 내역 조회 중 오류가 발생했습니다.');
    }
    setVehicleRegistering(false);
  };

  const billingSum = displayedRows.reduce((sum, r) => sum + safeAmountToNumber(r.billingAmount), 0);
  const paymentSum = displayedRows.reduce((sum, r) => sum + safeAmountToNumber(r.paymentAmount), 0);
  const profitSum = billingSum - paymentSum;
  const dataCount = displayedRows.length;
  
  // 선택된 행들의 합계 계산 - tableData 사용 (유효성 검증 포함)
  const selectedBillingSum = Array.from(selectedRows).reduce((sum, rowIndex) => {
    // 유효한 인덱스인지 확인
    if (rowIndex < 0 || rowIndex >= tableData.length) {
      return sum;
    }
    const tableRow = tableData[rowIndex];
    const billingAmountColIdx = COLUMN_FIELDS.indexOf('billingAmount');
    if (tableRow && billingAmountColIdx >= 0) {
      const amount = tableRow[billingAmountColIdx];
           // console.log(`선택된 행 ${rowIndex} (tableData):`, amount);
      return sum + safeAmountToNumber(amount);
    }
    return sum;
  }, 0);
  const selectedPaymentSum = Array.from(selectedRows).reduce((sum, rowIndex) => {
    // 유효한 인덱스인지 check
    if (rowIndex < 0 || rowIndex >= tableData.length) {
      return sum;
    }
    const tableRow = tableData[rowIndex];
    const paymentAmountColIdx = COLUMN_FIELDS.indexOf('paymentAmount');
    if (tableRow && paymentAmountColIdx >= 0) {
      const amount = tableRow[paymentAmountColIdx];
      return sum + safeAmountToNumber(amount);
    }
    return sum;
  }, 0);
  const selectedDataCount = selectedRows.size; // 선택된 행 개수
  
  // 선택된 행들의 손익 계산
  const selectedProfitSum = selectedBillingSum - selectedPaymentSum; // 선택된 행들의 손익

  // 통합일보와 동일하게 Handsontable 렌더러 등록
  try {
    (Handsontable as any).renderers.registerRenderer('RedTextRenderer', function(
      instance, td, row, col, prop, value, cellProperties
    ) {
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      td.style.color = '#d32f2f';
      td.style.fontWeight = '700';
      td.style.fontSize = '15px';
      td.style.textAlign = 'center';
    });
  } catch (e) {}
  try {
    (Handsontable as any).renderers.registerRenderer('BlueTextRenderer', function(
      instance, td, row, col, prop, value, cellProperties
    ) {
      Handsontable.renderers.TextRenderer(instance, td, row, col, prop, value, cellProperties);
      td.style.color = '#1976d2';
      td.style.fontWeight = '700';
      td.style.fontSize = '15px';
      td.style.textAlign = 'center';
    });
  } catch (e) {}

  // 상태 변화 후 항상 강제 렌더링
  useEffect(() => {
    if (hotRef.current) hotRef.current.hotInstance.render();
  }, [allRows, tableData, sortedRows, selectedRows]);

  // 선택된 행의 유효성 검증 (데이터가 변경될 때마다)
  useEffect(() => {
    if (selectedRows.size > 0) {
      const validSelectedRows = new Set<number>();
      for (const rowIndex of selectedRows) {
        // 유효한 인덱스인지 확인
        if (rowIndex >= 0 && rowIndex < tableData.length) {
          validSelectedRows.add(rowIndex);
        }
      }
      
      // 유효하지 않은 선택이 있으면 제거
      if (validSelectedRows.size !== selectedRows.size) {
        console.log('유효하지 않은 선택된 행 제거:', selectedRows.size - validSelectedRows.size, '개');
        selectedRowsRef.current = validSelectedRows;
        setSelectedRows(validSelectedRows);
        if (hotRef.current?.hotInstance) hotRef.current.hotInstance.render();
      }
    }
  }, [tableData, selectedRows]);

  // 날짜 input ref 추가
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  // 거래처상호, 품명, 차종 옵션 추출 (중복 선언 제거)
  // companyOptions는 이제 loadItemCompanyOptions에서 관리됨
  const companyFilterOptions = Array.from(new Set(allRows.map(row => row.company).filter(Boolean)));
  
  // 동적 필터 옵션 생성
  const getDynamicItemNameOptions = () => {
    let filteredRows = sortedRows; // allRows 대신 sortedRows 사용
    
    // 거래처 필터가 적용된 경우 (배열 타입에 맞게 수정)
    if (filters.company.length > 0) {
      filteredRows = filteredRows.filter(row => 
        filters.company.some(company => row.company?.includes(company))
      );
    }
    
    const itemNames = filteredRows.map(row => row.itemName || '').filter(itemName => itemName !== '' && itemName.trim() !== '');
    const uniqueItemNames = Array.from(new Set(itemNames));
    
    // 품명이 없는 데이터가 있는지 확인 (공백 문자열 포함)
    const hasEmptyItemName = filteredRows.some(row => {
      const itemName = row.itemName;
      return !itemName || itemName === '' || itemName === null || itemName === undefined || itemName.trim() === '';
    });
    
    return hasEmptyItemName ? [...uniqueItemNames, '품명 없음'] : uniqueItemNames;
  };
  
  const getDynamicCargoOptions = () => {
    let filteredRows = sortedRows; // allRows 대신 sortedRows 사용
    
    // 거래처 필터가 적용된 경우 (배열 타입에 맞게 수정)
    if (filters.company.length > 0) {
      filteredRows = filteredRows.filter(row => 
        filters.company.some(company => row.company?.includes(company))
      );
    }
    
    // 품명 필터가 적용된 경우 (배열 타입에 맞게 수정)
    if (filters.itemName.length > 0) {
      filteredRows = filteredRows.filter(row => {
        if (filters.itemName.includes('품명 없음')) {
          const itemName = row.itemName;
          return !itemName || itemName === '' || itemName === null || itemName === undefined || itemName.trim() === '';
        }
        return filters.itemName.some(itemName => row.itemName?.includes(itemName));
      });
    }
    
    const cargoTypes = filteredRows.map(row => row.cargo || '').filter(cargo => cargo !== '' && cargo.trim() !== '');
    const uniqueCargoTypes = Array.from(new Set(cargoTypes));
    
    // 차종이 없는 데이터가 있는지 확인 (공백 문자열 포함)
    const hasEmptyCargo = filteredRows.some(row => {
      const cargo = row.cargo;
      return !cargo || cargo === '' || cargo === null || cargo === undefined || cargo.trim() === '';
    });
    
    return hasEmptyCargo ? [...uniqueCargoTypes, '차종 없음'] : uniqueCargoTypes;
  };
  
  const getDynamicDepartmentOptions = () => {
    let filteredRows = sortedRows; // allRows 대신 sortedRows 사용
    
    // 거래처 필터가 적용된 경우 (배열 타입에 맞게 수정)
    if (filters.company.length > 0) {
      filteredRows = filteredRows.filter(row => 
        filters.company.some(company => row.company?.includes(company))
      );
    }
    
    // 품명 필터가 적용된 경우 (배열 타입에 맞게 수정)
    if (filters.itemName.length > 0) {
      filteredRows = filteredRows.filter(row => {
        if (filters.itemName.includes('품명 없음')) {
          const itemName = row.itemName;
          return !itemName || itemName === '' || itemName === null || itemName === undefined || itemName.trim() === '';
        }
        return filters.itemName.some(itemName => row.itemName?.includes(itemName));
      });
    }
    
    // 차종 필터가 적용된 경우 (배열 타입에 맞게 수정)
    if (filters.cargo.length > 0) {
      filteredRows = filteredRows.filter(row => {
        if (filters.cargo.includes('차종 없음')) {
          const cargo = row.cargo;
          return !cargo || cargo === '' || cargo === null || cargo === undefined || cargo.trim() === '';
        }
        return filters.cargo.some(cargo => row.cargo === cargo);
      });
    }
    
    const departments = filteredRows.map(row => row.department || '').filter(dept => dept !== '' && dept.trim() !== '');
    const uniqueDepartments = Array.from(new Set(departments));
    
    // 소속이 없는 데이터가 있는지 확인 (공백 문자열 포함)
    const hasEmptyDepartment = filteredRows.some(row => {
      const department = row.department;
      return !department || department === '' || department === null || department === undefined || department.trim() === '';
    });
    
    return hasEmptyDepartment ? [...uniqueDepartments, '소속 없음'] : uniqueDepartments;
  };
  
  // 동적 옵션들
  const itemNameFilterOptions = getDynamicItemNameOptions();
  const cargoOptions = getDynamicCargoOptions();
  const departmentOptions = getDynamicDepartmentOptions();
  
  // 날짜 옵션 추출
  const dateOptions = Array.from(new Set(allRows.map(row => row.date).filter(Boolean)));
  
  // 정산일 옵션 추출
  const settlementDateOptions = Array.from(new Set(allRows.map(row => row.settlementDate).filter(Boolean)));

  // 검색어로 필터링된 옵션 가져오기
  const getFilteredOptions = (filterName: keyof typeof filterSearchTerms): string[] => {
    let allOptions: string[] = [];
    
    switch (filterName) {
      case 'company':
        allOptions = companyFilterOptions;
        break;
      case 'date':
        allOptions = dateOptions;
        break;
      case 'itemName':
        allOptions = itemNameFilterOptions;
        break;
      case 'cargo':
        allOptions = cargoOptions;
        break;
      case 'department':
        allOptions = departmentOptions;
        break;
      case 'settlementStatus':
        allOptions = ['미정산', '정산'];
        break;
      case 'settlementDate':
        allOptions = settlementDateOptions;
        break;
      default:
        return [];
    }
    
    const searchTerm = filterSearchTerms[filterName].toLowerCase().trim();
    
    if (!searchTerm) return allOptions;
    
    return allOptions.filter(option => 
      option.toLowerCase().includes(searchTerm)
    );
  };

  // 체크박스 선택 핸들러
  const handleCompanyCheck = (company: string) => {
    setSelectedCompanies(prev =>
      prev.includes(company)
        ? prev.filter(c => c !== company)
        : [...prev, company]
    );
  };

  // 거래처별 PDF 다운로드 함수
  const handlePdfDownload = async () => {
    setPdfGenerating(true);
    // 항상 input의 value를 직접 읽음
    const start = startDateRef.current?.value || statementDateRange.start;
    const end = endDateRef.current?.value || statementDateRange.end;
      if (selectedTemplateType === 'B') {
      // 화주품명명세서: 거래처 1개, 품명 여러 개
      const company = selectedCompanies[0];
      for (const itemName of selectedItemNames) {
        try {
          // 1. 템플릿 정보 fetch
          const templateRes = await fetch(`/api/settlement-template/B`);
          const templateData = templateRes.ok ? await templateRes.json() : {};
          // 2. 거래처+품명 데이터 fetch
          const res = await fetch(`/api/settlement-reports/pg/company/${encodeURIComponent(company)}?start=${start}&end=${end}`);
          const rows: SettlementRow[] = await res.json();
          // 3. 품명별 데이터만 필터링
          const filteredRows = rows.filter(r => r.itemName === itemName);
          filteredRows.sort((a, b) => a.date.localeCompare(b.date));
          // PDF에 실제 표시되는 rows 생성
          const pdfRows = filteredRows.map((r, idx) => ({
            index: idx + 1,
            date: r.date,
            vehicleNumber: r.vehicleNumber || '',
            route: r.route || '',
            cargo: r.cargo || '',
            billingAmount: typeof r.billingAmount === 'number' ? r.billingAmount : Number(String(r.billingAmount).replace(/[^\d.-]/g, '')) || 0,
            templateType: 'B',
            pdfMode: true
          }));
          // 청구운송료(상단) = 실제 PDF 표에 표시되는 rows의 billingAmount 합계
          const totalAmount = pdfRows.reduce((sum, r) => sum + (Number(r.billingAmount) || 0), 0);
          // 4. StatementTemplatePage용 데이터 props 준비 (수정: supplier를 STATEMENT_SUPPLIERS[supplierType]로 직접 전달)
          const statementProps = {
            company: `${company}\n${itemName}`,
            dateRange: `${start} ~ ${end}`,
            totalAmount,
            rows: pdfRows,
            templateType: 'B',
            supplier: STATEMENT_SUPPLIERS[supplierType] // ← 직접 전달
          };
          // statementProps 로그 추가
          console.log('[명세서 생성] statementProps:', statementProps);
          // 5. 임시 div에 StatementTemplatePage 렌더링
          const tempDiv = document.createElement('div');
          tempDiv.style.position = 'fixed';
          tempDiv.style.left = '-9999px';
          document.body.appendChild(tempDiv);
          const tempRoot = createRoot(tempDiv);
          tempRoot.render(
            <StatementTemplatePage {...statementProps} isFullScreen={false} setIsFullScreen={() => {}} pdfMode={true} />
          );
          await waitForStatementPdfRender();
          await html2pdf().set({
            margin: [0, 0, 0, 0], // 여백 완전 제거
            filename: `${company}_${itemName}_${start}~${end}.pdf`,
            html2canvas: { 
              scale: 2, // 해상도 향상
              useCORS: true,
              backgroundColor: '#ffffff',
              width: 794, // A4 너비 고정
              height: undefined,
              scrollX: 0,
              scrollY: 0,
              letterRendering: true,
              allowTaint: true,
              dpi: 300,
              logging: false,
              removeContainer: true
            },
            jsPDF: { 
              unit: 'px', // 픽셀 단위 사용
              format: [794, 1123], // A4 크기 (픽셀)
              orientation: 'portrait',
              compress: true,
              putOnlyUsedFonts: true,
              floatPrecision: 16
            },
            pagebreak: { mode: ['css', 'legacy'] } // CSS page-break 강화
          }).from(tempDiv.firstElementChild || tempDiv).toPdf().get('pdf').then(function (pdf) {
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              pdf.setFontSize(10);
              pdf.text(`- ${i} -`, pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 20, { align: 'center' });
            }
          }).save();
          tempRoot.unmount();
          document.body.removeChild(tempDiv);
        } catch (error) {
          console.error('PDF 생성 중 오류:', error);
          alert('PDF 파일 생성 중 오류가 발생했습니다.');
        }
      }
    } else if (selectedTemplateType === 'C') {
      // 기사명세서 PDF 생성
      try {
        // 1. 템플릿 정보 fetch
        const templateRes = await fetch(`/api/settlement-template/C`);
        const templateData = templateRes.ok ? await templateRes.json() : {};
        const params = new URLSearchParams({
          start,
          end,
          vehicles: driverVehicleList.join(',')
        });
        const res = await fetch(`/api/settlement-reports/pg/driver?${params.toString()}`);
        const data = await res.json(); // { [차량번호]: [row, ...] }
        let hasGeneratedAny = false;
        for (const vehicle of driverVehicleList) {
          const rows = data[vehicle] || [];
          if (rows.length === 0) {
            alert(`차량번호 ${vehicle}의 내역이 존재하지 않습니다.`);
            continue;
          }
          // 기사명세서용 totalAmount는 paymentAmount 총합 (항상 숫자 변환)
          const totalAmount = rows.reduce((sum: any, r: any) => sum + (Number(r.paymentAmount) || 0), 0);
          const dateRangeStr = `${start} ~ ${end}`;
          // 3. 템플릿 매핑
          const tempDiv = document.createElement('div');
          tempDiv.style.position = 'fixed';
          tempDiv.style.left = '-9999px';
          document.body.appendChild(tempDiv);
          const tempRoot = createRoot(tempDiv);
          tempRoot.render(
            <StatementTemplatePage
              company={vehicle}
              dateRange={dateRangeStr}
              totalAmount={totalAmount}
              rows={rows}
              pdfMode={true}
              templateType={'C' as const}
              supplier={STATEMENT_SUPPLIERS[supplierType]}
            />
          );
          await waitForStatementPdfRender();
          await html2pdf().set({
            margin: [0, 0, 0, 0], // 여백 완전 제거
            filename: `${vehicle}_${start}~${end}.pdf`,
            html2canvas: { 
              scale: 2, // 해상도 향상
              useCORS: true,
              backgroundColor: '#ffffff',
              width: 794, // A4 너비 고정
              height: undefined,
              scrollX: 0,
              scrollY: 0,
              letterRendering: true,
              allowTaint: true,
              dpi: 300,
              logging: false,
              removeContainer: true
            },
            jsPDF: { 
              unit: 'px', // 픽셀 단위 사용
              format: [794, 1123], // A4 크기 (픽셀)
              orientation: 'portrait',
              compress: true,
              putOnlyUsedFonts: true,
              floatPrecision: 16
            },
            pagebreak: { mode: ['css', 'legacy'] } // CSS page-break 강화
          }).from(tempDiv.firstElementChild || tempDiv).toPdf().get('pdf').then(function (pdf) {
            const totalPages = pdf.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              pdf.setFontSize(10);
              pdf.text(`- ${i} -`, pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 20, { align: 'center' });
            }
          }).save();
          tempRoot.unmount();
          document.body.removeChild(tempDiv);
          hasGeneratedAny = true;
        }
        setPdfGenerating(false);
        if (hasGeneratedAny) {
          alert('PDF 파일이 생성되었습니다.');
        }
      } catch (error) {
        setPdfGenerating(false);
        alert('PDF 생성 중 오류가 발생했습니다.');
      }
      return;
    } else {
      // 기존 화주명세서 등 기존 방식
      for (const company of selectedCompanies) {
        try {
          // 1. 템플릿 정보 fetch (이 부분이 반드시 필요!)
          const templateRes = await fetch(`/api/settlement-template/${selectedTemplateType}`);
          const templateData = templateRes.ok ? await templateRes.json() : {};

          // 2. 거래처 데이터 fetch
          const res = await fetch(`/api/settlement-reports/pg/company/${encodeURIComponent(company)}?start=${start}&end=${end}`);
          const rows: SettlementRow[] = await res.json();
          rows.sort((a, b) => a.date.localeCompare(b.date));
          const totalAmount = rows.reduce((sum: any, r: any) => sum + (typeof r.billingAmount === 'number' ? r.billingAmount : Number(String(r.billingAmount).replace(/[^\d.-]/g, '')) || 0), 0);

          // 로그 추가
          console.log('[명세서 생성] rows:', rows);
          console.log('[명세서 생성] rows.length:', rows.length);
          console.log('[명세서 생성] totalAmount:', totalAmount);

          // 3. statementProps에 templateData 값 전달
          const statementProps = {
            company,
            dateRange: `${start} ~ ${end}`,
            totalAmount,
            rows: rows.map((r, idx) => ({
              index: idx + 1,
              date: r.date,
              vehicleNumber: r.vehicleNumber || '',
              route: r.route || '',
              cargo: r.cargo || '',
              billingAmount: typeof r.billingAmount === 'number' ? r.billingAmount : Number(String(r.billingAmount).replace(/[^\d.-]/g, '')) || 0,
              templateType: selectedTemplateType,
              pdfMode: true
            })),
            templateType: selectedTemplateType,
            supplier: STATEMENT_SUPPLIERS[supplierType],
            registrationNumber: templateData.registrationNumber,
            companyName: templateData.companyName,
            ceo: templateData.ceo,
            address: templateData.address,
            addressFontSize: templateData.addressFontSize,
            phone: templateData.phone,
            fax: templateData.fax,
            tableHeaders: templateData.tableHeaders,
          };

          // statementProps 로그 추가
          console.log('[명세서 생성] statementProps:', statementProps);

          const tempDiv = document.createElement('div');
          tempDiv.style.position = 'fixed';
          tempDiv.style.left = '-9999px';
          document.body.appendChild(tempDiv);
          const tempRoot = createRoot(tempDiv);
          tempRoot.render(
            <StatementTemplatePage {...statementProps} isFullScreen={false} setIsFullScreen={() => {}} pdfMode={true} />
          );
          await waitForStatementPdfRender();
                      await html2pdf().set({
              margin: [0, 0, 0, 0], // 여백 완전 제거
              filename: `${company}_${start}~${end}.pdf`,
              html2canvas: { 
                scale: 2, // 해상도 향상
                useCORS: true,
                backgroundColor: '#ffffff',
                width: 794, // A4 너비 고정
                height: undefined,
                scrollX: 0,
                scrollY: 0,
                letterRendering: true,
                allowTaint: true,
                dpi: 300,
                logging: false,
                removeContainer: true
              },
              jsPDF: { 
                unit: 'px', // 픽셀 단위 사용
                format: [794, 1123], // A4 크기 (픽셀)
                orientation: 'portrait',
                compress: true,
                putOnlyUsedFonts: true,
                floatPrecision: 16
              },
              pagebreak: { mode: ['css', 'legacy'] } // CSS page-break 강화
            }).from(tempDiv.firstElementChild || tempDiv).toPdf().get('pdf').then(function (pdf) {
              const totalPages = pdf.internal.getNumberOfPages();
              for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i);
                pdf.setFontSize(10);
                pdf.text(`- ${i} -`, pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 20, { align: 'center' });
              }
            }).save();
          tempRoot.unmount();
          document.body.removeChild(tempDiv);
        } catch (error) {
          console.error('PDF 생성 중 오류:', error);
          alert('PDF 파일 생성 중 오류가 발생했습니다.');
        }
      }
    }
    setPdfGenerating(false);
    alert('PDF 파일이 생성되었습니다.');
  };

  // 거래처별 엑셀 다운로드 함수
  const handleExcelDownload = async () => {
    setExcelGenerating(true); // 엑셀 생성 시작 시 로딩 상태 true
    const start = startDateRef.current?.value || statementDateRange.start;
    const end = endDateRef.current?.value || statementDateRange.end;
    // @ts-ignore
    const XlsxPopulate = window.XlsxPopulate;
    if (!XlsxPopulate) {
      alert('엑셀 라이브러리가 로드되지 않았습니다.\n새로고침 후 다시 시도해 주세요.\npublic/index.html에 xlsx-populate CDN 스크립트가 포함되어 있는지 확인하세요.');
      setExcelGenerating(false); // 실패 시에도 false로 복구
      return;
    }
    if (selectedTemplateType === 'A') {
      for (const company of selectedCompanies) {
        // 1. 템플릿 파일 불러오기
        const response = await fetch('/excel-template/company_template.xlsx');
        const arrayBuffer = await response.arrayBuffer();
        // @ts-ignore
        const workbook = await window.XlsxPopulate.fromDataAsync(arrayBuffer);
        const sheet = workbook.sheet(0);

        // 2. 데이터 fetch
        const res = await fetch(`/api/settlement-reports/pg/company/${encodeURIComponent(company)}?start=${start}&end=${end}`);
        let rows: SettlementRow[] = await res.json();
        // 3. vehicleNumber, route가 모두 존재하는 행만 필터링
        rows = rows.filter(r => r.vehicleNumber && r.route);

        rows.sort((a, b) => a.date.localeCompare(b.date));
        const totalAmount = rows.reduce((sum: any, r: any) => sum + (typeof r.billingAmount === 'number' ? r.billingAmount : Number(String(r.billingAmount).replace(/[^\d.-]/g, '')) || 0), 0);

        // 4. 템플릿 매핑
        sheet.cell('A3').value(company);
        sheet.cell('A8').value(`청구금액 : ${totalAmount.toLocaleString()}원 ( 부가세 별도 ) / ${start} ~ ${end}`);
        // 공급자 정보 매핑
        const supplier = STATEMENT_SUPPLIERS[supplierType];
        sheet.cell('F3').value(supplier.registrationNumber);
        sheet.cell('F4').value(supplier.name);
        sheet.cell('H4').value(supplier.ceo);
        sheet.cell('F5').value(supplier.address);
        sheet.cell('F6').value(supplier.phone);
        sheet.cell('F7').value(supplier.fax);

        // 5. 데이터 행 입력 (A11~)
        for (let i = 0; i < rows.length; i++) {
          const r = rows[i];
          const rowNum = 11 + i;
          sheet.cell(`A${rowNum}`).value(i + 1);
          sheet.cell(`B${rowNum}`).value(r.date);
          sheet.cell(`C${rowNum}`).value(r.vehicleNumber || '');
          sheet.cell(`D${rowNum}`).value(r.route || '');
          // D,E,F 병합
          sheet.range(`D${rowNum}:F${rowNum}`).merged(true);
          sheet.cell(`G${rowNum}`).value(r.cargo || '');
          sheet.cell(`H${rowNum}`).value(typeof r.billingAmount === 'number' ? r.billingAmount : Number(String(r.billingAmount).replace(/[^\d.-]/g, '')) || 0).style("numberFormat", "#,##0");
        }

        // 6. 테이블 데이터 테두리 적용 (헤더 행 10행 + 데이터 행들)
        if (rows.length > 0) {
          const headerRow = 10;
          const lastDataRow = 10 + rows.length;
          for (let r = headerRow; r <= lastDataRow; r++) {
            for (let c = 1; c <= 8; c++) { // A~H 컬럼
              sheet.cell(r, c).style('border', true);
            }
          }
        }

        // 7. 데이터 끝난 후 2행 띄우고, 오른쪽 하단에 합계
        const sumRow = 11 + rows.length + 2;
        sheet.cell(`G${sumRow}`).value('합계 :');
        sheet.cell(`H${sumRow}`).value(totalAmount).style("numberFormat", "#,##0");



        // 9. 파일 저장 및 다운로드
        const blob = await workbook.outputAsync();
        const fileName = `${company}_${start}~${end}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
      }
      setExcelGenerating(false);
      // setShowStatementModal(false); // 사이드바는 사용자가 직접 닫을 때까지 열어둠
      alert('엑셀 파일이 생성되었습니다.');
      return;
    }
    if (selectedTemplateType === 'B') {
      const company = selectedCompanies[0];
      // 1. 템플릿 정보 fetch
      const templateRes = await fetch(`/api/settlement-template/B`);
      const templateData = templateRes.ok ? await templateRes.json() : {};
      for (const itemName of selectedItemNames) {
        try {
          // 2. 거래처+품명 데이터 fetch (엑셀용)
          const res = await fetch(`/api/settlement-reports/pg/company/${encodeURIComponent(company)}?start=${start}&end=${end}`);
          const rows: SettlementRow[] = await res.json();
          // 3. 품명별 데이터만 필터링
          const filteredRows = rows.filter(r => r.itemName === itemName);
          filteredRows.sort((a, b) => a.date.localeCompare(b.date));
          // 합계 먼저 계산
          const totalAmount = filteredRows.reduce((sum, r) => sum + (typeof r.billingAmount === 'number' ? r.billingAmount : Number(String(r.billingAmount).replace(/[^\d.-]/g, '')) || 0), 0);
          // 4. 엑셀 템플릿 파일 불러오기
          const response = await fetch('/excel-template/company_template.xlsx');
          const arrayBuffer = await response.arrayBuffer();
          // @ts-ignore
          const workbook = await window.XlsxPopulate.fromDataAsync(arrayBuffer);
          const sheet = workbook.sheet(0);
          // 공급자 정보 매핑
          const supplier = STATEMENT_SUPPLIERS[supplierType];
          sheet.cell('F3').value(supplier.registrationNumber);
          sheet.cell('F4').value(supplier.name);
          sheet.cell('H4').value(supplier.ceo);
          sheet.cell('F5').value(supplier.address);
          sheet.cell('F6').value(supplier.phone);
          sheet.cell('F7').value(supplier.fax);
          // 상단 정보 매핑 추가
          sheet.cell('A3').value(company + '\n' + itemName);
          sheet.cell('A3').style('wrapText', true);
          sheet.cell('A8').value(`청구운송료 : ${totalAmount.toLocaleString()}원 ( 부가세 별도 ) / ${start} ~ ${end}`);
          // 5. 데이터 행 입력 (A11~)
          for (let i = 0; i < filteredRows.length; i++) {
            const r = filteredRows[i];
            const rowNum = 11 + i;
            sheet.cell(`A${rowNum}`).value(i + 1);
            sheet.cell(`B${rowNum}`).value(r.date);
            sheet.cell(`C${rowNum}`).value(r.vehicleNumber || '');
            sheet.cell(`D${rowNum}`).value(r.route || '');
            // D,E,F 병합
            sheet.range(`D${rowNum}:F${rowNum}`).merged(true);
            sheet.cell(`G${rowNum}`).value(r.cargo || '');
            sheet.cell(`H${rowNum}`).value(typeof r.billingAmount === 'number' ? r.billingAmount : Number(String(r.billingAmount).replace(/[^\d.-]/g, '')) || 0).style("numberFormat", "#,##0");
          }
          // 6. 테이블 데이터 테두리 적용 (헤더 행 10행 + 데이터 행들)
          if (filteredRows.length > 0) {
            const headerRow = 10;
            const lastDataRow = 10 + filteredRows.length;
            for (let r = headerRow; r <= lastDataRow; r++) {
              for (let c = 1; c <= 8; c++) { // A~H 컬럼
                sheet.cell(r, c).style('border', true);
              }
            }
          }

          // 7. 데이터 끝난 후 2행 띄우고, 오른쪽 하단에 합계
          const sumRow = 11 + filteredRows.length + 2;
          sheet.cell(`G${sumRow}`).value('합계 :');
          sheet.cell(`H${sumRow}`).value(totalAmount).style("numberFormat", "#,##0");
          

          
          // 9. 파일 저장 및 다운로드
          const blob = await workbook.outputAsync();
          const fileName = `${company}_${itemName}_${start}~${end}.xlsx`;
          const url = window.URL.createObjectURL(new Blob([blob]));
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);
        } catch (error) {
          console.error('엑셀 생성 중 오류:', error);
          alert('엑셀 파일 생성 중 오류가 발생했습니다.');
        }
      }
      setExcelGenerating(false);
      // setShowStatementModal(false); // 사이드바는 사용자가 직접 닫을 때까지 열어둠
      alert('엑셀 파일이 생성되었습니다.');
      return;
    }
    if (selectedTemplateType === 'C') {
      for (const vehicle of driverVehicleList) {
        // 1. 템플릿 파일 불러오기
        const response = await fetch('/excel-template/vehicle_template.xlsx');
        const arrayBuffer = await response.arrayBuffer();
        // @ts-ignore
        const workbook = await window.XlsxPopulate.fromDataAsync(arrayBuffer);
        const sheet = workbook.sheet(0);

        // 2. 데이터 준비
        const params = new URLSearchParams({
          start,
          end,
          vehicles: vehicle
        });
        const res = await fetch(`/api/settlement-reports/pg/driver?${params.toString()}`);
        const data = await res.json();
        const supplier = STATEMENT_SUPPLIERS[supplierType];
        const originalRows = data[vehicle] || [];
        const rows = filterDriverStatementRows(originalRows, supplier?.name || '');
        if (rows.length === 0) {
          alert(`차량번호 ${vehicle}의 내역이 존재하지 않습니다.`);
          continue;
        }
        const totalAmount = rows.reduce((sum, r) => sum + (Number(r.paymentAmount) || 0), 0);
        const dateRangeStr = `${start} ~ ${end}`;

        // 3. 템플릿 매핑 (A3: 차량번호 + 조건부 계좌 문구)
        const supplierNameCompact = (supplier?.name || '').replace(/\s+/g, '');
        const hasCompany = (target: string) => originalRows.some((r) => getStatementRowCompany(r) === target);
        let driverAccountLine = '';
        if (/성우로지스/.test(supplierNameCompact) && hasCompany('지입료')) {
          driverAccountLine = '국민 078737-01-001847 ( 성우로지스 )';
        } else if (/한국운수/.test(supplierNameCompact) && hasCompany('화물공제보험')) {
          driverAccountLine = '기업 938-020507-01-014 ( 장원석 )';
        }
        const a3 = sheet.cell('A3');
        a3.value(driverAccountLine ? `${vehicle}\n${driverAccountLine}` : vehicle);
        // 줄바꿈 보이도록 wrap 설정(템플릿에 설정되어 있다면 생략 가능)
        try { a3.style('wrapText', true); } catch {}
        sheet.cell('A8').value(`결산금액 : ${totalAmount.toLocaleString()}원 ( 부가세 별도 ) / ${dateRangeStr}`);
        // 공급자 정보 매핑
        sheet.cell('F3').value(supplier.registrationNumber);
        sheet.cell('F4').value(supplier.name);
        sheet.cell('H4').value(supplier.ceo);
        sheet.cell('F5').value(supplier.address);
        sheet.cell('F6').value(supplier.phone);
        sheet.cell('F7').value(supplier.fax);

        // 4. 데이터 행 입력 (A11~)
        let 누계 = 0;
        for (let i = 0; i < rows.length; i++) {
          const r = rows[i];
          누계 += Number(r.paymentAmount) || 0;
          const rowNum = 11 + i;
          sheet.cell(`A${rowNum}`).value(i + 1);
          sheet.cell(`B${rowNum}`).value(r.date);
          sheet.cell(`C${rowNum}`).value(r.cargo || '');
          sheet.cell(`D${rowNum}`).value(r.route || '');
          // D,E,F 병합
          sheet.range(`D${rowNum}:F${rowNum}`).merged(true);
          sheet.cell(`G${rowNum}`).value(Number(r.paymentAmount) || 0).style("numberFormat", "#,##0");
          sheet.cell(`H${rowNum}`).value(누계).style("numberFormat", "#,##0");
        }

        // 5. 테이블 데이터 테두리 적용 (헤더 행 10행 + 데이터 행들)
        if (rows.length > 0) {
          const headerRow = 10;
          const lastDataRow = 10 + rows.length;
          for (let r = headerRow; r <= lastDataRow; r++) {
            for (let c = 1; c <= 8; c++) { // A~H 컬럼
              sheet.cell(r, c).style('border', true);
            }
          }
        }

        // 6. 데이터 끝난 후 2행 띄우고, 오른쪽 하단에 합계
        const sumRow = 11 + rows.length + 2;
        sheet.cell(`G${sumRow}`).value('합계 :');
        sheet.cell(`H${sumRow}`).value(totalAmount).style("numberFormat", "#,##0");



        // 8. 파일 저장 및 다운로드
        const blob = await workbook.outputAsync();
        const fileName = `${vehicle}_${start}~${end}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
      }
      setExcelGenerating(false);
      // setShowStatementModal(false); // 사이드바는 사용자가 직접 닫을 때까지 열어둠
      alert('엑셀 파일이 생성되었습니다.');
      return;
    }
    if (selectedTemplateType === 'ramen') {
      try {
        const response = await fetch('/excel-template/ramen_template.xlsx');
        const arrayBuffer = await response.arrayBuffer();
        // @ts-ignore
        const workbook = await window.XlsxPopulate.fromDataAsync(arrayBuffer);
        const sheet = workbook.sheet(0);

        const getDateList = (start: string, end: string) => {
          const result: string[] = [];
          let cur = new Date(start);
          const endDate = new Date(end);
          while (cur <= endDate) {
            result.push(cur.toISOString().slice(0, 10));
            cur.setDate(cur.getDate() + 1);
          }
          return result;
        };
        const dateList = getDateList(start, end);

        const supplier = STATEMENT_SUPPLIERS[supplierType];
        sheet.cell('F3').value(supplier.registrationNumber);
        sheet.cell('F4').value(supplier.name);
        sheet.cell('H4').value(supplier.ceo);
        sheet.cell('F5').value(supplier.address);
        sheet.cell('F6').value(supplier.phone);
        sheet.cell('F7').value(supplier.fax);

        // 1. settlement-reports API로 기간 전체 rows fetch
        const rowsRes = await fetch(`/api/settlement-reports/pg/company/${encodeURIComponent('오뚜기농협물류')}?start=${start}&end=${end}`);
        if (!rowsRes.ok) throw new Error('데이터 조회 실패');
        const allRows = await rowsRes.json();
        // 2. itemName === '라면'만 필터링
        const ramenRows = allRows.filter((r: any) => (r.itemName || '').replace(/\s+/g, '') === '라면');
        // 3. 날짜별로 그룹핑
        const rowsByDate: { [date: string]: any[] } = {};
        ramenRows.forEach((row: any) => {
          if (!rowsByDate[row.date]) rowsByDate[row.date] = [];
          rowsByDate[row.date].push(row);
        });

        // Firestore(배차 요약)에서 라면 수량 요약을 한 번에 가져오기
        const ramenSummaryRes = await fetch(`/api/search-ramen?start=${start}&end=${end}`);
        const ramenSummary: { date: string; exportramenNum: number; domesticramenNum: number; oilramenNum: number }[] =
          ramenSummaryRes.ok ? await ramenSummaryRes.json() : [];
        const ramenByDate: Record<string, { exp: number; dom: number }> = {};
        ramenSummary.forEach(r => {
          const d = (r.date || '').slice(0, 10);
          if (!d) return;
          ramenByDate[d] = {
            exp: Number(r.exportramenNum) || 0,
            dom: Number(r.domesticramenNum) || 0,
          };
        });

        // 1. A8 셀에 수출/내수 총합 표시
        let totalExport = 0;
        let totalDomestic = 0;
        for (const date of dateList) {
          const info = ramenByDate[date];
          if (!info) continue;
          totalExport += info.exp;
          totalDomestic += info.dom;
        }
        sheet.cell('A8').value(`수출 : ${totalExport.toFixed(2)} / 내수 : ${totalDomestic.toFixed(2)} / ${start} ~ ${end}`);

        let currentRow = 11;
        let dateIndex = 1;
        for (const date of dateList) {
          const rows = rowsByDate[date] || [];
          if (rows.length === 0) continue;
          // 날짜별 라면 수량: settlement_rows에 값이 있으면 우선 사용, 없으면 Firestore 요약에서 보완
          let domesticramenNum = '';
          let exportramenNum = '';
          if (rows[0].domesticramenNum !== undefined && rows[0].exportramenNum !== undefined) {
            domesticramenNum = rows[0].domesticramenNum;
            exportramenNum = rows[0].exportramenNum;
          }
          const info = ramenByDate[date];
          if (info) {
            if (!exportramenNum) exportramenNum = String(info.exp);
            if (!domesticramenNum) domesticramenNum = String(info.dom);
          }
          const startRow = currentRow;
          const endRow = currentRow + rows.length - 1;
          if (rows.length > 1) {
            sheet.range(`A${startRow}:A${endRow}`).merged(true); // 순번 병합
            sheet.range(`B${startRow}:B${endRow}`).merged(true); // 날짜 병합
            // 운행구간 병합 제거 (D~F)
            sheet.range(`G${startRow}:G${endRow}`).merged(true); // 수출 병합
            sheet.range(`H${startRow}:H${endRow}`).merged(true); // 내수 병합
          }
          for (let i = 0; i < rows.length; i++) {
            const rowNum = currentRow + i;
            if (i === 0) {
              sheet.cell(`A${rowNum}`).value(dateIndex); // 날짜별 순번
              sheet.cell(`B${rowNum}`).value(date);      // 날짜
              // 수출/내수 값을 숫자로 변환하여 입력 (소수점 둘째 자리까지만)
              sheet.cell(`G${rowNum}`).value(Number(exportramenNum) || 0).style('numberFormat', '#,##0.00');
              sheet.cell(`H${rowNum}`).value(Number(domesticramenNum) || 0).style('numberFormat', '#,##0.00');
            }
            sheet.cell(`C${rowNum}`).value(rows[i].vehicleNumber || '');
            // 운행구간 D~F 병합 및 값 입력
            sheet.range(`D${rowNum}:F${rowNum}`).merged(true);
            sheet.cell(`D${rowNum}`).value(rows[i].route || '');
          }
          currentRow += rows.length;
          dateIndex += 1;
        }
        // 표 데이터 테두리 적용
        const lastRow = currentRow - 1;
        if (lastRow >= 11) {
          for (let r = 11; r <= lastRow; r++) {
            for (let c = 1; c <= 8; c++) { // A~H
              sheet.cell(r, c).style('border', true);
            }
          }
        }
        // 총합계 행 추가
        const sumRow = currentRow;
        sheet.range(`A${sumRow}:F${sumRow}`).merged(true);
        sheet.cell(`A${sumRow}`).value('총 합계');
        sheet.range(`G${sumRow}:H${sumRow}`).merged(true);
        const totalSum = Number(totalExport) + Number(totalDomestic);
        sheet.cell(`G${sumRow}`).value(`총 합계: ${totalSum.toFixed(2)}`).style('numberFormat', '#,##0.00');
        // 총합계 행 테두리 적용
        for (let c = 1; c <= 8; c++) {
          sheet.cell(sumRow, c).style('border', true);
        }
        const blob = await workbook.outputAsync();
        const fileName = `라면명세서_${start}~${end}.xlsx`;
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
        setExcelGenerating(false);
        alert('엑셀 파일이 생성되었습니다.');
        return;
      } catch (error) {
        setExcelGenerating(false);
        alert('엑셀 파일 생성 중 오류가 발생했습니다.');
        return;
      }
    }
  };

  // 기사계산서: tax_template.xlsx 다운로드 (차량별 1파일)
  const handleTaxInvoiceDownload = async () => {
    setTaxGenerating(true);
    const start = startDateRef.current?.value || statementDateRange.start;
    const end = endDateRef.current?.value || statementDateRange.end;
    // @ts-ignore
    const XlsxPopulate = window.XlsxPopulate;
    if (!XlsxPopulate) {
      alert('엑셀 라이브러리가 로드되지 않았습니다.\n새로고침 후 다시 시도해 주세요.');
      setTaxGenerating(false);
      return;
    }
    try {
      const params = new URLSearchParams({
        start,
        end,
        vehicles: driverVehicleList.join(','),
      });
      const res = await fetch(`/api/settlement-reports/pg/driver?${params.toString()}`);
      const data = await res.json();
      let generated = 0;

      for (const vehicle of driverVehicleList) {
        const rows: SettlementRow[] = data[vehicle] || [];
        if (rows.length === 0) {
          alert(`차량번호 ${vehicle}의 내역이 존재하지 않습니다.`);
          continue;
        }

        const supplyTotal = rows.reduce(
          (sum, r) => sum + (Number(r.paymentAmount) || 0),
          0
        );
        const taxAmount = supplyTotal * 0.1;

        const driverName =
          rows.map(r => r.driverName).find(n => n && String(n).trim()) || '';
        const businessNumber =
          rows.map(r => r.businessNumber).find(n => n && String(n).trim()) || '';

        const response = await fetch('/excel-template/tax_template.xlsx');
        const arrayBuffer = await response.arrayBuffer();
        // @ts-ignore
        const workbook = await XlsxPopulate.fromDataAsync(arrayBuffer);
        const sheet = workbook.sheet(0);

        if (supplierType === 'korea') {
          const recipient = STATEMENT_SUPPLIERS.korea;
          sheet.cell('V5').value(recipient.registrationNumber);
          sheet.cell('V7').value(recipient.name);
          sheet.cell('V29').value(recipient.registrationNumber);
          sheet.cell('V31').value(recipient.name);
        }

        sheet.cell('D6').value(formatBusinessNumber(businessNumber));
        sheet.cell('F7').value(vehicle);
        sheet.cell('M7').value(driverName);
        sheet.cell('U17').value(supplyTotal);
        sheet.cell('Z17').value(taxAmount);
        // 합계금액(B22)은 공급가액·세액만 표시 — 템플릿 SUM 공식 제거
        sheet.cell('B22').value('');

        const blob = await workbook.outputAsync();
        const fileName = `${vehicle}_${start}~${end}_계산서.xlsx`;
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
        generated += 1;
      }

      if (generated > 0) {
        alert('계산서 파일이 생성되었습니다.');
      }
    } catch (error) {
      console.error('계산서 생성 중 오류:', error);
      alert('계산서 파일 생성 중 오류가 발생했습니다.');
    } finally {
      setTaxGenerating(false);
    }
  };

  // 구조 동기화 이벤트는 증분 이벤트로 처리 (전체 재조회 생략)
  useEffect(() => {
    const handleStructureUpdated = (data: { date: string, eventId?: string }) => {
      // no-op: row_added/row_deleted 이벤트에서 증분 반영
      if (!data.date || data.date < dateRange.start || data.date > dateRange.end) return;
    };
    socket.on('dispatch-row-structure-updated', handleStructureUpdated);
    return () => {
      socket.off('dispatch-row-structure-updated', handleStructureUpdated);
    };
  }, [dateRange]);

  // [비활성화] 증분 처리 자동 반영 중지 — 알림 방식으로 전환

  // 4) 포커스/가시성/소켓 재연결 시 강제 재조회 비활성화 (대용량 편집 세션 보호)
  useEffect(() => {
    // intentionally disabled: 실시간 동기화는 소켓 증분 이벤트로 유지
  }, [dateRange, debouncedFetch]);

  // 5) 동시 접속자 presence + 마지막 편집 정보
  useEffect(() => {
    const user = auth.currentUser;
    const email = user?.email || '';
    const name = emailToName[email] || email.split('@')[0] || '익명';
    socket.emit('settlement-join', { email, name });

    const handlePresence = (users: { email: string; name: string }[]) => {
      setPresenceUsers(users.filter(u => u.email !== email));
    };
    socket.on('settlement-presence', handlePresence);

    const handleEditTrack = (data: { userId?: string; timestamp?: string; userName?: string; userEmail?: string; source?: string }) => {
      // 정산일보 변경만 알림 (배차·컨테이너 배차·통합일보 등은 제외)
      if (data.source !== 'settlement') return;
      // 이벤트 시점의 로그인 사용자 기준 (행추가 시 order 일괄 저장은 예전에 userId 없이 userEmail만 보내 본인 편집이 '타인 변경'으로 집계됨)
      const uid = auth.currentUser?.uid;
      const myEmail = auth.currentUser?.email || '';
      if (data.userId && uid && data.userId === uid) return;
      if (data.userEmail && myEmail && data.userEmail === myEmail) return;
      const editEmail = data.userEmail || '';
      const editName = data.userName || emailToName[editEmail] || editEmail.split('@')[0] || '익명';
      if (editEmail) {
        setLastEditInfo({ email: editEmail, name: editName, timestamp: data.timestamp || new Date().toISOString() });
        setPendingChangesCount(prev => prev + 1);
      }
    };
    socket.on('report-updated', handleEditTrack);

    return () => {
      socket.emit('settlement-leave');
      socket.off('settlement-presence', handlePresence);
      socket.off('report-updated', handleEditTrack);
    };
  }, []);

  // 라면 버튼 클릭 핸들러 추가
  const handleRamenClick = () => {
    setRamenModalOpen(true);
    setRamenRows([]);
    setRamenStartDate('');
    setRamenEndDate('');
  };

  const [ramenModalOpen, setRamenModalOpen] = useState(false);
  const [ramenStartDate, setRamenStartDate] = useState('');
  const [ramenEndDate, setRamenEndDate] = useState('');
  const [ramenRows, setRamenRows] = useState<any[]>([]);
  const ramenModalRef = useRef<HTMLDivElement>(null);
  const [ramenLoading, setRamenLoading] = useState(false);

  // ESC 키로 모달 닫기
  useEffect(() => {
    if (!ramenModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setRamenModalOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [ramenModalOpen]);

  // 모달 바깥 클릭 시 닫기
  useEffect(() => {
    if (!ramenModalOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (ramenModalRef.current && !ramenModalRef.current.contains(e.target as Node)) {
        setRamenModalOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ramenModalOpen]);

  useEffect(() => {
    if (!settlementPasswordModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelSettlementPassword();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [settlementPasswordModalOpen, cancelSettlementPassword]);

  // 라면 데이터 조회
  const handleRamenSearch = async () => {
    if (!ramenStartDate || !ramenEndDate) return;
    setRamenLoading(true);
    try {
      const res = await fetch(`/api/search-ramen?start=${ramenStartDate}&end=${ramenEndDate}`);
      if (!res.ok) throw new Error('데이터 없음');
      const rows = await res.json();
      setRamenRows(rows);
    } catch {
      setRamenRows([]);
    }
    setRamenLoading(false);
  };

  const toRamenNum = (v: any) => Math.round((Number(String(v).replace(/[^\d.-]/g, '')) || 0) * 100) / 100;
  // 라면 표 엑셀 다운로드 (소수점 둘째 자리)
  const handleRamenExcel = () => {
    if (!ramenRows.length) return;
    const ws = XLSX.utils.json_to_sheet(ramenRows.map(r => ({
      날짜: r.date,
      수출라면: toRamenNum(r.exportramenNum),
      내수라면: toRamenNum(r.domesticramenNum),
      유지: toRamenNum(r.oilramenNum)
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '라면');
    XLSX.writeFile(wb, `라면_${ramenStartDate}_${ramenEndDate}.xlsx`);
  };

  // 합계 계산
  const ramenSum = ramenRows.reduce(
    (acc, r) => ({
      exportramenNum: acc.exportramenNum + (Number(r.exportramenNum) || 0),
      domesticramenNum: acc.domesticramenNum + (Number(r.domesticramenNum) || 0),
      oilramenNum: acc.oilramenNum + (Number(r.oilramenNum) || 0),
    }),
    { exportramenNum: 0, domesticramenNum: 0, oilramenNum: 0 }
  );

  // 우클릭 메뉴: 현재 표 데이터(모으기/감추기/검색 결과) 일괄 정산처리
  const handleBulkSettleVisibleRows = useCallback(async () => {
    const currentRows = collectedRowsRef.current || [];
    const targets = currentRows.filter((r: any) => r && r.id && r.date && !String(r.id).startsWith('new-'));
    if (targets.length === 0) {
      alert('정산처리할 데이터가 없습니다.');
      return;
    }

    const needUpdate = targets.filter((r: any) => String(r.settlementStatus || '') !== '정산');
    if (needUpdate.length === 0) {
      alert('현재 표 데이터는 이미 모두 정산 상태입니다.');
      return;
    }

    const confirmed = window.confirm(
      `현재 표 데이터를 모두 정산처리하시겠습니까?\n(총 ${targets.length}건, 변경 대상 ${needUpdate.length}건)`
    );
    if (!confirmed) return;

    const todayStr = new Date().toISOString().slice(0, 10);
    const successIds = new Set<string>();
    let failCount = 0;
    const chunkSize = 20;

    for (let i = 0; i < needUpdate.length; i += chunkSize) {
      const chunk = needUpdate.slice(i, i + chunkSize);
      const results = await Promise.allSettled(
        chunk.map((row: any) =>
          fetch('/api/settlement-reports/update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: row.id,
              date: row.date,
              settlementStatus: '정산',
              settlementDate: todayStr,
              userId: auth.currentUser?.uid,
              userEmail: auth.currentUser?.email,
            }),
          })
        )
      );

      results.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value.ok) {
          successIds.add(String(chunk[idx].id));
        } else {
          failCount += 1;
        }
      });
    }

    if (successIds.size > 0) {
      setAllRows(prev =>
        prev.map((r: any) =>
          successIds.has(String(r.id))
            ? { ...r, settlementStatus: '정산', settlementDate: todayStr }
            : r
        )
      );
      setTimeout(() => {
        if (hotRef.current) hotRef.current.hotInstance.render();
      }, 10);
    }

    if (failCount > 0) {
      alert(`정산처리 완료: 성공 ${successIds.size}건 / 실패 ${failCount}건`);
    } else {
      alert(`정산처리 완료: ${successIds.size}건`);
    }
  }, []);

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: (isStatementSidebarOpen && !isStatementSidebarCollapsed) ? 'calc(100% - 400px)' : isStatementSidebarOpen ? 'calc(100% - 40px)' : '100%', 
      flex: 1,
      minHeight: 0,
      height: '100%',
      display: 'flex', 
      flexDirection: 'column', 
      position: 'relative',
      transition: 'max-width 0.3s ease'
    }}>
      <style>
        {`
          .billing-amount {
            background-color: #fff6ef !important;
            color: #d32f2f !important;
            font-weight: 700 !important;
          }
          .payment-amount {
            background-color: #edf2ff !important;
            color: #1976d2 !important;
            font-weight: 700 !important;
          }
          /* 손익: 숫자색 유지, 배경은 뉴트럴 웜 스톤(지불 열 연파랑과 톤 분리) */
          .profit-amount-positive,
          .profit-amount-negative {
            background-color: #f0ebe6 !important;
            font-weight: 700 !important;
          }
          .profit-amount-positive {
            color: #d32f2f !important;
          }
          .profit-amount-negative {
            color: #1976d2 !important;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* 로딩 오버레이 */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            padding: 40,
            borderRadius: 16,
            boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            fontSize: 22,
            fontWeight: 700,
            color: '#1976d2'
          }}>
            <div className="spinner" style={{ marginBottom: 18 }}>
              <svg width="48" height="48" viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20" fill="none" stroke="#1976d2" strokeWidth="6" strokeDasharray="31.415, 31.415" transform="rotate(72.5042 25 25)">
                  <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            로딩 중...
          </div>
        </div>
      )}
      
      {/* 상단 UI 업그레이드 - 2단 구조 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
        {/* 1단: 왼쪽 액션 버튼 | 오른쪽(날짜·모드·전체화면) — 날짜 카드는 절대배치 제거로 복구 버튼과 겹침 방지 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            width: '100%',
            minHeight: 40,
            flexWrap: 'nowrap',
          }}
        >
          {/* 왼쪽: 엑셀/라면/명세서 버튼 */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: 0, flexShrink: 0 }}>
            <button
              onClick={handleDownloadExcel}
              style={{
                background: '#fff',
                color: '#222',
                border: '1px solid #d1d5db',
                borderRight: 'none',
                borderRadius: '4px 0 0 4px',
                padding: '0 22px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: 'none',
                outline: 'none',
                transition: 'background 0.2s',
                margin: 0
              }}
              onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              엑셀
            </button>
            <button
              onClick={handleRamenClick}
              style={{
                background: '#fff',
                color: '#222',
                border: '1px solid #d1d5db',
                borderRight: 'none',
                borderRadius: 0,
                padding: '0 22px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: 'none',
                outline: 'none',
                transition: 'background 0.2s',
                margin: 0
              }}
              onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              라면
            </button>
            <button
              onClick={handleStatementClick}
              style={{
                background: '#fff',
                color: '#222',
                border: '1px solid #d1d5db',
                borderRight: 'none',
                borderRadius: 0,
                padding: '0 22px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: 'none',
                outline: 'none',
                transition: 'background 0.2s',
                margin: 0
              }}
              onMouseOver={e => e.currentTarget.style.background = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              명세서
            </button>
            <button
              onClick={() => setFilterSectionVisible(v => !v)}
              style={{
                background: filterSectionVisible ? '#e8f4fd' : '#fff',
                color: '#1976d2',
                border: '1px solid #d1d5db',
                borderRight: 'none',
                borderRadius: 0,
                padding: '0 22px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: 'none',
                outline: 'none',
                transition: 'background 0.2s',
                margin: 0
              }}
              onMouseOver={e => { e.currentTarget.style.background = filterSectionVisible ? '#d4ebfa' : '#f3f4f6'; }}
              onMouseOut={e => { e.currentTarget.style.background = filterSectionVisible ? '#e8f4fd' : '#fff'; }}
              title={filterSectionVisible ? '필터 영역 숨기기' : '필터 영역 보기'}
            >
              필터 {filterSectionVisible ? '▲' : '▼'}
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('global-search-toggle', { detail: true }))}
              style={{
                background: '#fff',
                color: '#222',
                border: '1px solid #d1d5db',
                borderRight: 'none',
                borderRadius: '0 4px 4px 0',
                padding: '0 18px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: 'none',
                outline: 'none',
                transition: 'background 0.2s',
                margin: 0
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseOut={e => { e.currentTarget.style.background = '#fff'; }}
              title="차량결제내역 / 운송구간 통계 / 운송구간 목록 검색"
            >
              검색
            </button>
            <button
              type="button"
              onClick={() => setStatsBarVisible(v => !v)}
              style={{
                background: statsBarVisible ? '#1976d2' : '#fff',
                color: statsBarVisible ? '#fff' : '#222',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                padding: '0 18px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: 'none',
                outline: 'none',
                transition: 'background 0.2s',
                margin: 0,
                marginLeft: 8
              }}
              onMouseOver={e => { if (!statsBarVisible) e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseOut={e => { if (!statsBarVisible) e.currentTarget.style.background = '#fff'; }}
              title={statsBarVisible ? '정산 통계 숨기기' : '정산 통계 보기'}
            >
              통계
            </button>
            <button
              type="button"
              disabled={!remittanceButtonEnabled}
              onClick={handleOpenRemittanceModal}
              title={
                remittanceButtonEnabled
                  ? '현재 표에 보이는 행으로 송금(대량이체) 편집 창을 엽니다.'
                  : '표에 보이는 모든 행이 정산이고 정산일이 같을 때 사용할 수 있습니다. (필터·모으기·검색 반영)'
              }
              style={{
                background: remittanceButtonEnabled ? '#fff' : '#f9fafb',
                color: remittanceButtonEnabled ? '#047857' : '#9ca3af',
                border: remittanceButtonEnabled ? '1px solid #10b981' : '1px solid #e5e7eb',
                borderRadius: 4,
                padding: '0 18px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: remittanceButtonEnabled ? 'pointer' : 'not-allowed',
                boxShadow: 'none',
                outline: 'none',
                margin: 0,
                marginLeft: 8,
                opacity: remittanceButtonEnabled ? 1 : 0.85
              }}
              onMouseOver={e => {
                if (remittanceButtonEnabled) e.currentTarget.style.background = '#ecfdf5';
              }}
              onMouseOut={e => {
                if (remittanceButtonEnabled) e.currentTarget.style.background = '#fff';
              }}
            >
              송금
            </button>
            <button
              type="button"
              onClick={handleRestoreModalOpen}
              style={{
                background: '#fff',
                color: '#b45309',
                border: '1px solid #f59e0b',
                borderRadius: 4,
                padding: '0 18px',
                fontWeight: 500,
                fontSize: '15px',
                height: 36,
                boxSizing: 'border-box',
                cursor: 'pointer',
                boxShadow: 'none',
                outline: 'none',
                margin: 0,
                marginLeft: 8
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#fffbeb'; }}
              onMouseOut={e => { e.currentTarget.style.background = '#fff'; }}
              title="백업 목록에서 날짜를 선택해 데이터 복구"
            >
              복구
            </button>
          </div>

          {/* 가운데: 날짜·조회·체크박스 — 화면 중앙 근처 배치, 전체화면 버튼과 겹치지 않게 여유 확보 */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              paddingLeft: 12,
              paddingRight: 12,
              overflow: 'visible',
            }}
          >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              rowGap: 8,
              padding: '6px 12px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
              maxWidth: 'min(100%, 920px)',
              justifyContent: 'center',
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 16, color: '#6b7280' }} />
            <input 
              type="date" 
              value={pendingDateRange.start} 
              onChange={e => handlePendingDateChange(e, 'start')}
              style={{
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                fontSize: 13,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                width: 130
              }}
            />
            <span style={{ color: '#6b7280', fontWeight: 500, fontSize: 13 }}>~</span>
            <input 
              type="date" 
              value={pendingDateRange.end} 
              onChange={e => handlePendingDateChange(e, 'end')}
              style={{
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                fontSize: 13,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                width: 130
              }}
            />
            <button 
              onClick={handleDateConfirm} 
              style={{ 
                padding: '6px 20px', 
                borderRadius: 4, 
                border: '1px solid #1976d2', 
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', 
                color: '#fff', 
                fontWeight: 600, 
                cursor: 'pointer', 
                fontSize: 13,
                whiteSpace: 'nowrap',
                minWidth: 56,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
                transition: 'all 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              조회
            </button>
            {/* 변경 이력(감사 로그) 버튼 — 조회 오른쪽에 배치해 항상 보이게 */}
            <button
              type="button"
              onClick={handleAuditLogClick}
              style={{
                marginLeft: 12,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 500,
                color: '#1e40af',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: 6,
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.borderColor = '#93c5fd'; }}
              onMouseOut={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
            >
              이력
            </button>
            {/* 운송구간 운임 이력 ON/OFF: ON이면 클릭 시 이력 사이드바, OFF면 운송구간 편집 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 16, fontSize: 13, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={routeFreightHistoryMode}
                onChange={e => setRouteFreightHistoryMode(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>청구</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, fontSize: 13, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={vehicleFreightHistoryMode}
                onChange={e => setVehicleFreightHistoryMode(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>지불</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, fontSize: 13, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={selectionLockMode}
                onChange={e => {
                  const checked = e.target.checked;
                  setSelectionLockMode(checked);
                  if (!checked) {
                    debouncedSetSelectedRowsRef.cancel();
                    selectedRowsRef.current = new Set();
                    setSelectedRows(new Set());
                    const hot = hotRef.current?.hotInstance;
                    if (hot) {
                      if (typeof (hot as any).deselectCell === 'function') (hot as any).deselectCell();
                      (hot as any).render?.();
                    }
                  }
                }}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>선택고정</span>
            </label>
          </div>
          </div>

          {/* 오른쪽: 전체화면 — 고정 폭 확보로 날짜 카드(선택고정)와 겹침 방지 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexShrink: 0,
              minWidth: 100,
              paddingLeft: 8,
              marginLeft: 4,
            }}
          >
            <FullScreenButton
              isFullScreen={isFullScreen}
              setIsFullScreen={setIsFullScreen}
              position="relative"
            />
          </div>
        </div>

        {/* 변경 이력(감사 로그) 모달 */}
        {auditLogOpen && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              background: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24
            }}
            onClick={() => setAuditLogOpen(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                maxWidth: 720,
                width: '100%',
                maxHeight: '80vh',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>{auditLogTitle || '변경 이력'}</span>
                <button
                  type="button"
                  onClick={() => setAuditLogOpen(false)}
                  style={{ padding: '6px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
                >
                  닫기
                </button>
              </div>
              <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
                {auditLogLoading ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>불러오는 중...</div>
                ) : auditLogEntries.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>변경 이력이 없습니다.</div>
                ) : (
                  <>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '10px 8px', color: '#374151', whiteSpace: 'nowrap' }}>대상 일자</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', color: '#374151' }}>항목</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', color: '#374151' }}>변경 전</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', color: '#374151' }}>변경 후</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', color: '#374151' }}>수정자</th>
                        <th style={{ textAlign: 'left', padding: '10px 8px', color: '#374151' }}>수정 시각</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogEntries.map(entry => {
                        const colLabels: Record<string, string> = {
                          billing_amount: '청구운송비', payment_amount: '지불운송비', profit: '손익',
                          settlement_status: '정산상태', settlement_date: '정산일', company: '거래처상호',
                          vehicle_number: '차량번호', route: '운송구간', item_name: '품명', cargo: '차종',
                          department: '소속', contact: '연락처', driver_name: '이름', business_number: '사업자번호',
                          Receipt: '인수증', remarks: '비고', order: '순서', memo: '메모', date: '날짜',
                          distance: '거리', weight: '중량', customer_message: '고객메시지'
                        };
                        const label = colLabels[entry.columnName] || entry.columnName;
                        // 대상 일자: DB DATE가 UTC로 오면 2026-03-13T15:00:00.000Z 처럼 되어 KST에서 하루 전으로 보일 수 있음 → 로컬 날짜만 YYYY-MM-DD로 표시
                        const entityDateStr = entry.entityDate
                          ? (() => {
                              const s = String(entry.entityDate);
                              if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
                              const d = new Date(entry.entityDate);
                              return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                            })()
                          : '-';
                        // 수정 시각: 서버 기준 시각을 로컬 시간으로 표시
                        const changedAt = entry.changedAt ? new Date(entry.changedAt).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'medium' }) : '';
                        // 수정자: 이메일 → 이름 매핑 있으면 이름 표시, 없으면 @ 앞부분 또는 이메일
                        const modifierDisplay = entry.changedBy ? (emailToName[entry.changedBy] || entry.changedBy.split('@')[0] || entry.changedBy) : '-';
                        return (
                          <tr key={entry.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '10px 8px', color: '#111', fontWeight: 500, whiteSpace: 'nowrap' }}>{entityDateStr}</td>
                            <td style={{ padding: '10px 8px', color: '#374151' }}>{label}</td>
                            <td style={{ padding: '10px 8px', color: '#6b7280' }}>{entry.oldValue ?? '-'}</td>
                            <td style={{ padding: '10px 8px', color: '#111', fontWeight: 500 }}>{entry.newValue ?? '-'}</td>
                            <td style={{ padding: '10px 8px', color: '#374151' }}>{modifierDisplay}</td>
                            <td style={{ padding: '10px 8px', color: '#6b7280', whiteSpace: 'nowrap' }}>{changedAt}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {auditLogHasMore && (
                    <div style={{ textAlign: 'center', padding: '12px 0', borderTop: '1px solid #e5e7eb' }}>
                      <button
                        type="button"
                        onClick={handleAuditLogLoadMore}
                        disabled={auditLogLoadingMore}
                        style={{
                          padding: '8px 20px',
                          fontSize: 14,
                          fontWeight: 500,
                          color: '#1e40af',
                          background: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          borderRadius: 8,
                          cursor: auditLogLoadingMore ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {auditLogLoadingMore ? '불러오는 중...' : '더보기'}
                      </button>
                    </div>
                  )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 백업 복구 모달 */}
        {restoreModalOpen && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              background: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24
            }}
            onClick={() => !restoreLoading && setRestoreModalOpen(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                maxWidth: 480,
                width: '100%',
                overflow: 'hidden'
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>데이터 복구</span>
                <button
                  type="button"
                  disabled={restoreLoading}
                  onClick={() => setRestoreModalOpen(false)}
                  style={{ padding: '6px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: restoreLoading ? 'not-allowed' : 'pointer' }}
                >
                  닫기
                </button>
              </div>
              <div style={{ padding: 20 }}>
                <p style={{ margin: '0 0 12px', fontSize: 14, color: '#6b7280' }}>
                  복구할 백업 날짜를 선택하세요. 선택한 시점의 데이터로 전체가 덮어씌워집니다.
                </p>
                {backupListLoading ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>백업 목록 불러오는 중...</div>
                ) : backupList.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>사용 가능한 백업이 없습니다. (서버 BACKUP_DIR 확인)</div>
                ) : (
                  <>
                    <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12 }}>
                      {backupList.map(b => (
                        <label
                          key={b.filename}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            background: selectedBackup === b.filename ? '#fffbeb' : 'transparent',
                            borderBottom: '1px solid #f3f4f6'
                          }}
                        >
                          <input
                            type="radio"
                            name="restore-backup"
                            checked={selectedBackup === b.filename}
                            onChange={() => { setSelectedBackup(b.filename); setRestoreError(null); }}
                          />
                          <span style={{ marginLeft: 8, fontWeight: 500 }}>{b.date}</span>
                          <span style={{ marginLeft: 6, fontSize: 12, color: '#9ca3af' }}>{b.filename}</span>
                        </label>
                      ))}
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, color: '#6b7280' }}>
                        추가 인증키 (서버에 SETTLEMENT_RESTORE_SECRET이 설정된 경우에만 입력)
                      </label>
                      <input
                        type="password"
                        value={restoreSecret}
                        onChange={e => setRestoreSecret(e.target.value)}
                        placeholder="미설정 시 비워 두세요"
                        style={{ marginLeft: 8, padding: '4px 8px', width: 200 }}
                      />
                    </div>
                    {restoreError && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{restoreError}</div>}
                    <button
                      type="button"
                      disabled={restoreLoading || !selectedBackup}
                      onClick={handleRestore}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        fontSize: 15,
                        fontWeight: 600,
                        color: '#fff',
                        background: selectedBackup && !restoreLoading ? '#b45309' : '#d1d5db',
                        border: 'none',
                        borderRadius: 8,
                        cursor: selectedBackup && !restoreLoading ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {restoreLoading ? '복구 중...' : '선택한 날짜로 복구'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 송금(대량이체 준비) 편집 모달 */}
        {remittanceModalOpen && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10001,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16
            }}
            onClick={closeRemittanceModal}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                width: 'min(1920px, calc(100vw - 24px))',
                maxWidth: '100%',
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  padding: '14px 18px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexShrink: 0,
                  gap: 12
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    flexWrap: 'wrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <div>
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>송금 · 대량이체 편집</span>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                      정산일 {remittanceSummaryDate ? <strong style={{ color: '#111' }}>{remittanceSummaryDate}</strong> : '-'}
                      {' · '}
                      {remittanceDraftRows.length}건
                      {remittanceDetailRows.length > remittanceDraftRows.length
                        ? ` (정산 행 ${remittanceDetailRows.length}건 통합)`
                        : ''}
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      padding: '6px 10px',
                      borderRadius: 10,
                      border: '1px solid #e5e7eb',
                      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                      backdropFilter: 'blur(4px)',
                      minWidth: 120,
                      fontVariantNumeric: 'tabular-nums',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        color: '#6b7280',
                        fontWeight: 600,
                        letterSpacing: '-0.2px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      송금액 합계
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#111827', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                      <span style={{ color: '#b91c1c' }}>{remittanceAmountTotalSum.toLocaleString('ko-KR')}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginLeft: 2 }}>원</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={handleExportRemittanceExcels}
                    title="대량이체용·송금내역 엑셀 2개를 순서대로 저장합니다"
                    style={{
                      padding: '8px 16px',
                      fontSize: 14,
                      border: '1px solid #1976d2',
                      borderRadius: 8,
                      background: '#e3f2fd',
                      color: '#0d47a1',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    엑셀
                  </button>
                  <button
                    type="button"
                    onClick={closeRemittanceModal}
                    style={{
                      padding: '8px 16px',
                      fontSize: 14,
                      border: '1px solid #d1d5db',
                      borderRadius: 8,
                      background: '#fff',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    닫기
                  </button>
                </div>
              </div>
              <div style={{ padding: '0 12px 12px', overflow: 'auto', flex: 1 }}>
                <RemittanceEditTable
                  rows={remittanceDraftRows}
                  onRowsChange={setRemittanceDraftRows}
                  onPersistDriverBankAccount={persistDriverBankAccount}
                  onPersistDriverName={persistRemittanceDriverName}
                />
              </div>
            </div>
          </div>
        )}

        {/* 접속자 / 최근 편집 / 변경 알림 바 */}
        {(presenceUsers.length > 0 || lastEditInfo || pendingChangesCount > 0) && (
          <div style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
            padding: '5px 14px',
            background: '#fafbfc', borderRadius: 8,
            border: '1px solid #e5e7eb'
          }}>
            {presenceUsers.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '3px 10px', borderRadius: 5,
                background: '#f0fdf4', border: '1px solid #bbf7d0',
                fontSize: 12, color: '#166534'
              }}>
                <PeopleIcon sx={{ fontSize: 14, color: '#16a34a' }} />
                <span style={{ fontWeight: 600 }}>접속 중</span>
                {presenceUsers.map((u) => (
                  <span key={u.email} style={{
                    padding: '1px 6px', borderRadius: 4,
                    background: '#dcfce7', fontWeight: 500
                  }}>
                    {u.name}
                  </span>
                ))}
              </div>
            )}
            {lastEditInfo && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '3px 10px', borderRadius: 5,
                background: '#eff6ff', border: '1px solid #bfdbfe',
                fontSize: 12, color: '#1e40af'
              }}>
                <span style={{ fontWeight: 600 }}>최근 편집</span>
                <span style={{ fontWeight: 500 }}>{lastEditInfo.name}</span>
                <span style={{ color: '#6b7280' }}>
                  {(() => {
                    const d = new Date(lastEditInfo.timestamp);
                    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                  })()}
                </span>
              </div>
            )}
            {pendingChangesCount > 0 && (
              <button
                onClick={() => {
                  setPendingChangesCount(0);
                  fetchRows(dateRange.start, dateRange.end);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', borderRadius: 5,
                  background: '#fef3c7', border: '1px solid #fcd34d',
                  fontSize: 12, color: '#92400e', fontWeight: 600,
                  cursor: 'pointer', animation: 'pulse 2s infinite'
                }}
                title="다른 사용자의 변경사항을 새로고침합니다"
              >
                {pendingChangesCount}건 변경됨 · 새로고침
              </button>
            )}
          </div>
        )}

        {/* 2단: 필터들 (카드 형태) — 필터 버튼으로 표시/숨김 */}
        {filterSectionVisible && (
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: 12,
          padding: '20px',
          background: '#fff',
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          {/* 거래처 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <BusinessIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              거래처
            </label>
            <button
              onClick={() => openFilterPopup('company')}
              style={{
                width: 140,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.company.length === 0 
                  ? '전체' 
                  : filters.company.length === 1 
                    ? filters.company[0] 
                    : `${filters.company.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 날짜 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              날짜
            </label>
            <button
              onClick={() => openFilterPopup('date')}
              style={{
                width: 110,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.date.length === 0 
                  ? '전체' 
                  : filters.date.length === 1 
                    ? filters.date[0] 
                    : `${filters.date.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 차량번호 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <DirectionsCarIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              차량번호
            </label>
            <input 
              name="vehicleNumber" 
              value={filters.vehicleNumber} 
              onChange={handleFilterChange} 
              placeholder="차량번호 입력"
              style={{ 
                width: 130, 
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            />
          </div>

          {/* 운송구간 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <RouteIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              운송구간
            </label>
            <input 
              name="route" 
              value={filters.route} 
              onChange={handleFilterChange} 
              placeholder="운송구간 입력"
              style={{ 
                width: 140, 
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            />
          </div>

          {/* 품명 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <InventoryIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              품명
            </label>
            <button
              onClick={() => openFilterPopup('itemName')}
              style={{
                width: 120,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.itemName.length === 0 
                  ? '전체' 
                  : filters.itemName.length === 1 
                    ? filters.itemName[0] 
                    : `${filters.itemName.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 차종 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <LocalShippingIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              차종
            </label>
            <button
              onClick={() => openFilterPopup('cargo')}
              style={{
                width: 100,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.cargo.length === 0 
                  ? '전체' 
                  : filters.cargo.length === 1 
                    ? filters.cargo[0] 
                    : `${filters.cargo.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 소속 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <GroupsIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              소속
            </label>
            <button
              onClick={() => openFilterPopup('department')}
              style={{
                width: 120,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.department.length === 0 
                  ? '전체' 
                  : filters.department.length === 1 
                    ? filters.department[0] 
                    : `${filters.department.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 정산상태 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <PaymentIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              정산상태
            </label>
            <button
              onClick={() => openFilterPopup('settlementStatus')}
              style={{
                width: 110,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.settlementStatus.length === 0 
                  ? '전체' 
                  : filters.settlementStatus.length === 1 
                    ? filters.settlementStatus[0] 
                    : `${filters.settlementStatus.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 정산일 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              정산일
            </label>
            <button
              onClick={() => openFilterPopup('settlementDate')}
              style={{
                width: 110,
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            >
              <span>
                {filters.settlementDate.length === 0 
                  ? '전체' 
                  : filters.settlementDate.length === 1 
                    ? filters.settlementDate[0] 
                    : `${filters.settlementDate.length}개 선택`}
              </span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>▼</span>
            </button>
          </div>

          {/* 비고 필터 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              <NoteIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              비고
            </label>
            <input
              type="text"
              name="remarks"
              value={filters.remarks}
              onChange={handleFilterChange}
              placeholder="비고 검색"
              style={{ 
                width: 130, 
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                fontSize: 14,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
              }}
            />
          </div>

          {/* 필터 초기화 버튼 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, justifyContent: 'flex-end' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', opacity: 0 }}>⠀</label>
            <button
              onClick={handleResetFilters}
              title="필터 초기화"
              style={{ 
                width: 40, 
                height: 36, 
                fontSize: 16, 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                padding: 0,
                border: '1px solid #d1d5db',
                borderRadius: 6,
                background: '#fff',
                color: '#6b7280',
                transition: 'all 0.2s'
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              ↺
            </button>
          </div>
        </div>
        )}

      </div>
      {/* 합계 표시 영역 - 선택된 행 포함 */}
      <div style={{ width: '100%', position: 'relative', height: 48, margin: '1px 0 1px 0', top: '-8px' }}>
        {/* 모으기/검색 상태 표시 (왼쪽) - 풀기/검색해제 시 자동으로 사라짐 */}
        {(Object.keys(collectedFilters).length > 0 || Object.keys(hiddenFilters).length > 0 || (searchFilter?.query?.trim() ?? '')) ? (
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            paddingLeft: 12,
            fontSize: 15,
            color: '#555',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            maxWidth: 'calc(110px + 160px + 120px + 180px)',
            boxSizing: 'border-box',
          }}>
            {Object.keys(collectedFilters).length > 0 && (
              <span style={{ whiteSpace: 'nowrap' }}>
                모으기 - {Object.keys(collectedFilters)
                  .map(f => {
                    const header = COLUMN_HEADERS[COLUMN_FIELDS.indexOf(f)] ?? f;
                    const m = collectedFilters[f].mode === 'partial' ? '⊃' : '=';
                    return `${header}${m}`;
                  })
                  .join(' / ')}
              </span>
            )}
            {Object.keys(hiddenFilters).length > 0 && (
              <span style={{ whiteSpace: 'nowrap' }}>
                감추기 - {Object.keys(hiddenFilters)
                  .map(f => {
                    const header = COLUMN_HEADERS[COLUMN_FIELDS.indexOf(f)] ?? f;
                    return `${header}=`;
                  })
                  .join(' / ')}
              </span>
            )}
            {(Object.keys(collectedFilters).length > 0 || Object.keys(hiddenFilters).length > 0) && searchFilter?.query?.trim() && <span style={{ color: '#ccc' }}>|</span>}
            {searchFilter?.query?.trim() && (
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                검색 - {(COLUMN_HEADERS[COLUMN_FIELDS.indexOf(searchFilter.field)] ?? searchFilter.field)}: {searchFilter.query.trim()}
              </span>
            )}
          </div>
        ) : null}
        {/* 상단 요약: 모드(읽기/입력) + 카드형 — 모드는 선택·청구 카드 바로 앞 */}
        <div
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 10,
            flexWrap: 'wrap',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'nowrap',
              fontSize: 13,
              whiteSpace: 'nowrap',
              padding: '6px 14px',
              minWidth: 200,
              borderRadius: 10,
              border: '1px solid #cbd5e1',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              flexShrink: 0,
            }}
          >
            <span style={{ color: '#64748b', fontWeight: 600 }}>모드</span>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                cursor: 'pointer',
                color: settlementViewMode === 'read' ? '#1976d2' : '#6b7280',
              }}
            >
              <input
                type="radio"
                name="settlementViewMode"
                checked={settlementViewMode === 'read'}
                onChange={() => setSettlementViewMode('read')}
                style={{ margin: 0 }}
              />
              읽기
            </label>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                cursor: 'pointer',
                color: settlementViewMode === 'input' ? '#1976d2' : '#6b7280',
              }}
            >
              <input
                type="radio"
                name="settlementViewMode"
                checked={settlementViewMode === 'input'}
                onChange={() => {
                  if (settlementViewMode === 'input') return;
                  setSettlementPasswordModalOpen(true);
                  setSettlementPasswordInput('');
                  setSettlementPasswordError('');
                }}
                style={{ margin: 0 }}
              />
              입력
            </label>
          </div>
          {(() => {
            const summaryCardBase: React.CSSProperties = {
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '6px 10px',
              borderRadius: 10,
              border: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              backdropFilter: 'blur(4px)',
              minWidth: 120,
              fontVariantNumeric: 'tabular-nums',
            };
            const selectionCardBase: React.CSSProperties = {
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '6px 10px',
              borderRadius: 10,
              border: '1px solid #e5e7eb',
              background: '#ffffffcc',
              backdropFilter: 'blur(4px)',
              minWidth: 240,
              fontVariantNumeric: 'tabular-nums',
            };
            const labelStyle: React.CSSProperties = {
              fontSize: 12,
              color: '#6b7280',
              fontWeight: 600,
              letterSpacing: '-0.2px',
              whiteSpace: 'nowrap',
            };
            const valueStyle: React.CSSProperties = {
              fontSize: 16,
              fontWeight: 600,
              color: '#111827',
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
            };
            const sumSuffixStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#6b7280', marginLeft: 2 };

            const profitColor = profitSum >= 0 ? '#d32f2f' : '#1976d2';
            const selectedProfitColor = selectedProfitSum >= 0 ? '#d32f2f' : '#1976d2';
            const hasSelection = selectedDataCount > 0;

            return (
              <>
                {/* 선택 요약 (먼저 표시) */}
                <div
                  style={{
                    ...selectionCardBase,
                    borderColor: hasSelection ? '#90caf9' : '#e5e7eb',
                    // Handsontable 기본 선택 영역(연한 파란색)과 톤을 맞춤
                    background: hasSelection ? '#d9edf7' : '#ffffffcc',
                    boxShadow: hasSelection ? '0 6px 16px rgba(25,118,210,0.18)' : 'none',
                    opacity: hasSelection ? 1 : 0.65,
                    marginRight: 12,
                  }}
                >
                  <div style={{ ...labelStyle, color: hasSelection ? '#3730a3' : '#6b7280' }}>
                    선택 {selectedDataCount.toLocaleString()}건
                  </div>
                  <div style={{ ...valueStyle, fontSize: 14, fontWeight: 600 }}>
                    <span style={{ color: '#b91c1c' }}>청구 {selectedBillingSum.toLocaleString()}</span>
                    <span style={{ color: '#9ca3af', margin: '0 8px' }}>|</span>
                    <span style={{ color: '#1d4ed8' }}>지불 {selectedPaymentSum.toLocaleString()}</span>
                    <span style={{ color: '#9ca3af', margin: '0 8px' }}>|</span>
                    <span style={{ color: selectedProfitColor }}>손익 {selectedProfitSum.toLocaleString()}</span>
                  </div>
                </div>

                <div style={summaryCardBase}>
                  <div style={labelStyle}>청구</div>
                  <div style={valueStyle}>
                    <span style={{ color: '#b91c1c' }}>{billingSum.toLocaleString()}</span>
                    <span style={sumSuffixStyle}>원</span>
                  </div>
                </div>
                <div style={summaryCardBase}>
                  <div style={labelStyle}>지불</div>
                  <div style={valueStyle}>
                    <span style={{ color: '#1d4ed8' }}>{paymentSum.toLocaleString()}</span>
                    <span style={sumSuffixStyle}>원</span>
                  </div>
                </div>
                <div style={summaryCardBase}>
                  <div style={labelStyle}>손익</div>
                  <div style={valueStyle}>
                    <span style={{ color: profitColor }}>{profitSum.toLocaleString()}</span>
                    <span style={sumSuffixStyle}>원</span>
                  </div>
                </div>
                <div style={{ ...summaryCardBase, minWidth: 110 }}>
                  <div style={labelStyle}>건수</div>
                  <div style={valueStyle}>{dataCount.toLocaleString()}건</div>
                </div>
              </>
            );
          })()}
        </div>
      </div>
      {/* 표 */}
      <div ref={settlementTableAreaRef} style={{ flex: 1, minHeight: 0, position: 'relative', width: '100%' }}>
        {displayedRows.length === 0 ? (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fafafa',
            color: '#666',
            fontSize: 16,
            gap: 12,
          }}>
            <div style={{ fontWeight: 600 }}>검색 결과가 없습니다</div>
            {(Object.keys(collectedFilters).length > 0 || (searchFilter?.query?.trim() ?? '')) ? (
              <button
                type="button"
                onClick={() => {
                  setCollectedFilters({});
                  setSearchFilter(null);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: 14,
                  color: '#1976d2',
                  background: '#fff',
                  border: '1px solid #1976d2',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                검색·모으기 해제
              </button>
            ) : null}
          </div>
        ) : (
        <HotTable
          ref={hotRef}
          key={`settlement-grid-${settlementViewMode}`}
          data={tableData}
          colHeaders={COLUMN_HEADERS}
          rowHeaders={true}
          width="100%"
          height={settlementHotHeight}
          colWidths={colWidths}
          manualRowResize={true}
          manualColumnResize={true}
          autoColumnSize={false}
          autoRowSize={false}
          className="htCenter"
          licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
          afterChange={handleAfterChange}
          beforeKeyDown={(event: KeyboardEvent) => {
            if (settlementViewMode === 'read') return;

            const hot = hotRef.current?.hotInstance;
            const sel = hot?.getSelected();

            if ((event.key === ' ' || event.code === 'Space') && !event.repeat) {
              if (sel && sel.length > 0) {
                const [r1, c1, r2, c2] = sel[0];
                const row = Math.min(r1, r2);
                const col = Math.min(c1, c2);
                if (
                  row >= 0 &&
                  row < displayedRows.length &&
                  (col === SETTLEMENT_STATUS_COL || col === RECEIPT_COL)
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                  void handleCellClick(row, col);
                  return false;
                }
              }
            }

            if (event.key === 'ArrowDown') {
              if (sel && sel.length > 0) {
                const row = sel[0][0];
                if (displayedRows.length > 0 && row === displayedRows.length - 1) {
                  event.preventDefault();
                  event.stopPropagation();
                  addRowsBelowDisplayedIndex(row, 1);
                  pendingSelectRowIndexRef.current = displayedRows.length;
                }
              }
            }
          }}
          afterCreateRow={() => {}}
          afterRemoveRow={() => {}}
          contextMenu={{
            items: {
              modici_collect: {
                name: '모으기 (완전 일치)',
                callback: function(_key: string, selection: any) {
                  const sel = Array.isArray(selection) && selection[0] ? selection[0] : selection;
                  const row = sel?.start?.row ?? 0;
                  const col = sel?.start?.col ?? 0;
                  const rows = collectedRowsRef.current;
                  if (row >= 0 && row < rows.length && col >= 0 && col < COLUMN_FIELDS.length) {
                    const field = COLUMN_FIELDS[col];
                    const value = String(rows[row][field] ?? '').trim();
                    if (value) setCollectedFilters(prev => ({ ...prev, [field]: { value, mode: 'exact' } }));
                  }
                }
              },
              modici_collect_partial: {
                name: '모으기 (부분 일치)',
                callback: function(_key: string, selection: any) {
                  const sel = Array.isArray(selection) && selection[0] ? selection[0] : selection;
                  const row = sel?.start?.row ?? 0;
                  const col = sel?.start?.col ?? 0;
                  const rows = collectedRowsRef.current;
                  if (row >= 0 && row < rows.length && col >= 0 && col < COLUMN_FIELDS.length) {
                    const field = COLUMN_FIELDS[col];
                    const value = String(rows[row][field] ?? '').trim();
                    if (value) setCollectedFilters(prev => ({ ...prev, [field]: { value, mode: 'partial' } }));
                  }
                }
              },
              modici_hide: {
                name: '감추기',
                callback: function(_key: string, selection: any) {
                  const sel = Array.isArray(selection) && selection[0] ? selection[0] : selection;
                  const row = sel?.start?.row ?? 0;
                  const col = sel?.start?.col ?? 0;
                  const rows = collectedRowsRef.current;
                  if (row >= 0 && row < rows.length && col >= 0 && col < COLUMN_FIELDS.length) {
                    const field = COLUMN_FIELDS[col];
                    const value = String(rows[row][field] ?? '').trim();
                    if (value) {
                      setHiddenFilters(prev => ({ ...prev, [field]: { value } }));
                    }
                  }
                }
              },
              modici_release: {
                name: '풀기',
                callback: function() { setShowReleaseModal(true); }
              },
              modici_release_all: {
                name: '모두풀기',
                disabled: () => Object.keys(collectedFilters).length === 0 && Object.keys(hiddenFilters).length === 0,
                callback: function() { 
                  setCollectedFilters({}); 
                  setHiddenFilters({});
                }
              },
              sep1: { name: '────────', separator: true },
              modici_search: {
                name: '검색',
                callback: function(_key: string, selection: any) {
                  const sel = Array.isArray(selection) && selection[0] ? selection[0] : selection;
                  const col = sel?.start?.col ?? 0;
                  if (col >= 0 && col < COLUMN_FIELDS.length) {
                    setSearchModalField(COLUMN_FIELDS[col]);
                    setSearchModalQuery(searchFilter?.field === COLUMN_FIELDS[col] ? searchFilter.query : '');
                    setShowSearchModal(true);
                  }
                }
              },
              modici_search_clear: {
                name: '검색해제',
                disabled: () => !searchFilter,
                callback: function() { setSearchFilter(null); }
              },
              sep2: { name: '────────', separator: true },
              modici_add_rows: {
                name: '행 추가',
                disabled: () => settlementViewMode === 'read',
                callback: function(_key: string, selection: any) {
                  const sel = Array.isArray(selection) && selection[0] ? selection[0] : selection;
                  const row = sel?.start?.row ?? 0;
                  const rows = collectedRowsRef.current;
                  if (row >= 0 && row < rows.length) {
                    setAddRowsContextRowIndex(row);
                    setAddRowsCountInput('');
                    setShowAddRowsModal(true);
                  }
                }
              },
              modici_delete_row: {
                name: '삭제',
                disabled: () => settlementViewMode === 'read',
                callback: function(_key: string, selection: any) {
                  const hot = hotRef.current?.hotInstance;
                  const rows = collectedRowsRef.current;
                  let targetRowIndexes: number[] = [];
                  if (selectionLockMode && selectedRowsRef.current.size > 0) {
                    targetRowIndexes = Array.from(selectedRowsRef.current).filter(row => row >= 0 && row < rows.length);
                  } else {
                    const currentSelection = hot ? hot.getSelected() || [] : [];
                    if (currentSelection.length > 0) {
                      for (const r of currentSelection) {
                        const [r1, , r2] = r;
                        const start = Math.min(r1, r2);
                        const end = Math.max(r1, r2);
                        for (let row = start; row <= end; row++) {
                          if (row >= 0 && row < rows.length) targetRowIndexes.push(row);
                        }
                      }
                    } else {
                      const sel = Array.isArray(selection) && selection[0] ? selection[0] : selection;
                      const row = sel?.start?.row ?? 0;
                      if (row >= 0 && row < rows.length) targetRowIndexes.push(row);
                    }
                  }
                  const targets = targetRowIndexes
                    .map(idx => rows[idx])
                    .filter((t: any) => t && t.id);
                  if (!targets.length) return;
                  setDeleteModalTargets(targets);
                  setDeleteModalStep(1);
                  setShowDeleteModal(true);
                }
              },
              sep3: { name: '────────', separator: true },
              modici_settlement_process: {
                name: '정산처리',
                disabled: () => settlementViewMode === 'read',
                callback: function() {
                  handleBulkSettleVisibleRows();
                }
              }
            }
          }}
          readOnly={settlementViewMode === 'read'}
          {...({ observeChanges: true } as any)}
          columns={COLUMN_HEADERS.map((header, idx) => {
            /** 열별 readOnly가 인스턴스 readOnly보다 우선하므로, 읽기 모드에서는 열에서도 전부 잠금 */
            const ro = settlementViewMode === 'read';
            if (header === '손익') {
              return {
                readOnly: true,
              } as any;
            }
            if (header === '거래처상호') {
              return {
                type: 'autocomplete',
                strict: false,
                allowInvalid: true,
                filter: false,
                visibleRows: 8,
                trimDropdown: true,
                source: companyAutocompleteSource as any,
                readOnly: ro,
              } as any;
            }
            if (header === '정산상태') {
              return {
                renderer: function(instance, td, row, col, prop, value, cellProperties) {
                  td.innerHTML = value === '정산'
                    ? '<span style="font-size:18px;">✅</span> <span style="font-weight:700;color:#1976d2;">정산</span>'
                    : '<span style="font-size:18px;">❌</span> <span style="font-weight:700;color:#d32f2f;">미정산</span>';
                  td.style.textAlign = 'center';
                  td.title = '셀 선택 후 스페이스바 — 정산 ↔ 미정산';
                },
                readOnly: true
              };
            }
            if (header === '정산일') {
              return {
                type: 'date',
                dateFormat: 'YYYY-MM-DD',
                correctFormat: true,
                allowInvalid: false,
                readOnly: ro,
                defaultDate: new Date().toISOString().slice(0, 10),
              };
            }
            if (header === '인수증') {
              return {
                renderer: function(instance, td, row, col, prop, value, cellProperties) {
                  const v = value || '미수취';
                  td.innerHTML = v === '수취'
                    ? '<span style="font-size:18px;">✅</span> <span style="font-weight:700;color:#1976d2;">수취</span>'
                    : '<span style="font-size:18px;">❌</span> <span style="font-weight:700;color:#d32f2f;">미수취</span>';
                  td.style.textAlign = 'center';
                  td.title = '셀 선택 후 스페이스바 — 수취 ↔ 미수취';
                },
                readOnly: true
              };
            }
            if (header === '지불운송비') {
              return { readOnly: ro };
            }
            if (header === '운송구간') {
              if (routeFreightHistoryMode || ro) return { readOnly: true };
              return {
                type: 'autocomplete',
                strict: false,
                allowInvalid: true,
                filter: false,
                visibleRows: 8,
                trimDropdown: true,
                source: routeAutocompleteSource as any,
                readOnly: ro,
              } as any;
            }
            if (vehicleFreightHistoryMode && header === '차량번호') {
              return { readOnly: true };
            }
            return { readOnly: ro };
          })}
          beforeOnCellMouseDown={(e, coords) => {
            if (e.button !== 0 || !coords || coords.row < 0 || coords.col < 0) {
              cellMouseDownRef.current = null;
              return;
            }
            cellMouseDownRef.current = {
              row: coords.row,
              col: coords.col,
              x: e.clientX,
              y: e.clientY,
              shift: e.shiftKey,
              ctrl: !!(e.ctrlKey || e.metaKey),
            };
            // 마지막 행으로 스크롤된 직후 등, 보이지 않는 앵커가 남아 범위 선택되는 것 방지
            if (
              !selectionLockMode &&
              !e.shiftKey &&
              !e.ctrlKey &&
              !e.metaKey
            ) {
              const hot = hotRef.current?.hotInstance;
              if (hot && typeof (hot as any).deselectCell === 'function') {
                (hot as any).deselectCell();
              }
            }
          }}
          afterOnCellMouseDown={(e, coords) => {
            if (e.button === 2) return;
            if (coords.row >= 0) {
              if (routeFreightHistoryMode && coords.col === 3) {
                handleRouteCellClick(coords.row);
                return;
              }
              if (vehicleFreightHistoryMode && coords.col === 2) {
                handleVehicleCellClick2(coords.row);
                return;
              }
              // 정산상태·인수증: 클릭은 선택만 — 값 변경은 스페이스바만
              const col = coords.col;
              if (col === SETTLEMENT_STATUS_COL || col === RECEIPT_COL) {
                return;
              }
            }
          }}
          afterOnCellMouseUp={(e, coords) => {
            const hot = hotRef.current?.hotInstance;
            const down = cellMouseDownRef.current;
            cellMouseDownRef.current = null;
            if (!hot || !down || e.button !== 0) return;
            if (selectionLockMode) return;
            if (e.shiftKey || e.ctrlKey || e.metaKey || down.shift || down.ctrl) return;
            if (!coords || coords.row < 0 || coords.col < 0) return;

            const dx = Math.abs(e.clientX - down.x);
            const dy = Math.abs(e.clientY - down.y);
            const CLICK_MOVE_PX = 8;
            if (dx >= CLICK_MOVE_PX || dy >= CLICK_MOVE_PX) return;

            const sel = hot.getSelected();
            if (!sel || sel.length === 0) return;
            const [r1, c1, r2, c2] = sel[0];
            const rowMin = Math.min(r1, r2);
            const rowMax = Math.max(r1, r2);
            if (rowMin === rowMax) return;

            hot.selectCell(coords.row, coords.col);
          }}
          afterSelection={() => {
            const hot = hotRef.current?.hotInstance;
            if (!hot) return;
            const ranges = hot.getSelected() || [];
            const newRowsSet = new Set<number>();
            for (const r of ranges) {
              const [r1, , r2] = r;
              const start = Math.min(r1, r2);
              const end = Math.max(r1, r2);
              for (let row = start; row <= end; row++) {
                if (row >= 0 && row < displayedRows.length) newRowsSet.add(row);
              }
            }
            const rowsSet = selectionLockMode
              ? new Set([...selectedRowsRef.current, ...newRowsSet])
              : newRowsSet;
            selectedRowsRef.current = rowsSet;
            hot.render();
            debouncedSetSelectedRowsRef(rowsSet);
          }}
          afterDeselect={() => {
            if (selectionLockMode) return;
            debouncedSetSelectedRowsRef.cancel();
            selectedRowsRef.current = new Set();
            setSelectedRows(new Set());
          }}
          cells={cells}
        />
        )}
      </div>

      <RouteHistorySidebar
        open={routeHistoryOpen}
        onClose={() => setRouteHistoryOpen(false)}
        data={routeHistoryData.recentHistory || []}
        avg={routeHistoryData.avg}
        mode={routeHistoryData.mode}
        min={routeHistoryData.min}
        max={routeHistoryData.max}
        count={routeHistoryData.count}
        lastUsed={routeHistoryData.lastUsed}
        onLoadMore={handleRouteHistoryLoadMore}
        loading={routeHistoryLoading}
        hasMore={routeHistoryHasMore}
      />

      <VehicleHistorySidebar
        open={vehicleHistoryOpen}
        onClose={() => setVehicleHistoryOpen(false)}
        data={vehicleHistoryData.recentHistory || []}
        avg={vehicleHistoryData.avg}
        mode={vehicleHistoryData.mode}
        min={vehicleHistoryData.min}
        max={vehicleHistoryData.max}
        count={vehicleHistoryData.count}
        lastUsed={vehicleHistoryData.lastUsed}
        onLoadMore={handleVehicleHistoryLoadMore}
        loading={vehicleHistoryLoading}
        vehicleNumber={vehicleHistoryVehicleNumber}
        hasMore={vehicleHistoryHasMore}
      />
      
      {/* 저장 실패 시 우측 하단에 에러 메시지 토스트 표시 */}
      {Object.entries(saveStatus).map(([rowId, status]) => (
        status.status === 'error' && (
          <div
            key={rowId}
            style={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              background: '#d32f2f',
              color: 'white',
              padding: '12px 24px',
              borderRadius: 4,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              zIndex: 1000,
              animation: 'fadeIn 0.3s ease-in-out'
            }}
          >
            {status.message}
          </div>
        )
      ))}

      {/* 풀기 모달: 모은 항목 체크 해제 후 완료 시 적용 */}
      {showReleaseModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.4)',
          zIndex: 2500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} onClick={() => setShowReleaseModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            minWidth: 360,
            maxWidth: 480,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: 16, fontWeight: 700, fontSize: 18, color: '#1976d2' }}>모은/감춘 항목 풀기</div>
            <p style={{ marginBottom: 16, fontSize: 13, color: '#555' }}>
              해제할 항목의 체크를 해제한 뒤 완료하세요. 전부 해제 후 완료하면 모든 모은/감춘 데이터가 풀립니다.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {Object.keys(collectedFilters).length === 0 && Object.keys(hiddenFilters).length === 0 ? (
                <div style={{ color: '#888', fontSize: 14 }}>모은/감춘 항목이 없습니다.</div>
              ) : (
                <>
                  {Object.entries(collectedFilters).map(([field, filter]) => {
                    const header = COLUMN_HEADERS[COLUMN_FIELDS.indexOf(field)] ?? field;
                    const modeLabel = filter.mode === 'partial' ? '부분 일치' : '완전 일치';
                    return (
                      <label key={`collect-${field}`} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={releaseModalChecked[field] !== false}
                          onChange={e => setReleaseModalChecked(prev => ({ ...prev, [field]: e.target.checked }))}
                        />
                        <span style={{ fontWeight: 500 }}>{header}</span>
                        <span style={{ color: '#666' }}>: {filter.value}</span>
                        <span style={{ color: '#999', fontSize: 12 }}>({modeLabel})</span>
                      </label>
                    );
                  })}
                  {Object.keys(hiddenFilters).length > 0 && Object.keys(collectedFilters).length > 0 && (
                    <div style={{ height: 1, background: '#eee', margin: '6px 0' }} />
                  )}
                  {Object.entries(hiddenFilters).map(([field, filter]) => {
                    const header = COLUMN_HEADERS[COLUMN_FIELDS.indexOf(field)] ?? field;
                    return (
                      <label key={`hide-${field}`} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={releaseModalChecked[field] !== false}
                          onChange={e => setReleaseModalChecked(prev => ({ ...prev, [field]: e.target.checked }))}
                        />
                        <span style={{ fontWeight: 500 }}>{header}</span>
                        <span style={{ color: '#666' }}>: {filter.value}</span>
                        <span style={{ color: '#999', fontSize: 12 }}>(감추기)</span>
                      </label>
                    );
                  })}
                </>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setShowReleaseModal(false)}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 14 }}
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  const nextCollected: Record<string, { value: string; mode: 'exact' | 'partial' }> = {};
                  const nextHidden: Record<string, { value: string }> = {};
                  Object.entries(releaseModalChecked).forEach(([k, checked]) => {
                    if (checked && collectedFilters[k] != null) nextCollected[k] = collectedFilters[k];
                    if (checked && hiddenFilters[k] != null) nextHidden[k] = hiddenFilters[k];
                  });
                  setCollectedFilters(nextCollected);
                  setHiddenFilters(nextHidden);
                  setShowReleaseModal(false);
                }}
                style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
              >
                완료
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 검색 팝업: 우클릭 → 검색 선택 시 해당 컬럼에서 부분 일치 검색 */}
      {showSearchModal && searchModalField && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.4)',
          zIndex: 2500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} onClick={() => setShowSearchModal(false)}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            minWidth: 360,
            maxWidth: 420,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 18, color: '#1976d2' }}>
              {COLUMN_HEADERS[COLUMN_FIELDS.indexOf(searchModalField)] ?? searchModalField} 검색
            </div>
            <p style={{ marginBottom: 16, fontSize: 13, color: '#555' }}>
              검색어가 포함된 행만 표시됩니다. (예: 오뚜기 → 오뚜기, 오뚜기농협물류 등)
            </p>
            <input
              type="text"
              value={searchModalQuery}
              onChange={e => setSearchModalQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  setSearchFilter(searchModalQuery.trim() ? { field: searchModalField, query: searchModalQuery.trim() } : null);
                  setShowSearchModal(false);
                }
              }}
              placeholder="검색어 입력"
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 15,
                marginBottom: 20,
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowSearchModal(false)}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 14 }}
              >
                취소
              </button>
              {searchFilter?.field === searchModalField && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchFilter(null);
                    setSearchModalQuery('');
                    setShowSearchModal(false);
                  }}
                  style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #d32f2f', background: '#fff', color: '#d32f2f', cursor: 'pointer', fontSize: 14 }}
                >
                  검색 해제
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setSearchFilter(searchModalQuery.trim() ? { field: searchModalField, query: searchModalQuery.trim() } : null);
                  setShowSearchModal(false);
                }}
                style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
              >
                검색
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 행 추가 개수 입력 팝업: 우클릭 → 행 추가 선택 시 */}
      {showAddRowsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.4)',
          zIndex: 2500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} onClick={() => { setShowAddRowsModal(false); setAddRowsCountInput(''); }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            minWidth: 320,
            maxWidth: 400,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 18, color: '#1976d2' }}>
              행 추가
            </div>
            <p style={{ marginBottom: 12, fontSize: 14, color: '#333' }}>
              선택한 행 바로 아래에 추가할 행 개수를 입력하세요. (1~100)
            </p>
            <input
              type="number"
              min={1}
              max={100}
              value={addRowsCountInput}
              onChange={e => {
                const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 3);
                setAddRowsCountInput(onlyDigits);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') handleAddRowsConfirm();
              }}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: 8,
                fontSize: 15,
                marginBottom: 20,
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => { setShowAddRowsModal(false); setAddRowsCountInput(''); }}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 14 }}
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleAddRowsConfirm}
                style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 행 삭제 확인 팝업: 우클릭 → 행 삭제 선택 시 (2단계 확인) */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.4)',
          zIndex: 2600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} onClick={() => { setShowDeleteModal(false); setDeleteModalStep(1); }}>
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              minWidth: 360,
              maxWidth: 440,
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 18, color: '#d32f2f' }}>
              행 삭제
            </div>
            {deleteModalStep === 1 ? (
              <>
                <p style={{ marginBottom: 12, fontSize: 14, color: '#333', lineHeight: 1.5 }}>
                  선택된 <strong>{deleteModalTargets.length}</strong>개의 데이터를 모두 삭제하시겠습니까?
                </p>
                <p style={{ marginBottom: 20, fontSize: 13, color: '#666' }}>
                  계속 진행하면 두 번째 단계에서 한 번 더 확인합니다.
                </p>
              </>
            ) : (
              <>
                <p style={{ marginBottom: 12, fontSize: 14, color: '#d32f2f', lineHeight: 1.5 }}>
                  삭제 후에는 복구가 불가능합니다.
                </p>
                <p style={{ marginBottom: 20, fontSize: 13, color: '#333' }}>
                  정말로 선택된 <strong>{deleteModalTargets.length}</strong>개의 데이터를 삭제하시겠습니까?
                </p>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => { setShowDeleteModal(false); setDeleteModalStep(1); }}
                style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 14 }}
              >
                취소
              </button>
              {deleteModalStep === 1 ? (
                <button
                  type="button"
                  onClick={() => setDeleteModalStep(2)}
                  style={{ padding: '8px 20px', borderRadius: 6, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
                >
                  계속
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleteConfirmLoading}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 6,
                    border: 'none',
                    background: deleteConfirmLoading ? '#e57373' : '#d32f2f',
                    color: '#fff',
                    cursor: deleteConfirmLoading ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    fontSize: 14,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 100,
                    justifyContent: 'center'
                  }}
                >
                  {deleteConfirmLoading ? (
                    <>
                      <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.5)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                      삭제 중...
                    </>
                  ) : (
                    '삭제'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

            {/* 필터 팝업들 */}
      {filterPopups.company && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 450,
            maxWidth: 600,
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>거래처 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.company}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, company: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.company.length === getFilteredOptions('company').length && getFilteredOptions('company').length > 0}
                onChange={() => handleSelectAll('company', getFilteredOptions('company'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 - 길어질 수 있으므로 스크롤 영역으로 분리 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('company').map(company => (
                <label key={company} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.company.includes(company)}
                    onChange={() => handleTempSelectionChange('company', company)}
                    style={{ margin: 0 }}
                  />
                  <span>{company}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('company')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('company')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 날짜 필터 팝업 */}
      {filterPopups.date && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 450,
            maxWidth: 600,
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>날짜 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.date}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, date: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.date.length === getFilteredOptions('date').length && getFilteredOptions('date').length > 0}
                onChange={() => handleSelectAll('date', getFilteredOptions('date'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('date').map(date => (
                <label key={date} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.date.includes(date)}
                    onChange={() => handleTempSelectionChange('date', date)}
                    style={{ margin: 0 }}
                  />
                  <span>{date}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('date')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('date')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 품명 필터 팝업 */}
      {filterPopups.itemName && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 450,
            maxWidth: 600,
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>품명 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.itemName}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, itemName: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.itemName.length === getFilteredOptions('itemName').length && getFilteredOptions('itemName').length > 0}
                onChange={() => handleSelectAll('itemName', getFilteredOptions('itemName'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('itemName').map(itemName => (
                <label key={itemName} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.itemName.includes(itemName)}
                    onChange={() => handleTempSelectionChange('itemName', itemName)}
                    style={{ margin: 0 }}
                  />
                  <span>{itemName}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('itemName')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('itemName')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 차종 필터 팝업 */}
      {filterPopups.cargo && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 450,
            maxWidth: 600,
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>차종 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.cargo}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, cargo: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.cargo.length === getFilteredOptions('cargo').length && getFilteredOptions('cargo').length > 0}
                onChange={() => handleSelectAll('cargo', getFilteredOptions('cargo'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('cargo').map(cargo => (
                <label key={cargo} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.cargo.includes(cargo)}
                    onChange={() => handleTempSelectionChange('cargo', cargo)}
                    style={{ margin: 0 }}
                  />
                  <span>{cargo}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('cargo')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('cargo')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 소속 필터 팝업 */}
      {filterPopups.department && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 450,
            maxWidth: 600,
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>소속 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.department}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, department: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.department.length === getFilteredOptions('department').length && getFilteredOptions('department').length > 0}
                onChange={() => handleSelectAll('department', getFilteredOptions('department'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('department').map(department => (
                <label key={department} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.department.includes(department)}
                    onChange={() => handleTempSelectionChange('department', department)}
                    style={{ margin: 0 }}
                  />
                  <span>{department}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('department')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('department')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 정산상태 필터 팝업 */}
      {filterPopups.settlementStatus && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 450,
            maxWidth: 600,
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>정산상태 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.settlementStatus}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, settlementStatus: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.settlementStatus.length === getFilteredOptions('settlementStatus').length && getFilteredOptions('settlementStatus').length > 0}
                onChange={() => handleSelectAll('settlementStatus', getFilteredOptions('settlementStatus'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('settlementStatus').map(status => (
                <label key={status} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.settlementStatus.includes(status)}
                    onChange={() => handleTempSelectionChange('settlementStatus', status)}
                    style={{ margin: 0 }}
                  />
                  <span>{status}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('settlementStatus')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('settlementStatus')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 정산일 필터 팝업 */}
      {filterPopups.settlementDate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            minWidth: 450,
            maxWidth: 600,
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            marginTop: 100
          }}>
            <div style={{ marginBottom: 16, fontWeight: 600, fontSize: 16 }}>정산일 필터</div>
            
            {/* 검색 */}
            <input
              type="text"
              placeholder="검색..."
              value={filterSearchTerms.settlementDate}
              onChange={(e) => setFilterSearchTerms(prev => ({ ...prev, settlementDate: e.target.value }))}
              style={{
                width: 'calc(100% - 20px)',
                padding: '6px 10px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
                marginBottom: 16,
                fontSize: '14px'
              }}
            />
            
            {/* 전체 선택 */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
              <input
                type="checkbox"
                checked={tempSelections.settlementDate.length === getFilteredOptions('settlementDate').length && getFilteredOptions('settlementDate').length > 0}
                onChange={() => handleSelectAll('settlementDate', getFilteredOptions('settlementDate'))}
                style={{ margin: 0 }}
              />
              <span style={{ fontWeight: 600 }}>(모두 선택)</span>
            </label>
            
            {/* 개별 항목들 */}
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
              {getFilteredOptions('settlementDate').map(date => (
                <label key={date} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={tempSelections.settlementDate.includes(date)}
                    onChange={() => handleTempSelectionChange('settlementDate', date)}
                    style={{ margin: 0 }}
                  />
                  <span>{date}</span>
                </label>
              ))}
            </div>
            
            {/* 버튼들 */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button
                onClick={() => applyFilter('settlementDate')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1976d2',
                  borderRadius: 4,
                  background: '#1976d2',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                확인
              </button>
              <button
                onClick={() => cancelFilter('settlementDate')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  background: '#fff',
                  color: '#333',
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 명세서 사이드바 */}
      {isStatementSidebarOpen && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          right: isStatementSidebarCollapsed ? '-360px' : 0, 
          width: '400px', 
          height: '100vh', 
          background: '#fff', 
          boxShadow: '-2px 0 8px rgba(0,0,0,0.15)', 
          zIndex: 10000,
          overflowY: 'auto',
          borderLeft: '1px solid #e0e0e0',
          transition: 'right 0.3s ease'
        }}>
          {/* 숨겨진 상태에서 꺼내기 버튼 */}
          {isStatementSidebarCollapsed && (
            <div style={{
              position: 'absolute',
              left: '0px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '40px',
              height: '80px',
              background: '#1976d2',
              border: 'none',
              borderRadius: '8px 0 0 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '18px',
              fontWeight: 'bold',
              boxShadow: '-2px 0 4px rgba(0,0,0,0.2)',
              zIndex: 10001
            }}
            onClick={() => setStatementSidebarCollapsed && setStatementSidebarCollapsed(false)}
            title="명세서 사이드바 펼치기"
            >
              ◀
            </div>
          )}
          <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, color: '#1976d2' }}>명세서 생성</h3>
                              <button 
                  onClick={() => setStatementSidebarCollapsed && setStatementSidebarCollapsed(!isStatementSidebarCollapsed)}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    fontSize: 20, 
                    cursor: 'pointer', 
                    color: '#666',
                    padding: 4
                  }}
                  title={isStatementSidebarCollapsed ? '펼치기' : '숨기기'}
                >
                  {isStatementSidebarCollapsed ? '◀' : '▶'}
                </button>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <span>기간:</span>
              <input type="date" ref={startDateRef} value={statementDateRange.start} onChange={e => {
                setStatementDateRange(prev => ({ ...prev, start: e.target.value }));
                // 날짜 변경 시 즉시 거래처 목록 비우기
                if (selectedTemplateType === 'A' || selectedTemplateType === 'B') {
                  setCompanyOptions([]);
                  setSelectedCompanies([]);
                  setItemNameOptions([]); // B타입 품명 목록도 비우기
                  setSelectedItemNames([]); // B타입 선택된 품명도 비우기
                  setCompanyLoading(true);
                }
              }} />
              <span>~</span>
              <input type="date" ref={endDateRef} value={statementDateRange.end} onChange={e => {
                setStatementDateRange(prev => ({ ...prev, end: e.target.value }));
                // 날짜 변경 시 즉시 거래처 목록 비우기
                if (selectedTemplateType === 'A' || selectedTemplateType === 'B') {
                  setCompanyOptions([]);
                  setSelectedCompanies([]);
                  setItemNameOptions([]); // B타입 품명 목록도 비우기
                  setSelectedItemNames([]); // B타입 선택된 품명도 비우기
                  setCompanyLoading(true);
                }
              }} />
              {/* <button onClick={loadAllCompanies} style={{ marginLeft: 12, padding: '4px 12px', borderRadius: 4, border: '1px solid #1976d2', background: '#e3f2fd', color: '#1976d2', fontWeight: 600, cursor: 'pointer' }}>거래처 불러오기</button> */}
            </div>
            {/* 명세서 양식 선택 드롭다운 */}
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>명세서 양식:</span>
              <select 
                value={selectedTemplateType} 
                onChange={(e) => handleTemplateTypeChange(e.target.value as 'A' | 'B' | 'C' | 'D' | 'ramen' | '')}
              >
                <option value="">선택</option>
                <option value="A">화주명세서</option>
                <option value="B">화주품명명세서</option>
                <option value="C">기사명세서</option>
                <option value="D">기사계산서</option>
                <option value="ramen">라면</option>
              </select>
            </div>
            {/* 분기 UI */}
            {selectedTemplateType === 'A' && (
              <div style={{ marginBottom: 12 }}>
                {/* 화주명세서: 기존 거래처 다중 선택 UI */}
                <div style={{ color: '#888', marginBottom: 8 }}>기간 내 거래처를 다중 선택할 수 있습니다.</div>
                {companyLoading && (
                  <div style={{ 
                    color: '#1976d2', 
                    marginBottom: 8, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 500
                  }}>
                    <div style={{ 
                      width: 16, 
                      height: 16, 
                      border: '2px solid #1976d2', 
                      borderTop: '2px solid transparent', 
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    화주명세서 거래처 목록 불러오는 중...
                  </div>
                )}
                {companyOptions.length > 0 && (
                  <div style={{ maxHeight: 500, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                    {companyOptions.map(company => (
                      <div key={company} style={{ marginBottom: 6 }}>
                        <label style={{ cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedCompanies.includes(company)}
                            onChange={() => handleCompanyCheck(company)}
                            style={{ marginRight: 8 }}
                          />
                          {company}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {companyOptions.length === 0 && !companyLoading && (
                  <div style={{ color: '#888', marginBottom: 8 }}>기간 내 거래처가 없습니다.</div>
                )}
              </div>
            )}
            {selectedTemplateType === 'B' && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: '#888', marginBottom: 8 }}>거래처를 1개 선택하면 해당 거래처의 품명을 다중 선택할 수 있습니다.</div>
                {/* 거래처 단일 선택 */}
                {companyLoading && (
                  <div style={{ 
                    color: '#1976d2', 
                    marginBottom: 8, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 500
                  }}>
                    <div style={{ 
                      width: 16, 
                      height: 16, 
                      border: '2px solid #1976d2', 
                      borderTop: '2px solid transparent', 
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    화주품명명세서 거래처 목록 불러오는 중...
                  </div>
                )}
                {companyOptions.length > 0 && (
                  <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                    {companyOptions.map(company => (
                      <div key={company} style={{ marginBottom: 6 }}>
                        <label style={{ cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name="b-company"
                            checked={selectedCompanies[0] === company}
                            onChange={() => handleBCompanySelect(company)}
                            style={{ marginRight: 8 }}
                          />
                          {company}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {companyOptions.length === 0 && !companyLoading && (
                  <div style={{ color: '#888', marginBottom: 8 }}>기간 내 거래처가 없습니다.</div>
                )}
                {/* 품명 다중 선택 */}
                {selectedCompanies[0] && itemNameOptions.filter(itemName => !!itemName && itemName.trim() !== '').length > 0 && (
                  <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                    <div style={{ color: '#888', marginBottom: 8 }}>품명을 다중 선택하세요.</div>
                    {itemNameOptions.filter(itemName => !!itemName && itemName.trim() !== '').map(itemName => (
                      <div key={itemName} style={{ marginBottom: 6 }}>
                        <label style={{ cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedItemNames.includes(itemName)}
                            onChange={() => handleItemNameCheck(itemName)}
                            style={{ marginRight: 8 }}
                          />
                          {itemName}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {selectedCompanies[0] && itemNameOptions.length === 0 && (
                  <div style={{ color: '#888', marginBottom: 8 }}>해당 거래처의 품명이 없습니다.</div>
                )}
              </div>
            )}
            {(selectedTemplateType === 'C' || selectedTemplateType === 'D') && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: '#888', marginBottom: 8 }}>
                  차량번호 일부(예: 4517)를 입력하면 settlement_rows 기준으로 매칭되는 차량 목록이 뜹니다. 선택하거나 직접 입력 후 쉼표/엔터로 등록할 수 있습니다.
                </div>
                <div ref={driverVehicleInputWrapperRef} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', position: 'relative' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <input
                      type="text"
                      value={driverVehicleInput}
                      onChange={handleDriverVehicleInputChange}
                      onKeyDown={handleDriverVehicleInputKeyDown}
                      placeholder="예: 4517 입력 후 제안 목록에서 선택"
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #bbb', fontSize: 15, boxSizing: 'border-box' }}
                    />
                    {showDriverVehicleSuggestions && driverVehicleSuggestions.length > 0 && (
                      <ul
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          margin: 0,
                          padding: 0,
                          listStyle: 'none',
                          background: '#fff',
                          border: '1px solid #bbb',
                          borderRadius: 4,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                          maxHeight: 200,
                          overflowY: 'auto',
                          zIndex: 20
                        }}
                      >
                        {driverVehicleSuggestions.map((num, idx) => (
                          <li
                            key={idx}
                            onClick={() => handleSelectDriverVehicleSuggestion(num)}
                            style={{
                              padding: '8px 10px',
                              cursor: 'pointer',
                              fontSize: 14,
                              borderBottom: idx < driverVehicleSuggestions.length - 1 ? '1px solid #eee' : 'none'
                            }}
                            onMouseEnter={e => { (e.currentTarget as HTMLLIElement).style.background = '#e8f4fd'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLLIElement).style.background = '#fff'; }}
                          >
                            {num}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleAddDriverVehicle}
                    style={{ background: vehicleRegistering ? '#bbb' : '#1976d2', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: vehicleRegistering ? 'not-allowed' : 'pointer' }}
                    disabled={vehicleRegistering || !driverVehicleInput.trim()}
                  >
                    {vehicleRegistering ? '조회중...' : '등록'}
                  </button>
                </div>
                <div style={{
                  marginTop: 10,
                  display: 'flex',
                  flexDirection: 'column', // 세로로 쌓기
                  gap: 8,
                  maxHeight: 160, // 5개 정도 높이
                  overflowY: 'auto',
                  border: '1px solid #eee',
                  borderRadius: 4,
                  padding: 8,
                  background: '#fafbfc'
                }}>
                  {driverVehicleList.map(vehicle => (
                    <div key={vehicle} style={{
                      background: '#f5f7fa',
                      border: '1px solid #bbb',
                      borderRadius: 4,
                      padding: '4px 10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <span>{vehicle}</span>
                      <button
                        onClick={() => handleRemoveDriverVehicle(vehicle)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#d32f2f',
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: 15
                        }}
                        title="삭제"
                      >×</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', marginTop: 8 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap' }}>
              <button
                onClick={handleExcelDownload}
                disabled={
                  selectedTemplateType === 'D'
                  || (selectedTemplateType === 'C'
                    ? driverVehicleList.length === 0
                    : selectedTemplateType === 'ramen'
                      ? false
                      : selectedCompanies.length === 0)
                  || excelGenerating || pdfGenerating || taxGenerating
                }
                style={{ background: (selectedTemplateType !== 'D' && (selectedTemplateType === 'C' ? driverVehicleList.length : selectedTemplateType === 'ramen' ? 1 : selectedCompanies.length) && !excelGenerating && !pdfGenerating && !taxGenerating) ? '#1976d2' : '#bbb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', fontWeight: 600, fontSize: 16, cursor: (selectedTemplateType !== 'D' && (selectedTemplateType === 'C' ? driverVehicleList.length : selectedTemplateType === 'ramen' ? 1 : selectedCompanies.length) && !excelGenerating && !pdfGenerating && !taxGenerating) ? 'pointer' : 'not-allowed', position: 'relative', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {excelGenerating ? '엑셀 생성중...' : '엑셀'}
                {excelGenerating && (
                  <span style={{ marginLeft: 8, display: 'inline-block', verticalAlign: 'middle' }}>
                    <svg width="18" height="18" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeDasharray="31.415, 31.415" transform="rotate(72.5042 25 25)"><animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/></circle></svg>
                  </span>
                )}
              </button>
              <button
                onClick={handlePdfDownload}
                disabled={
                  selectedTemplateType === 'D'
                  || (selectedTemplateType === 'C' ? driverVehicleList.length === 0 : selectedCompanies.length === 0)
                  || pdfGenerating || excelGenerating || taxGenerating
                }
                style={{ background: (selectedTemplateType !== 'D' && (selectedTemplateType === 'C' ? driverVehicleList.length : selectedCompanies.length) && !pdfGenerating && !excelGenerating && !taxGenerating) ? '#1976d2' : '#bbb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', fontWeight: 600, fontSize: 16, cursor: (selectedTemplateType !== 'D' && (selectedTemplateType === 'C' ? driverVehicleList.length : selectedCompanies.length) && !pdfGenerating && !excelGenerating && !taxGenerating) ? 'pointer' : 'not-allowed', position: 'relative', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {pdfGenerating ? 'PDF 생성중...' : 'PDF'}
                {pdfGenerating && (
                  <span style={{ marginLeft: 8, display: 'inline-block', verticalAlign: 'middle' }}>
                    <svg width="18" height="18" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeDasharray="31.415, 31.415" transform="rotate(72.5042 25 25)"><animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/></circle></svg>
                  </span>
                )}
              </button>
              <button onClick={() => setIsStatementSidebarOpen(false)} style={{ background: '#d32f2f', color: 'white', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }} disabled={excelGenerating || pdfGenerating || taxGenerating}>
                닫기
              </button>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap' }}>
              <button
                onClick={handleTaxInvoiceDownload}
                disabled={
                  selectedTemplateType !== 'D'
                  || driverVehicleList.length === 0
                  || taxGenerating || excelGenerating || pdfGenerating
                }
                style={{ background: (selectedTemplateType === 'D' && driverVehicleList.length > 0 && !taxGenerating && !excelGenerating && !pdfGenerating) ? '#1976d2' : '#bbb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 24px', fontWeight: 600, fontSize: 16, cursor: (selectedTemplateType === 'D' && driverVehicleList.length > 0 && !taxGenerating && !excelGenerating && !pdfGenerating) ? 'pointer' : 'not-allowed', position: 'relative', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 88 }}
              >
                {taxGenerating ? '계산서 생성중...' : '계산서'}
                {taxGenerating && (
                  <span style={{ marginLeft: 8, display: 'inline-block', verticalAlign: 'middle' }}>
                    <svg width="18" height="18" viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeDasharray="31.415, 31.415" transform="rotate(72.5042 25 25)"><animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite"/></circle></svg>
                  </span>
                )}
              </button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', margin: '16px 0 0 0' }}>
              <span style={{ fontWeight: 600 }}>공급자:</span>
              <label style={{ cursor: 'pointer', marginRight: 12 }}>
                <input
                  type="radio"
                  name="supplierType"
                  value="seongwoo"
                  checked={supplierType === 'seongwoo'}
                  onChange={() => setSupplierType('seongwoo')}
                  style={{ marginRight: 6 }}
                />
                성우로지스(주)
              </label>
              <label style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="supplierType"
                  value="korea"
                  checked={supplierType === 'korea'}
                  onChange={() => setSupplierType('korea')}
                  style={{ marginRight: 6 }}
                />
                한국운수
              </label>
            </div>
          </div>
        </div>
      )}

      {statsBarVisible && (
        <SettlementSidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onOpen={() => setSidebarOpen(true)}
          onHideBar={() => setStatsBarVisible(false)}
          filteredRows={filteredRows}
          dateRangeStart={dateRange.start}
          dateRangeEnd={dateRange.end}
        />
      )}

      {/* 라면 모달 */}
      {ramenModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.2)', zIndex: 10000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center'
        }}>
          <div ref={ramenModalRef}
            style={{
              background: '#fff', borderRadius: 12, padding: 32, minWidth: 340,
              boxShadow: '0 2px 16px rgba(0,0,0,0.15)', position: 'relative',
              marginTop: 32
            }}>
            {/* X 버튼 */}
            <button onClick={() => setRamenModalOpen(false)} style={{ position: 'absolute', top: 12, right: 12, background: 'transparent', border: 'none', fontSize: 22, fontWeight: 700, color: '#d32f2f', cursor: 'pointer' }} title="닫기">×</button>
            <h3 style={{ marginBottom: 18 }}>라면 집계</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <span>기간:</span>
              <input type="date" value={ramenStartDate} onChange={e => setRamenStartDate(e.target.value)} />
              <span>~</span>
              <input type="date" value={ramenEndDate} onChange={e => setRamenEndDate(e.target.value)} />
              <button
                onClick={handleRamenSearch}
                disabled={ramenLoading}
                style={{ marginLeft: 8, padding: '6px 16px', borderRadius: 4, border: '1px solid #1976d2', background: '#e3f2fd', color: '#1976d2', fontWeight: 600, cursor: 'pointer' }}
              >
                {ramenLoading ? '조회중...' : '조회'}
              </button>
            </div>
            {/* 표 */}
            {ramenRows.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead>
                      <tr style={{ background: '#ffe082' }}>
                        <th style={{ border: '1px solid #bbb', padding: 6 }}>날짜</th>
                        <th style={{ border: '1px solid #bbb', padding: 6 }}>수출라면</th>
                        <th style={{ border: '1px solid #bbb', padding: 6 }}>내수라면</th>
                        <th style={{ border: '1px solid #bbb', padding: 6 }}>유지</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ramenRows.map(r => (
                        <tr key={r.date}>
                          <td style={{ border: '1px solid #bbb', padding: 6, textAlign: 'center' }}>{r.date}</td>
                          <td style={{ border: '1px solid #bbb', padding: 6, textAlign: 'right' }}>{(Number(r.exportramenNum) || 0).toFixed(2)}</td>
                          <td style={{ border: '1px solid #bbb', padding: 6, textAlign: 'right' }}>{(Number(r.domesticramenNum) || 0).toFixed(2)}</td>
                          <td style={{ border: '1px solid #bbb', padding: 6, textAlign: 'right' }}>{(Number(r.oilramenNum) || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr style={{ background: '#fffde7', fontWeight: 700 }}>
                        <td style={{ border: '1px solid #bbb', padding: 6, textAlign: 'center' }}>합계</td>
                        <td style={{ border: '1px solid #bbb', padding: 6, textAlign: 'right' }}>{ramenSum.exportramenNum.toFixed(2)}</td>
                        <td style={{ border: '1px solid #bbb', padding: 6, textAlign: 'right' }}>{ramenSum.domesticramenNum.toFixed(2)}</td>
                        <td style={{ border: '1px solid #bbb', padding: 6, textAlign: 'right' }}>{ramenSum.oilramenNum.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <button onClick={handleRamenExcel} style={{ marginTop: 18, background: '#43a047', color: '#fff', border: 'none', borderRadius: 5, padding: '8px 18px', fontWeight: 600, fontSize: '16px', cursor: 'pointer' }}>엑셀 다운로드</button>
              </div>
            )}
          </div>
        </div>
      )}

      {settlementPasswordModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: 32,
              borderRadius: 12,
              width: 400,
              maxWidth: '90vw',
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                marginBottom: 8,
                fontWeight: 700,
                fontSize: 18,
                color: '#374151',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <EditIcon sx={{ fontSize: 20, color: '#1976d2' }} />
              입력 모드 인증
            </div>
            <div style={{ marginBottom: 20, fontSize: 14, color: '#6b7280' }}>
              입력 모드에서는 정산일보를 기존과 같이 수정할 수 있습니다. 비밀번호를 입력하세요.
            </div>
            <input
              type="password"
              name="settlement-input-mode-password"
              value={settlementPasswordInput}
              onChange={e => setSettlementPasswordInput(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && confirmSettlementPassword()}
              onFocus={e => {
                e.target.readOnly = false;
                e.target.setAttribute('autocomplete', 'new-password');
                e.target.setAttribute('data-form-type', 'other');
              }}
              onBlur={e => {
                e.target.readOnly = true;
              }}
              style={{
                width: '100%',
                fontSize: 16,
                padding: '12px 16px',
                marginBottom: 8,
                border: settlementPasswordError ? '1px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: 8,
                background: '#fff',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              placeholder="비밀번호 입력"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
              readOnly
              autoFocus
            />
            {settlementPasswordError && (
              <div style={{ marginBottom: 16, fontSize: 13, color: '#ef4444' }}>{settlementPasswordError}</div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={confirmSettlementPassword}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 0',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                확인
              </button>
              <button
                type="button"
                onClick={cancelSettlementPassword}
                style={{
                  flex: 1,
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  padding: '12px 0',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SettlementReportPage;